import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import {
  createConfigCanned,
  createPriority,
  createSlaPolicy,
  createStatus,
  updateStatus,
  deleteStatus,
  setDefaultStatus,
  reorderStatuses,
  reorderTaskStatuses,
  updatePriority,
  deletePriority,
  setDefaultPriority,
  listTaskPriorities,
  createTaskPriority,
  updateTaskPriority,
  deleteTaskPriority,
  setDefaultTaskPriority,
  listTicketTypes,
  createTicketType,
  updateTicketType,
  deleteTicketType,
  setDefaultTicketType,
  listTaskStatuses,
  createTaskStatus,
  updateTaskStatus,
  deleteTaskStatus,
  setDefaultTaskStatus,
  listTicketCategories,
  createTicketCategory,
  updateTicketCategory,
  deleteTicketCategory,
  updateConfigCanned,
  deleteConfigCanned,
  updateSlaPolicy,
  deleteSlaPolicy,
  listTerms,
  createTerm,
  updateTerm,
  deleteTerm,
  setCurrentTerm,
  listDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  listDepartmentApprovers,
  setDepartmentApprovers,
  createTeam,
  updateTeam,
  deleteTeam,
  listTeams,
  listUserTeams,
  setTeamMember,
  removeTeamMember,
  listAssetTypes,
  createAssetType,
  updateAssetType,
  deleteAssetType,
  listAssetCategories,
  createAssetCategory,
  listItSystems,
  createItSystem,
  updateItSystem,
  deleteItSystem,
  updateAssetCategory,
  deleteAssetCategory,
  listAssetStatuses,
  createAssetStatus,
  updateAssetStatus,
  deleteAssetStatus,
  reorderAssetStatuses,
  getBranding,
  getBusinessHours,
  importTickets,
  listAudit,
  listConfigCanned,
  listPriorities,
  listSlaPolicies,
  listStatuses,
  getCsatConfig,
  setCsatConfig,
  listCsatResponses,
  listTenantUsers,
  listRoles,
  setBranding as apiSetBranding,
  uploadBrandingLogo,
  setBusinessHours,
  listDirectoryConnections,
  createEntraConnection,
  syncDirectoryConnection,
  listDirectoryRuns,
  listSsoConnectionsAdmin,
  createSsoConnection,
  updateSsoConnection,
  deleteSsoConnection,
  createUser,
  updateUser,
  setUserRole,
  resetUserPassword,
  getEmailConfig,
  setEmailConfig,
  sendTestEmail,
  listEmailTemplates,
  saveEmailTemplate,
  revertEmailTemplate,
  testEmailTemplate,
  getDigestSetting,
  setDigestSetting,
  getEmailIntakeConfig,
  setEmailIntakeConfig,
  getTicketNumbering,
  setTicketNumbering,
  getFacilityNumbering,
  setFacilityNumbering,
  getChangeNumbering,
  setChangeNumbering,
  getFacilityConfig,
  setFacilityConfig,
  getChangeConfig,
  setChangeConfig,
  getMe,
  listFacilityGroups,
  createFacilityGroup,
  deleteFacilityGroup,
  listFacilityGroupMembers,
  addFacilityGroupMember,
  removeFacilityGroupMember,
  listFacilityVendors,
  createFacilityVendor,
  updateFacilityVendor,
  deleteFacilityVendor,
  listAssignees,
  listPmTemplates,
  createPmTemplate,
  updatePmTemplate,
  deletePmTemplate,
  generateDuePms,
  type FacilityOption,
  type FacilityBuildingOption,
  type FacilityRoomOption,
  type FacilityConfig,
  type ChangeConfig,
  type ChangeOption,
  listKbCategories,
  createKbCategory,
  updateKbCategory,
  deleteKbCategory,
  type EmailConfig,
  type EmailProviderKind,
  type SystemEmailTemplate,
  type TicketNumberingConfig,
  type FacilityNumberingConfig,
  type ChangeNumberingConfig,
  getWorkspaceConfig,
  setWorkspaceConfig,
  getTicketSources,
  setTicketSources,
  getActionItemCategories,
  setActionItemCategories,
  listTicketTemplates,
  listTicketFlows,
  createTicketFlow,
  updateTicketFlow,
  deleteTicketFlow,
  initiateTicketFlow,
  listTicketFlowRuns,
  getTicketFlowRun,
} from "../lib/api";
import { WORKSPACE_LANDING_OPTIONS } from "../lib/types";
import { getCurrentUser } from "../lib/auth";
import { useApi, useMutation } from "../lib/useApi";
import { applyBranding } from "../lib/branding";
import { formatChangeNumber, formatDate, formatTicketNumber, parseJson } from "../lib/format";
import type {
  AcademicTermRow,
  DepartmentRow,
  DepartmentApprovalRule,
  AssetTypeRow,
  AssetCategoryRow,
  AssetStatusRow,
  ItSystem,
  TeamRow,
  BusinessHoursRow,
  TicketStatusRow,
  TicketPriorityRow,
  TaskPriorityRow,
  TicketTypeRow,
  TaskStatusRow,
  TicketCategoryRow,
  CannedResponseRow,
  SlaPolicyRow,
  DirectoryConnection,
  DirectorySyncRun,
  KbCategoryRow,
  FacilityPmTemplateRow,
  FacilityGroupRow,
  FacilityVendorRow,
  CreateFacilityPmTemplateInput,
  ImportTicketRow,
  ImportTicketsResponse,
  TicketTemplate,
  TicketFlow,
  TicketFlowField,
  TicketFlowFieldType,
  TicketFlowStep,
  TicketFlowCreateStepConfig,
  TicketFlowEmailStepConfig,
  TicketFlowRun,
  TicketFlowRunStep,
  CsatResponseRow,
  CsatBucket,
} from "../lib/types";
import { saveBlob, csvBlob } from "../lib/download";
import { Link } from "react-router-dom";
import { PageBar } from "../components/Layout";
import { Icon, type IconName } from "../components/Icon";
import { confirmDialog } from "../components/ConfirmDialog";
import { RolesTab } from "../components/RolesTab";
import { RoleVisibilityTab } from "../components/RoleVisibilityTab";
import { Modal } from "../components/Modal";
import { RichTextEditor, richTextIsEmpty } from "../components/RichText";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import type { ReactNode } from "react";

type TenantUserRowType = import("../lib/types").TenantUser;

// Sort accessors for the genuine unordered DATA lists in Settings. Config lists
// whose order is meaningful (statuses, priorities, ticket/task types, request
// types, KB categories, facility value sets, business hours, SLA, PM templates)
// are intentionally NOT sortable — their row order is user-defined / positional.
const USERS_SORT: Record<string, SortAccessor<TenantUserRowType>> = {
  user: (u) => u.name || u.email,
  role: (u) => (u.roles ?? "").split(",")[0] || "",
  access: (u) => (u.can_access ? 1 : 0),
  status: (u) => u.status,
};
const TEAMS_SORT: Record<string, SortAccessor<TeamRow>> = {
  name: (t) => t.name,
};
const ASSET_TYPES_SORT: Record<string, SortAccessor<AssetTypeRow>> = {
  name: (t) => t.name,
};
const ASSET_CATEGORIES_SORT: Record<string, SortAccessor<AssetCategoryRow>> = {
  name: (c) => c.name,
};
const TERMS_SORT: Record<string, SortAccessor<AcademicTermRow>> = {
  name: (t) => t.name,
  code: (t) => t.code,
  starts: (t) => t.starts_on,
  ends: (t) => t.ends_on,
};
const DEPARTMENTS_SORT: Record<string, SortAccessor<DepartmentRow>> = {
  name: (d) => d.name,
  code: (d) => d.code,
};

/** Small confirm + run helper for destructive row actions (styled dialog). */
function confirmThen(message: string, fn: () => void): void {
  void confirmDialog({ message, danger: true }).then((ok) => ok && fn());
}

/**
 * Full-width list card for the settings lookup sections. Header shows the title,
 * a faint count, and a solid "Add <X>" button (top-right) that opens the
 * section's create/edit modal. Body renders the list / states passed as children.
 */
function SettingsListCard({
  title,
  count,
  addLabel,
  onAdd,
  intro,
  headerExtra,
  children,
}: {
  title: string;
  count: ReactNode;
  addLabel: string;
  onAdd: () => void;
  intro?: ReactNode;
  /** Optional control rendered in the card head, left of the count (e.g. a filter). */
  headerExtra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>{title}</h3>
        <div className="btn-row" style={{ alignItems: "center" }}>
          {headerExtra}
          <span className="faint">{count}</span>
          <button className="btn small primary" onClick={onAdd}>
            {addLabel}
          </button>
        </div>
      </div>
      <div className="card-body">
        {intro}
        {children}
      </div>
    </div>
  );
}

type Tab =
  | "general"
  | "statuses"
  | "priorities"
  | "numbering"
  | "ticket-types"
  | "ticket-flow"
  | "submission-tracking"
  | "task-statuses"
  | "task-priorities"
  | "task-categories"
  | "request-types"
  | "terms"
  | "departments"
  | "locations"
  | "asset-types"
  | "asset-categories"
  | "asset-statuses"
  | "facility-numbering"
  | "facility-groups"
  | "facility-vendors"
  | "facility-statuses"
  | "facility-types"
  | "facility-categories"
  | "facility-priorities"
  | "facility-pm"
  | "change-statuses"
  | "change-types"
  | "change-risks"
  | "change-urgencies"
  | "change-impacts"
  | "change-schedules"
  | "change-numbering"
  | "it-systems"
  | "canned"
  | "hours"
  | "branding"
  | "email"
  | "email-templates"
  | "email-intake"
  | "csat"
  | "sla"
  | "kb"
  | "import"
  | "users"
  | "tech-groups"
  | "roles"
  | "role-visibility"
  | "identity"
  | "audit";

/** Settings tabs grouped into logical sections for the left settings-nav. Each
 *  group carries an icon for the section header chip. */
const TAB_GROUPS: { title: string; icon: IconName; color: string; tabs: { id: Tab; label: string }[] }[] = [
  {
    title: "General",
    icon: "settings",
    color: "#64748b",
    tabs: [{ id: "general", label: "Workspace" }],
  },
  {
    title: "Helpdesk",
    icon: "tickets",
    color: "#3b82f6",
    tabs: [
      { id: "statuses", label: "Statuses" },
      { id: "priorities", label: "Priorities" },
      { id: "numbering", label: "Ticket Numbering" },
      { id: "ticket-types", label: "Ticket Types" },
      { id: "ticket-flow", label: "Ticket Flow" },
      { id: "submission-tracking", label: "Submission Tracking" },
      { id: "request-types", label: "Categories" },
      { id: "canned", label: "Canned Responses" },
      { id: "hours", label: "Business Hours" },
      { id: "sla", label: "SLA Policies" },
      { id: "csat", label: "Satisfaction" },
      { id: "email-intake", label: "AI Email Intake" },
      { id: "kb", label: "KB Categories" },
      { id: "import", label: "Import Tickets" },
    ],
  },
  {
    title: "Organization",
    icon: "access",
    color: "#14b8a6",
    tabs: [
      { id: "terms", label: "Academic Terms" },
      { id: "departments", label: "Departments" },
      { id: "locations", label: "Locations" },
    ],
  },
  {
    title: "Action Items",
    icon: "action-items",
    color: "#8b5cf6",
    tabs: [
      { id: "task-statuses", label: "Statuses" },
      { id: "task-priorities", label: "Priorities" },
      { id: "task-categories", label: "Categories" },
    ],
  },
  {
    title: "Assets",
    icon: "assets",
    color: "#f59e0b",
    tabs: [
      { id: "asset-types", label: "Asset Types" },
      { id: "asset-categories", label: "Asset Categories" },
      { id: "asset-statuses", label: "Asset Statuses" },
    ],
  },
  {
    title: "Facilities",
    icon: "wrench",
    color: "#f97316",
    tabs: [
      { id: "facility-groups", label: "Groups" },
      { id: "facility-vendors", label: "Vendors" },
      { id: "facility-statuses", label: "Statuses" },
      { id: "facility-types", label: "Types" },
      { id: "facility-categories", label: "Categories" },
      { id: "facility-priorities", label: "Priorities" },
      { id: "facility-numbering", label: "Work Order Numbering" },
      { id: "facility-pm", label: "PM Templates" },
    ],
  },
  {
    title: "Change Management",
    icon: "wrench",
    color: "#0ea5e9",
    tabs: [
      { id: "change-statuses", label: "Statuses" },
      { id: "change-types", label: "Types" },
      { id: "change-risks", label: "Risk Levels" },
      { id: "change-urgencies", label: "Urgency / Priority" },
      { id: "change-impacts", label: "Business Impact" },
      { id: "change-schedules", label: "Schedules" },
      { id: "it-systems", label: "IT Systems" },
      { id: "change-numbering", label: "Change Numbering" },
    ],
  },
  {
    title: "People & Access",
    icon: "user",
    color: "#ec4899",
    tabs: [
      { id: "users", label: "Users" },
      { id: "tech-groups", label: "Tech Groups" },
      { id: "roles", label: "Roles" },
      { id: "role-visibility", label: "Picker Visibility" },
      { id: "identity", label: "Identity & SSO" },
    ],
  },
  {
    title: "Brand & Channels",
    icon: "bell",
    color: "#06b6d4",
    tabs: [
      { id: "branding", label: "Branding" },
      { id: "email", label: "Email Settings" },
      { id: "email-templates", label: "Email Templates" },
    ],
  },
  {
    title: "System",
    icon: "reports",
    color: "#94a3b8",
    tabs: [{ id: "audit", label: "Audit Log" }],
  },
];

const TAB_HINTS: Partial<Record<Tab, string>> = {
  general:
    "Workspace name shown in the app and the page each member lands on after signing in. Which modules a person sees is set per role under People & access → Roles.",
  statuses: "Ticket lifecycle states and the closed/open category each maps to.",
  priorities: "Priority levels used on tickets. Separate from action-item / project-task priorities.",
  numbering: "How ticket numbers are displayed — prefix, starting number, and zero-padding.",
  "ticket-types": "Request categories (Service Request, Incident, Problem, …) shown on tickets.",
  "ticket-flow":
    "Build intake forms that auto-create tickets and send emails when a requester initiates a flow.",
  "submission-tracking": "Options for the ticket “Submission Tracking” (source channel) dropdown — Web, E-Mail, Phone, Walk-In, … Add, rename, reorder, or remove. Existing tickets keep their value.",
  "task-statuses": "Lifecycle states for project tasks and standalone action items (To do, In progress, Done, …).",
  "task-priorities": "Priority levels used on action items and project tasks. Separate from ticket priorities.",
  "task-categories": "Categories shown on the action-item Category dropdown. Reorder to set the display order; existing items keep their value if a category is removed.",
  "request-types": "Three-level Category → Sub Type → Item tree (each level depends on the one above).",
  canned: "Reusable reply snippets for the helpdesk.",
  hours: "Business hours that drive SLA timers.",
  sla: "First-response and resolution targets per priority.",
  kb: "Knowledge-base categories used to organize articles.",
  import:
    "Migrate tickets from a previous helpdesk. Export a CSV from the old system, upload it here, map its columns to ticket fields, preview, then import. Original creation dates are preserved.",
  terms: "Academic terms; mark one current for new tickets and projects.",
  departments: "Owning offices and the team each routes tickets to.",
  locations: "The Campus → Building → Room hierarchy — one shared list used by both tickets and facility work orders.",
  "asset-types": "Categories for inventory assets (Laptop, Monitor, License, …).",
  "asset-categories": "Categories for grouping inventory assets (Hardware, Software, …).",
  "asset-statuses": "Statuses for inventory assets (In stock, Assigned, …) — used across the Assets module.",
  "facility-statuses": "Lifecycle lanes for the work-order queue & board. Editing labels is safe; deleting a status leaves existing work orders on their current code.",
  "facility-types": "Work-order types (the nature of the work). Editing labels is safe; deleting a value leaves existing work orders on their current code.",
  "facility-categories": "Trade / area used to classify and filter work orders. Editing labels is safe; deleting a value leaves existing work orders on their current code.",
  "facility-priorities": "Urgency levels shown on the work-order Priority dropdown. Editing labels is safe; deleting a value leaves existing work orders on their current code.",
  "facility-numbering": "How work order numbers are displayed — prefix, starting number, and zero-padding.",
  "facility-pm": "Preventive-maintenance schedule templates — recurring blueprints that generate work orders. Run “Generate due PMs now” to materialize every template whose next-due date has passed.",
  "facility-vendors": "Vendors & suppliers — the companies that service your equipment and supply your parts. Capture contacts, contract dates, and payment terms. Assign a vendor to equipment from the equipment detail page, and pick a part's supplier on the part form.",
  "change-statuses": "Lifecycle lanes for the Change Management queue & board (first lane is the default for new changes). Editing labels is safe; deleting a status leaves existing changes on their current code.",
  "change-types": "Change types (Standard, Normal, Emergency…). Editing labels is safe; deleting a value leaves existing changes on their current code.",
  "change-risks": "Risk levels shown on the change Risk dropdown & badges. Editing labels is safe; deleting a value leaves existing changes on their current code.",
  "change-urgencies": "Urgency / priority levels shown on the change request form (High, Moderate, Low…). Editing labels is safe; deleting a value leaves existing changes on their current code.",
  "change-impacts": "Business-impact levels shown on the change request form (High, Moderate, Low…). Editing labels is safe; deleting a value leaves existing changes on their current code.",
  "change-schedules": "Recurrence cadences for automated/recurring changes (Daily, Weekly, Every Semester…). Editing labels is safe; deleting a value leaves existing changes on their current code.",
  "it-systems": "IT systems selectable on a change request (Active Directory, Banner, …). Inactive systems stay on existing changes but are hidden from the picker; deleting one removes it from every change.",
  "change-numbering": "How change request numbers are displayed — prefix, starting number, and zero-padding.",
  users: "People in this workspace, their status and role.",
  "tech-groups": "Technician groups (teams) used to route and scope tickets. Assign members on each user.",
  roles: "Role definitions and per-module permissions.",
  "role-visibility":
    "Limit which user roles appear in each people picker (Requested by / Assigned to / Shared with) per work type. Empty = all roles.",
  identity: "Azure Entra ID directory sync and SSO sign-in.",
  branding: "Logo and accent color for this workspace.",
  email: "Outbound email provider — choose how transactional mail is sent and send a test.",
  "email-templates": "Edit the system emails ActionConnect sends (ticket, sign-in, change-management). Customize the subject + body per tenant, insert {{placeholders}}, send a test, or revert to the built-in default.",
  "email-intake": "Turn inbound emails into AI-drafted, pre-filled tickets, and optionally connect a mailbox the system polls for new mail.",
  csat: "Send a satisfaction survey when a ticket is resolved. Configure the question, rating labels, thank-you message, which statuses trigger it, and a send delay.",
  audit: "Immutable log of configuration and data changes.",
};

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>("general");
  const [navFilter, setNavFilter] = useState("");
  const activeLabel = TAB_GROUPS.flatMap((g) => g.tabs).find((t) => t.id === tab)?.label ?? "";
  // The group containing the active tab — drives the mobile category picker.
  const mobileGroup = TAB_GROUPS.find((g) => g.tabs.some((t) => t.id === tab)) ?? TAB_GROUPS[0];

  // Filter the left nav by tab label; drop any group left with no matches.
  const fq = navFilter.trim().toLowerCase();
  const filteredGroups = fq
    ? TAB_GROUPS.map((g) => ({ ...g, tabs: g.tabs.filter((t) => t.label.toLowerCase().includes(fq)) })).filter(
        (g) => g.tabs.length > 0,
      )
    : TAB_GROUPS;

  return (
    <>
      <PageBar crumbs="Settings" />
      <div className="content settings-content">
        <div className="page-head">
          <div>
            <h1>Settings</h1>
          </div>
        </div>

        {/* Mobile-only drill-down: pick a category, then a setting within it —
            one at a time (the desktop chip nav is hidden at this width). */}
        <div className="settings-mobile-picker">
          <label className="smp-field">
            <span className="smp-label">Category</span>
            <select
              value={mobileGroup.title}
              onChange={(e) => {
                const g = TAB_GROUPS.find((x) => x.title === e.target.value);
                if (g && g.tabs[0]) setTab(g.tabs[0].id);
              }}
            >
              {TAB_GROUPS.map((g) => (
                <option key={g.title} value={g.title}>
                  {g.title}
                </option>
              ))}
            </select>
          </label>
          <label className="smp-field">
            <span className="smp-label">Setting</span>
            <select value={tab} onChange={(e) => setTab(e.target.value as Tab)}>
              {mobileGroup.tabs.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="settings-layout">
          <nav className="settings-nav" aria-label="Settings sections">
            <div className="settings-nav-search">
              <input
                type="search"
                value={navFilter}
                onChange={(e) => setNavFilter(e.target.value)}
                placeholder="Filter settings…"
                aria-label="Filter settings"
              />
            </div>
            {filteredGroups.length === 0 ? (
              <div className="settings-nav-empty faint">No settings match “{navFilter}”.</div>
            ) : (
              filteredGroups.map((group) => (
                <div
                  className="settings-nav-group"
                  key={group.title}
                  style={{ ["--cat" as string]: group.color }}
                >
                  <div className="settings-nav-title">
                    <span className="settings-nav-title-icon" aria-hidden="true">
                      <Icon name={group.icon} />
                    </span>
                    {group.title}
                  </div>
                  {group.tabs.map((t) => (
                    <button
                      key={t.id}
                      role="tab"
                      aria-selected={tab === t.id}
                      className={`settings-nav-item${tab === t.id ? " active" : ""}`}
                      onClick={() => setTab(t.id)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              ))
            )}
          </nav>

          <div className="settings-panel">
            <div className="settings-panel-head">
              <h2>{activeLabel}</h2>
              {TAB_HINTS[tab] ? <p className="sub">{TAB_HINTS[tab]}</p> : null}
            </div>

            {tab === "general" ? <GeneralTab /> : null}
            {tab === "statuses" ? <StatusesTab /> : null}
        {tab === "priorities" ? <PrioritiesTab /> : null}
        {tab === "numbering" ? <NumberingTab /> : null}
        {tab === "ticket-types" ? <TicketTypesTab /> : null}
        {tab === "ticket-flow" ? <TicketFlowTab /> : null}
        {tab === "submission-tracking" ? <SubmissionTrackingTab /> : null}
        {tab === "task-statuses" ? <TaskStatusesTab /> : null}
        {tab === "task-priorities" ? <TaskPrioritiesSection /> : null}
        {tab === "task-categories" ? <ActionItemCategoriesTab /> : null}
        {tab === "request-types" ? <RequestTypesTab /> : null}
        {tab === "terms" ? <TermsTab /> : null}
        {tab === "departments" ? <DepartmentsTab /> : null}
        {tab === "locations" ? <LocationsTab /> : null}
        {tab === "asset-types" ? <AssetTypesTab /> : null}
        {tab === "asset-categories" ? <AssetCategoriesTab /> : null}
        {tab === "asset-statuses" ? <AssetStatusesTab /> : null}
        {tab === "facility-groups" ? <FacilityGroupsTab /> : null}
        {tab === "facility-vendors" ? <FacilityVendorsTab /> : null}
        {tab === "facility-statuses" ? <FacilityStatusesTab /> : null}
        {tab === "facility-types" ? <FacilityTypesTab /> : null}
        {tab === "facility-categories" ? <FacilityCategoriesTab /> : null}
        {tab === "facility-priorities" ? <FacilityPrioritiesTab /> : null}
        {tab === "facility-numbering" ? <FacilityNumberingTab /> : null}
        {tab === "facility-pm" ? <FacilityPmTab /> : null}
        {tab === "change-statuses" ? <ChangeStatusesTab /> : null}
        {tab === "change-urgencies" ? <ChangeUrgenciesTab /> : null}
        {tab === "change-impacts" ? <ChangeImpactsTab /> : null}
        {tab === "change-schedules" ? <ChangeSchedulesTab /> : null}
        {tab === "change-types" ? <ChangeTypesTab /> : null}
        {tab === "change-risks" ? <ChangeRisksTab /> : null}
        {tab === "change-numbering" ? <ChangeNumberingTab /> : null}
        {tab === "it-systems" ? <ItSystemsTab /> : null}
        {tab === "canned" ? <CannedTab /> : null}
        {tab === "hours" ? <BusinessHoursTab /> : null}
        {tab === "branding" ? <BrandingTab /> : null}
        {tab === "email" ? <EmailTab /> : null}
        {tab === "email-templates" ? <EmailTemplatesTab /> : null}
        {tab === "email-intake" ? <EmailIntakeCard /> : null}
        {tab === "csat" ? <CsatTab /> : null}
        {tab === "sla" ? <SlaTab /> : null}
        {tab === "kb" ? <KnowledgeBaseTab /> : null}
        {tab === "import" ? <ImportTicketsTab /> : null}
        {tab === "users" ? <UsersTab /> : null}
        {tab === "tech-groups" ? <TechGroupsTab /> : null}
        {tab === "roles" ? <RolesTab /> : null}
        {tab === "role-visibility" ? <RoleVisibilityTab /> : null}
        {tab === "identity" ? <IdentityTab /> : null}
            {tab === "audit" ? <AuditTab /> : null}
          </div>
        </div>
      </div>
    </>
  );
}

// ===== Ticket Flow =========================================================
// Intake forms that auto-create tickets (from ticket templates) and send emails
// when a requester "initiates" the flow (0109). Admins build flows; requesters
// with ticket.write can initiate them.

const FLOW_FIELD_TYPES: { value: TicketFlowFieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Paragraph" },
  { value: "email", label: "Email" },
  { value: "select", label: "Dropdown" },
  { value: "date", label: "Date" },
  { value: "checkbox", label: "Checkbox" },
];

function flowUid(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Slugify a label into a stable field key (lowercase, non-alphanumeric → "_"). */
function slugifyFieldKey(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

/** Kebab-case public-link slug from the flow name (mirrors the server's normalizer). */
function slugifyFlowSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

/** Agent name for authed runs; requester name/email + "(public)" for share-link runs. */
function flowRunInitiator(r: TicketFlowRun): string {
  if (r.initiated_by_name) return r.initiated_by_name;
  const who = r.requester_name || r.requester_email;
  return who ? `${who} (public)` : "—";
}

function flowRunStatusPill(status: TicketFlowRun["status"]) {
  if (status === "completed") return <span className="pill ok">completed</span>;
  if (status === "failed") return <span className="pill danger">failed</span>;
  if (status === "partial") return <span className="pill">partial</span>;
  return <span className="pill">pending</span>;
}

function flowStepStatusPill(status: TicketFlowRunStep["status"]) {
  if (status === "done") return <span className="pill ok">done</span>;
  if (status === "failed") return <span className="pill danger">failed</span>;
  return <span className="pill">skipped</span>;
}

/** Ordered per-step status list, shared by the initiate summary and history. */
function FlowRunSteps({ steps }: { steps: TicketFlowRunStep[] }) {
  if (!steps.length) return <p className="hint">No steps ran for this flow.</p>;
  return (
    <ol className="flow-run-steps" style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 8 }}>
      {steps.map((s) => (
        <li key={s.id}>
          <div className="btn-row" style={{ alignItems: "center", gap: 8 }}>
            <strong>{s.label}</strong>
            {flowStepStatusPill(s.status)}
            {s.type === "create_ticket" && s.ticket_id ? (
              <Link className="btn small" to={`/tickets/${s.ticket_id}`}>
                View ticket
              </Link>
            ) : null}
          </div>
          {s.detail ? (
            <div className="faint" style={{ fontSize: 12 }}>
              {s.detail}
            </div>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function TicketFlowTab() {
  const { data, loading, error, reload } = useApi((signal) => listTicketFlows(signal), []);
  const flows = data?.flows ?? [];
  const [editing, setEditing] = useState<TicketFlow | "new" | null>(null);
  const [initiating, setInitiating] = useState<TicketFlow | null>(null);
  const [history, setHistory] = useState<TicketFlow | null>(null);
  const toggleMut = useMutation(updateTicketFlow);
  const deleteMut = useMutation(deleteTicketFlow);

  async function toggleActive(flow: TicketFlow, active: boolean) {
    const res = await toggleMut.run(flow.id, { is_active: active ? 1 : 0 });
    if (res) reload();
  }
  async function remove(flow: TicketFlow) {
    const res = await deleteMut.run(flow.id);
    if (res) reload();
  }

  // Editing swaps the list for the on-page builder (no dialog).
  if (editing) {
    return (
      <FlowBuilderCard
        flow={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
      />
    );
  }

  return (
    <>
      <SettingsListCard
        title="Ticket Flows"
        count={flows.length}
        addLabel="New flow"
        onAdd={() => setEditing("new")}
      >
        {toggleMut.error ? <Alert kind="error">{toggleMut.error}</Alert> : null}
        {deleteMut.error ? <Alert kind="error">{deleteMut.error}</Alert> : null}
        {loading ? (
          <Loading label="Loading ticket flows…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : flows.length === 0 ? (
          <EmptyState>No ticket flows yet — create one to auto-create tickets from an intake form.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Steps</th>
                <th>Active</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {flows.map((flow) => (
                <tr key={flow.id}>
                  <td>
                    <strong>{flow.name}</strong>
                    {flow.public_enabled === 1 ? (
                      <span className="pill ok" style={{ marginLeft: 8 }}>
                        public
                      </span>
                    ) : null}
                    {flow.description ? (
                      <div className="faint" style={{ fontSize: 12 }}>
                        {flow.description}
                      </div>
                    ) : null}
                  </td>
                  <td className="faint">{flow.steps.length}</td>
                  <td>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={flow.is_active === 1}
                        onChange={(e) => toggleActive(flow, e.target.checked)}
                      />
                    </label>
                  </td>
                  <td className="row-actions">
                    <button className="btn small" onClick={() => setEditing(flow)}>
                      Edit
                    </button>
                    <button
                      className="btn small"
                      disabled={flow.is_active !== 1}
                      title={flow.is_active !== 1 ? "Activate the flow to initiate it" : undefined}
                      onClick={() => setInitiating(flow)}
                    >
                      Initiate
                    </button>
                    <button className="btn small" onClick={() => setHistory(flow)}>
                      History
                    </button>
                    <button
                      className="btn small danger"
                      onClick={() =>
                        confirmThen(`Delete "${flow.name}"? This removes the flow and its run history.`, () =>
                          remove(flow),
                        )
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {initiating ? <InitiateFlowModal flow={initiating} onClose={() => setInitiating(null)} /> : null}
      {history ? <FlowHistoryModal flow={history} onClose={() => setHistory(null)} /> : null}
    </>
  );
}

function FlowBuilderCard({
  flow,
  onClose,
  onSaved,
}: {
  flow: TicketFlow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(flow?.name ?? "");
  const [description, setDescription] = useState(flow?.description ?? "");
  const [fields, setFields] = useState<TicketFlowField[]>(flow?.intake_form ?? []);
  const [steps, setSteps] = useState<TicketFlowStep[]>(flow?.steps ?? []);
  const [tab, setTab] = useState<"form" | "steps" | "share">("form");
  // Sharing & branding (0110)
  const [publicEnabled, setPublicEnabled] = useState(flow?.public_enabled === 1);
  const [publicSlug, setPublicSlug] = useState(flow?.public_slug ?? "");
  const [brandLogoUrl, setBrandLogoUrl] = useState(flow?.brand_logo_url ?? "");
  const [brandColor, setBrandColor] = useState(flow?.brand_color ?? "");
  const [copied, setCopied] = useState(false);
  const templatesApi = useApi((signal) => listTicketTemplates(signal), []);
  const templates: TicketTemplate[] = templatesApi.data?.templates ?? [];
  const createMut = useMutation(createTicketFlow);
  const updateMut = useMutation(updateTicketFlow);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  // Fields eligible to gate a step (checkbox / dropdown answers).
  const conditionFields = fields.filter((f) => f.type === "checkbox" || f.type === "select");

  // ---- intake fields -------------------------------------------------------
  function addField() {
    setFields((prev) => [...prev, { key: "", label: "", type: "text" }]);
  }
  function patchField(index: number, patch: Partial<TicketFlowField>) {
    setFields((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  }
  function setFieldLabel(index: number, label: string) {
    setFields((prev) =>
      prev.map((f, i) =>
        i === index ? { ...f, label, key: f.key || slugifyFieldKey(label) } : f,
      ),
    );
  }
  function removeField(index: number) {
    setFields((prev) => prev.filter((_, i) => i !== index));
  }
  function moveField(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= fields.length) return;
    setFields((prev) => {
      const copy = [...prev];
      [copy[index], copy[next]] = [copy[next]!, copy[index]!];
      return copy;
    });
  }

  // ---- steps ---------------------------------------------------------------
  function addCreateStep() {
    setSteps((prev) => [
      ...prev,
      {
        id: flowUid("s"),
        type: "create_ticket",
        label: "Create ticket",
        config: { template_id: "", subject: "", description: "" } satisfies TicketFlowCreateStepConfig,
      },
    ]);
  }
  function addEmailStep() {
    setSteps((prev) => [
      ...prev,
      {
        id: flowUid("s"),
        type: "send_email",
        label: "Send email",
        config: { to: "initiator", subject: "", body: "" } satisfies TicketFlowEmailStepConfig,
      },
    ]);
  }
  function patchStep(id: string, patch: Partial<TicketFlowStep>) {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }
  function patchStepConfig(id: string, patch: Record<string, unknown>) {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, config: { ...s.config, ...patch } } : s)));
  }
  function setStepCondition(id: string, condition: TicketFlowStep["condition"]) {
    setSteps((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        if (condition === undefined) {
          const copy = { ...s };
          delete copy.condition;
          return copy;
        }
        return { ...s, condition };
      }),
    );
  }
  function removeStep(id: string) {
    setSteps((prev) => prev.filter((s) => s.id !== id));
  }
  function moveStep(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= steps.length) return;
    setSteps((prev) => {
      const copy = [...prev];
      [copy[index], copy[next]] = [copy[next]!, copy[index]!];
      return copy;
    });
  }

  // Public share link: an explicit slug wins; otherwise derive from the name.
  const effectiveSlug = publicSlug.trim() ? slugifyFlowSlug(publicSlug) : slugifyFlowSlug(name);
  const shareUrl = `${window.location.origin}/flow/${effectiveSlug || "…"}`;

  function copyShareUrl() {
    void navigator.clipboard?.writeText(shareUrl).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    // Drop fields that never got a label/key so we don't persist blanks.
    const cleanFields = fields.filter((f) => f.key.trim() && f.label.trim());
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      intake_form: cleanFields,
      steps,
      public_enabled: publicEnabled ? 1 : 0,
      // Keep the slug when sharing is off so re-enabling restores the same URL.
      public_slug: effectiveSlug || null,
      brand_logo_url: brandLogoUrl.trim() || null,
      brand_color: brandColor.trim() || null,
    };
    const run = flow
      ? updateMut.run(flow.id, body)
      : createMut.run(body);
    void run.then((res) => res && onSaved());
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>{flow ? `Edit flow — ${flow.name}` : "New ticket flow"}</h3>
        <div className="btn-row" style={{ alignItems: "center" }}>
          <button className="btn small" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button className="btn small primary" type="submit" form="flow-builder-form" disabled={!name.trim() || pending}>
            {pending ? <Spinner label="Saving…" /> : flow ? "Save flow" : "Create flow"}
          </button>
        </div>
      </div>
      <div className="card-body">
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="flow-builder-form" onSubmit={onSubmit} style={{ display: "grid", gap: 16 }}>
        <div className="btn-row" style={{ gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label className="field" style={{ flex: "1 1 220px", margin: 0 }}>
            <span className="label">Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. New Staff Onboarding"
              required
              autoFocus
            />
          </label>
          <label className="field" style={{ flex: "2 1 320px", margin: 0 }}>
            <span className="label">Description</span>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this flow collects and does"
            />
          </label>
        </div>

        <div className="seg" role="group" aria-label="Flow builder section" style={{ justifySelf: "start" }}>
          <button
            type="button"
            className={`seg-btn${tab === "form" ? " active" : ""}`}
            onClick={() => setTab("form")}
          >
            Intake form ({fields.length})
          </button>
          <button
            type="button"
            className={`seg-btn${tab === "steps" ? " active" : ""}`}
            onClick={() => setTab("steps")}
          >
            Steps ({steps.length})
          </button>
          <button
            type="button"
            className={`seg-btn${tab === "share" ? " active" : ""}`}
            onClick={() => setTab("share")}
          >
            Sharing &amp; branding
          </button>
        </div>

        {/* ---- Intake form tab ---- */}
        {tab === "form" ? (
          <div style={{ display: "grid", gap: 10 }}>
            <div className="btn-row" style={{ justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <span className="hint" style={{ margin: 0 }}>
                Questions the requester answers when initiating this flow.
              </span>
              <button type="button" className="btn small" onClick={addField}>
                Add field
              </button>
            </div>
            {fields.length === 0 ? (
              <p className="hint" style={{ margin: 0 }}>
                No fields yet — use “Add field” to start.
              </p>
            ) : (
              fields.map((f, i) => (
                <div key={i} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12, display: "grid", gap: 10 }}>
                  <div className="btn-row" style={{ justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <span className="pill">Field {i + 1}</span>
                    <div className="btn-row" style={{ gap: 4 }}>
                      <button type="button" className="btn small" onClick={() => moveField(i, -1)} disabled={i === 0} aria-label="Move up" title="Move up">
                        ↑
                      </button>
                      <button type="button" className="btn small" onClick={() => moveField(i, 1)} disabled={i === fields.length - 1} aria-label="Move down" title="Move down">
                        ↓
                      </button>
                      <button type="button" className="btn small danger" onClick={() => removeField(i)}>
                        Remove
                      </button>
                    </div>
                  </div>
                  <div className="btn-row" style={{ alignItems: "flex-end", gap: 8, flexWrap: "wrap" }}>
                    <label className="field" style={{ flex: "2 1 160px", margin: 0 }}>
                      <span className="label">Label</span>
                      <input
                        type="text"
                        value={f.label}
                        onChange={(e) => setFieldLabel(i, e.target.value)}
                        placeholder="e.g. Work email"
                      />
                    </label>
                    <label className="field" style={{ flex: "1 1 120px", margin: 0 }}>
                      <span className="label">Type</span>
                      <select
                        value={f.type}
                        onChange={(e) => patchField(i, { type: e.target.value as TicketFlowFieldType })}
                      >
                        {FLOW_FIELD_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  {f.type === "select" ? (
                    <label className="field" style={{ margin: 0 }}>
                      <span className="label">Options (comma-separated)</span>
                      <input
                        type="text"
                        value={(f.options ?? []).join(", ")}
                        onChange={(e) =>
                          patchField(i, {
                            options: e.target.value
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean),
                          })
                        }
                        placeholder="e.g. Faculty, Staff, Adjunct"
                      />
                    </label>
                  ) : null}
                  <div className="btn-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                    <span className="hint" style={{ margin: 0 }}>
                      Token: <code>{`{{${f.key || "…"}}}`}</code>
                    </span>
                    <label className="toggle" style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={f.required === true}
                        onChange={(e) => patchField(i, { required: e.target.checked })}
                      />
                      Required
                    </label>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : null}

        {/* ---- Steps tab ---- */}
        {tab === "steps" ? (
          <div style={{ display: "grid", gap: 10 }}>
            <div className="btn-row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <span className="hint" style={{ margin: 0 }}>
                Steps run top to bottom when the flow is initiated.
              </span>
              <div className="btn-row" style={{ gap: 6 }}>
                <button type="button" className="btn small" onClick={addCreateStep}>
                  + Create Ticket
                </button>
                <button type="button" className="btn small" onClick={addEmailStep}>
                  + Send Email
                </button>
              </div>
            </div>
            {steps.length === 0 ? (
              <p className="hint" style={{ margin: 0 }}>
                No steps yet. Add “Create Ticket” or “Send Email” steps — they run top to bottom when initiated.
              </p>
            ) : (
              steps.map((step, i) => (
                <div key={step.id} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12, display: "grid", gap: 10 }}>
                  <div className="btn-row" style={{ justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <div className="btn-row" style={{ gap: 8, alignItems: "center" }}>
                      <span className="faint">Step {i + 1}</span>
                      <span className="pill">{step.type === "create_ticket" ? "Create ticket" : "Send email"}</span>
                    </div>
                    <div className="btn-row" style={{ gap: 4 }}>
                      <button type="button" className="btn small" onClick={() => moveStep(i, -1)} disabled={i === 0} aria-label="Move up" title="Move up">
                        ↑
                      </button>
                      <button type="button" className="btn small" onClick={() => moveStep(i, 1)} disabled={i === steps.length - 1} aria-label="Move down" title="Move down">
                        ↓
                      </button>
                      <button type="button" className="btn small danger" onClick={() => removeStep(step.id)}>
                        Remove
                      </button>
                    </div>
                  </div>
                  <label className="field" style={{ margin: 0 }}>
                    <span className="label">Step label</span>
                    <input
                      type="text"
                      value={step.label}
                      onChange={(e) => patchStep(step.id, { label: e.target.value })}
                      placeholder="Shown in the run history"
                    />
                  </label>

                  {step.type === "create_ticket" ? (
                    <FlowCreateStepFields
                      step={step}
                      templates={templates}
                      conditionFields={conditionFields}
                      allFields={fields}
                      priorCreateSteps={steps
                        .slice(0, i)
                        .filter((s) => s.type === "create_ticket")}
                      onConfig={(patch) => patchStepConfig(step.id, patch)}
                      onCondition={(condition) => setStepCondition(step.id, condition)}
                      onStep={(patch) => patchStep(step.id, patch)}
                    />
                  ) : (
                    <FlowEmailStepFields
                      step={step}
                      onConfig={(patch) => patchStepConfig(step.id, patch)}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        ) : null}

        {/* ---- Sharing & branding tab (0110) ---- */}
        {tab === "share" ? (
          <div style={{ display: "grid", gap: 14, maxWidth: 560 }}>
            <label className="toggle" style={{ margin: 0 }}>
              <input
                type="checkbox"
                checked={publicEnabled}
                onChange={(e) => setPublicEnabled(e.target.checked)}
              />
              Share with a public link
            </label>
            <span className="hint" style={{ margin: 0 }}>
              Anyone with the link can open the intake form and initiate this flow — no login needed. The
              flow must also be Active.
            </span>
            {publicEnabled ? (
              <>
                <label className="field" style={{ margin: 0 }}>
                  <span className="label">Public link</span>
                  <div className="btn-row" style={{ gap: 6 }}>
                    <input
                      type="text"
                      value={publicSlug}
                      onChange={(e) => setPublicSlug(e.target.value)}
                      placeholder={slugifyFlowSlug(name) || "flow-name"}
                      style={{ flex: 1 }}
                    />
                    <button type="button" className="btn small" onClick={copyShareUrl} disabled={!effectiveSlug}>
                      {copied ? "Copied!" : "Copy link"}
                    </button>
                  </div>
                  <span className="hint">
                    <span className="mono">{shareUrl}</span> — save the flow to apply link changes.
                  </span>
                </label>
                <label className="field" style={{ margin: 0 }}>
                  <span className="label">Custom logo URL (optional)</span>
                  <input
                    type="text"
                    value={brandLogoUrl}
                    onChange={(e) => setBrandLogoUrl(e.target.value)}
                    placeholder="https://…/logo.png"
                  />
                  <span className="hint">Shown at the top of the public page instead of the default logo.</span>
                </label>
                {brandLogoUrl.trim() ? (
                  <img
                    src={brandLogoUrl}
                    alt="Logo preview"
                    style={{ maxHeight: 40, maxWidth: 220, objectFit: "contain", justifySelf: "start" }}
                  />
                ) : null}
                <label className="field" style={{ margin: 0 }}>
                  <span className="label">Brand colour (optional)</span>
                  <div className="color-row">
                    <input
                      type="color"
                      className="color-swatch"
                      value={brandColor.trim() || "#047857"}
                      onChange={(e) => setBrandColor(e.target.value)}
                    />
                    <span className="mono faint">{brandColor.trim() || "default"}</span>
                    {brandColor.trim() ? (
                      <button type="button" className="btn small" onClick={() => setBrandColor("")}>
                        Reset
                      </button>
                    ) : null}
                  </div>
                  <span className="hint">Colours the buttons and accents on the public page.</span>
                </label>
              </>
            ) : null}
          </div>
        ) : null}
      </form>
      </div>
    </div>
  );
}

function FlowCreateStepFields({
  step,
  templates,
  conditionFields,
  allFields,
  priorCreateSteps,
  onConfig,
  onCondition,
  onStep,
}: {
  step: TicketFlowStep;
  templates: TicketTemplate[];
  conditionFields: TicketFlowField[];
  allFields: TicketFlowField[];
  /** Earlier create-ticket steps this one can wait on (a closed one gates it). */
  priorCreateSteps: TicketFlowStep[];
  onConfig: (patch: Partial<TicketFlowCreateStepConfig>) => void;
  onCondition: (condition: TicketFlowStep["condition"]) => void;
  onStep: (patch: Partial<TicketFlowStep>) => void;
}) {
  const cfg = step.config as TicketFlowCreateStepConfig;
  const cond = step.condition;
  const condField = cond ? allFields.find((f) => f.key === cond.field_key) : undefined;
  return (
    <>
      <label className="field" style={{ margin: 0 }}>
        <span className="label">Ticket template</span>
        <select value={cfg.template_id} onChange={(e) => onConfig({ template_id: e.target.value })}>
          <option value="">— Select a template —</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
          {cfg.template_id && !templates.some((t) => t.id === cfg.template_id) ? (
            <option value={cfg.template_id}>{cfg.template_id} (missing)</option>
          ) : null}
        </select>
      </label>
      <label className="field" style={{ margin: 0 }}>
        <span className="label">Subject override</span>
        <input
          type="text"
          value={cfg.subject ?? ""}
          onChange={(e) => onConfig({ subject: e.target.value })}
          placeholder="Optional — falls back to the template subject"
        />
        <span className="hint">
          Use <code>{"{{field_key}}"}</code> tokens (e.g. <code>{"{{new_hire_name}}"}</code>).
        </span>
      </label>
      <label className="field" style={{ margin: 0 }}>
        <span className="label">Description override</span>
        <textarea
          rows={2}
          value={cfg.description ?? ""}
          onChange={(e) => onConfig({ description: e.target.value })}
          placeholder="Optional — falls back to the template description"
        />
      </label>
      <label className="field" style={{ margin: 0 }}>
        <span className="label">Only run when</span>
        <select
          value={cond?.field_key ?? ""}
          onChange={(e) => {
            const key = e.target.value;
            if (!key) {
              onCondition(undefined);
              return;
            }
            const f = conditionFields.find((x) => x.key === key);
            const equals = f?.type === "checkbox" ? true : f?.options?.[0] ?? "";
            onCondition({ field_key: key, equals });
          }}
        >
          <option value="">— Always run —</option>
          {conditionFields.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label || f.key}
            </option>
          ))}
          {cond && !conditionFields.some((f) => f.key === cond.field_key) ? (
            <option value={cond.field_key}>{cond.field_key}</option>
          ) : null}
        </select>
        {cond && condField?.type === "select" ? (
          <select
            style={{ marginTop: 6 }}
            value={String(cond.equals ?? "")}
            onChange={(e) => onCondition({ field_key: cond.field_key, equals: e.target.value })}
          >
            {(condField.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        ) : null}
        {cond && condField?.type === "checkbox" ? <span className="hint">Runs when this box is checked.</span> : null}
      </label>
      {priorCreateSteps.length > 0 ? (
        <label className="field" style={{ margin: 0 }}>
          <span className="label">Create only after this ticket closes</span>
          <select
            value={step.depends_on ?? ""}
            onChange={(e) => onStep({ depends_on: e.target.value || null })}
          >
            <option value="">— Create right away —</option>
            {priorCreateSteps.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label || "Create ticket"}
              </option>
            ))}
            {step.depends_on && !priorCreateSteps.some((s) => s.id === step.depends_on) ? (
              <option value={step.depends_on}>(step removed)</option>
            ) : null}
          </select>
          <span className="hint">This step is created once the selected ticket is closed.</span>
        </label>
      ) : null}
    </>
  );
}

function FlowEmailStepFields({
  step,
  onConfig,
}: {
  step: TicketFlowStep;
  onConfig: (patch: Partial<TicketFlowEmailStepConfig>) => void;
}) {
  const cfg = step.config as TicketFlowEmailStepConfig;
  return (
    <>
      <label className="field" style={{ margin: 0 }}>
        <span className="label">Recipient</span>
        <select value={cfg.to} onChange={(e) => onConfig({ to: e.target.value as "initiator" | "custom" })}>
          <option value="initiator">The person initiating the flow</option>
          <option value="custom">A custom address</option>
        </select>
      </label>
      {cfg.to === "custom" ? (
        <label className="field" style={{ margin: 0 }}>
          <span className="label">To address</span>
          <input
            type="text"
            value={cfg.to_custom ?? ""}
            onChange={(e) => onConfig({ to_custom: e.target.value })}
            placeholder="e.g. {{manager_email}}"
          />
          <span className="hint">Tokens allowed, e.g. <code>{"{{manager_email}}"}</code>.</span>
        </label>
      ) : null}
      <label className="field" style={{ margin: 0 }}>
        <span className="label">Subject</span>
        <input
          type="text"
          value={cfg.subject}
          onChange={(e) => onConfig({ subject: e.target.value })}
          placeholder="e.g. Onboarding started for {{new_hire_name}}"
        />
      </label>
      <label className="field" style={{ margin: 0 }}>
        <span className="label">Body</span>
        <textarea
          rows={4}
          value={cfg.body}
          onChange={(e) => onConfig({ body: e.target.value })}
          placeholder="Plain text. Tokens like {{new_hire_name}} are substituted."
        />
      </label>
    </>
  );
}

function InitiateFlowModal({ flow, onClose }: { flow: TicketFlow; onClose: () => void }) {
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const init: Record<string, unknown> = {};
    for (const f of flow.intake_form) init[f.key] = f.type === "checkbox" ? false : "";
    return init;
  });
  const [missing, setMissing] = useState<string[]>([]);
  const [run, setRun] = useState<TicketFlowRun | null>(null);
  const mut = useMutation(initiateTicketFlow);

  function setValue(key: string, v: unknown) {
    setValues((prev) => ({ ...prev, [key]: v }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const unmet = flow.intake_form
      .filter((f) => {
        if (!f.required) return false;
        const v = values[f.key];
        return f.type === "checkbox" ? v !== true : String(v ?? "").trim() === "";
      })
      .map((f) => f.key);
    setMissing(unmet);
    if (unmet.length > 0) return;
    const res = await mut.run(flow.id, values);
    if (res) setRun(res.run);
  }

  return (
    <Modal
      title={`Initiate — ${flow.name}`}
      onClose={onClose}
      footer={
        run ? (
          <button className="btn primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button className="btn primary" type="submit" form="flow-initiate-form" disabled={mut.pending}>
              {mut.pending ? <Spinner label="Running…" /> : "Initiate flow"}
            </button>
          </>
        )
      }
    >
      {run ? (
        <div style={{ display: "grid", gap: 12 }}>
          <div className="btn-row" style={{ alignItems: "center", gap: 8 }}>
            <strong>Result</strong>
            {flowRunStatusPill(run.status)}
          </div>
          <FlowRunSteps steps={run.steps ?? []} />
        </div>
      ) : (
        <>
          {mut.error ? <Alert kind="error">{mut.error}</Alert> : null}
          {flow.description ? (
            <p className="hint" style={{ marginTop: 0 }}>
              {flow.description}
            </p>
          ) : null}
          <form id="flow-initiate-form" onSubmit={onSubmit} style={{ display: "grid", gap: 12 }}>
            {flow.intake_form.length === 0 ? (
              <p className="hint">This flow has no intake fields — just initiate it.</p>
            ) : (
              flow.intake_form.map((f) => {
                const invalid = missing.includes(f.key);
                if (f.type === "checkbox") {
                  return (
                    <label key={f.key} className="toggle">
                      <input
                        type="checkbox"
                        checked={values[f.key] === true}
                        onChange={(e) => setValue(f.key, e.target.checked)}
                      />
                      {f.label}
                      {f.required ? " *" : ""}
                    </label>
                  );
                }
                return (
                  <label key={f.key} className="field" style={{ margin: 0 }}>
                    <span className="label">
                      {f.label}
                      {f.required ? " *" : ""}
                    </span>
                    {f.type === "textarea" ? (
                      <textarea
                        rows={3}
                        value={String(values[f.key] ?? "")}
                        onChange={(e) => setValue(f.key, e.target.value)}
                      />
                    ) : f.type === "select" ? (
                      <select value={String(values[f.key] ?? "")} onChange={(e) => setValue(f.key, e.target.value)}>
                        <option value="">— Select —</option>
                        {(f.options ?? []).map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={f.type === "email" ? "email" : f.type === "date" ? "date" : "text"}
                        value={String(values[f.key] ?? "")}
                        onChange={(e) => setValue(f.key, e.target.value)}
                      />
                    )}
                    {invalid ? <span className="hint" style={{ color: "var(--danger)" }}>This field is required.</span> : null}
                  </label>
                );
              })
            )}
          </form>
        </>
      )}
    </Modal>
  );
}

function FlowHistoryModal({ flow, onClose }: { flow: TicketFlow; onClose: () => void }) {
  const { data, loading, error, reload } = useApi((signal) => listTicketFlowRuns(flow.id, signal), [flow.id]);
  const runs = data?.runs ?? [];
  const [selected, setSelected] = useState<{ run: TicketFlowRun; steps: TicketFlowRunStep[] } | null>(null);
  const detailMut = useMutation(getTicketFlowRun);

  async function openRun(runId: string) {
    const res = await detailMut.run(runId);
    if (res) setSelected({ run: res.run, steps: res.steps });
  }

  return (
    <Modal
      title={`Run history — ${flow.name}`}
      onClose={onClose}
      wide
      footer={
        selected ? (
          <button className="btn" onClick={() => setSelected(null)}>
            Back to runs
          </button>
        ) : (
          <button className="btn" onClick={onClose}>
            Close
          </button>
        )
      }
    >
      {selected ? (
        <div style={{ display: "grid", gap: 12 }}>
          <div className="btn-row" style={{ alignItems: "center", gap: 8 }}>
            <strong>{formatDate(selected.run.created_at)}</strong>
            {flowRunStatusPill(selected.run.status)}
            <span className="faint">by {flowRunInitiator(selected.run)}</span>
          </div>
          <FlowRunSteps steps={selected.steps} />
        </div>
      ) : loading ? (
        <Loading label="Loading runs…" />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : runs.length === 0 ? (
        <EmptyState>This flow has not been initiated yet.</EmptyState>
      ) : (
        <>
          {detailMut.error ? <Alert kind="error">{detailMut.error}</Alert> : null}
          <table className="data">
            <thead>
              <tr>
                <th>Initiated by</th>
                <th>When</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td>{flowRunInitiator(r)}</td>
                  <td className="faint">{formatDate(r.created_at)}</td>
                  <td>{flowRunStatusPill(r.status)}</td>
                  <td className="row-actions">
                    <button className="btn small" onClick={() => openRun(r.id)} disabled={detailMut.pending}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Modal>
  );
}

// ===== Statuses ============================================================

function StatusesTab() {
  const { data, loading, error, reload } = useApi((signal) => listStatuses(signal), []);
  const statuses = data?.statuses ?? [];
  // null = closed, "new" = add, row = edit.
  const [editing, setEditing] = useState<TicketStatusRow | "new" | null>(null);
  const [deleting, setDeleting] = useState<TicketStatusRow | null>(null);
  const reorderMut = useMutation(reorderStatuses);

  // Swap a row with its neighbor and persist the full id order.
  async function move(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= statuses.length) return;
    const ids = statuses.map((s) => s.id);
    [ids[index], ids[next]] = [ids[next]!, ids[index]!];
    const res = await reorderMut.run(ids);
    if (res) reload();
  }

  return (
    <>
      <SettingsListCard
        title="Ticket statuses"
        count={statuses.length}
        addLabel="Add status"
        onAdd={() => setEditing("new")}
      >
        {loading ? (
          <Loading label="Loading statuses…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : statuses.length === 0 ? (
          <EmptyState>No statuses defined.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Category</th>
                <th>Color</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {statuses.map((s, i) => (
                <StatusRow
                  key={s.id}
                  status={s}
                  onEdit={() => setEditing(s)}
                  onDelete={() => setDeleting(s)}
                  onChanged={reload}
                  onMoveUp={() => move(i, -1)}
                  onMoveDown={() => move(i, 1)}
                  isFirst={i === 0}
                  isLast={i === statuses.length - 1}
                  reordering={reorderMut.pending}
                />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <StatusModal
          status={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}

      {deleting ? (
        <StatusDeleteModal
          status={deleting}
          statuses={statuses}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Delete-status flow. The status FK on tickets is NOT NULL, so existing
 *  tickets must move to another status first — the admin picks which. */
function StatusDeleteModal({
  status,
  statuses,
  onClose,
  onDeleted,
}: {
  status: TicketStatusRow;
  statuses: TicketStatusRow[];
  onClose: () => void;
  onDeleted: () => void;
}) {
  const others = statuses.filter((s) => s.id !== status.id);
  // Default the target to a closed-category status, else the default, else the first.
  const initialTarget =
    others.find((s) => s.category === "closed")?.id ??
    others.find((s) => s.is_default)?.id ??
    others[0]?.id ??
    "";
  const [target, setTarget] = useState(initialTarget);
  const delMut = useMutation(deleteStatus);

  async function onConfirm() {
    const res = await delMut.run(status.id, target || undefined);
    if (res) onDeleted();
  }

  return (
    <Modal
      title={`Delete status “${status.name}”`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={delMut.pending}>
            Cancel
          </button>
          <button className="btn danger" onClick={onConfirm} disabled={delMut.pending || !target}>
            {delMut.pending ? <Spinner label="Deleting…" /> : "Delete status"}
          </button>
        </>
      }
    >
      {delMut.error ? <Alert kind="error">{delMut.error}</Alert> : null}
      <p className="hint" style={{ marginTop: 0 }}>
        Any tickets currently set to <strong>{status.name}</strong> will be moved to the status
        you choose below, then <strong>{status.name}</strong> is removed.
      </p>
      <label className="field">
        <span className="label">Move existing tickets to</span>
        <select value={target} onChange={(e) => setTarget(e.target.value)} disabled={delMut.pending}>
          {others.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
    </Modal>
  );
}

/** Add/Edit status modal — null status = add. */
/** Color picker for a status: an "auto" toggle + a native swatch. null = fall
 *  back to the UI's default per-code accent on the board. Shared by the status
 *  modals (ticket, task, facility). */
/** Tiny inline swatch for a status' saved color in list rows. Renders the hex as
 *  a filled dot; a muted "—" when no color is configured. */
function ColorSwatch({ color }: { color: string | null | undefined }) {
  if (!color) return <span className="faint" title="No custom color">—</span>;
  return (
    <span
      title={color}
      style={{
        display: "inline-block",
        width: 14,
        height: 14,
        borderRadius: 4,
        background: color,
        border: "1px solid rgba(0,0,0,0.15)",
        verticalAlign: "middle",
      }}
    />
  );
}

function StatusColorField({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const custom = value != null;
  return (
    <label className="field">
      <span className="label">Color</span>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <input
          type="checkbox"
          checked={custom}
          onChange={(e) => onChange(e.target.checked ? value ?? "#2563eb" : null)}
          aria-label="Use a custom color"
        />
        <input
          type="color"
          value={value ?? "#2563eb"}
          disabled={!custom}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 44, height: 30, padding: 0, border: "none", background: "none", cursor: custom ? "pointer" : "not-allowed" }}
        />
        <span className="faint">{custom ? value : "Auto (default accent)"}</span>
      </div>
    </label>
  );
}

function StatusModal({
  status,
  onClose,
  onSaved,
}: {
  status: TicketStatusRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(status?.name ?? "");
  const [code, setCode] = useState(status?.code ?? "");
  const [category, setCategory] = useState<"open" | "pending" | "closed">(status?.category ?? "open");
  const [color, setColor] = useState<string | null>(status?.color ?? null);
  const createMut = useMutation(createStatus);
  const updateMut = useMutation(updateStatus);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    const fields = { code: code.trim(), name: name.trim(), category, color };
    const res = status ? await updateMut.run(status.id, fields) : await createMut.run(fields);
    if (res) onSaved();
  }

  return (
    <Modal
      title={status ? "Edit status" : "Add status"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="status-form" disabled={pending || !code.trim() || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : status ? "Save changes" : "Add status"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="status-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Awaiting review" required />
        </label>
        <label className="field">
          <span className="label">Code</span>
          <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="awaiting_review" required />
        </label>
        <label className="field">
          <span className="label">Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value as typeof category)}>
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <StatusColorField value={color} onChange={setColor} />
      </form>
    </Modal>
  );
}

/** One status row: read-only with reorder/Default(★)/Edit/Delete. */
function StatusRow({
  status,
  onEdit,
  onDelete,
  onChanged,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  reordering,
}: {
  status: TicketStatusRow;
  onEdit: () => void;
  onDelete: () => void;
  onChanged: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  reordering: boolean;
}) {
  const defaultMut = useMutation(setDefaultStatus);
  const busy = defaultMut.pending || reordering;

  return (
    <tr>
      <td>
        <strong>{status.name}</strong>
        {status.is_default ? <span className="pill" style={{ marginLeft: 6 }}>default</span> : null}
      </td>
      <td className="mono">{status.code}</td>
      <td><span className="pill">{status.category}</span></td>
      <td><ColorSwatch color={status.color} /></td>
      <td className="row-actions">
        <button className="btn small" onClick={onMoveUp} disabled={busy || isFirst} aria-label="Move up" title="Move up">↑</button>
        <button className="btn small" onClick={onMoveDown} disabled={busy || isLast} aria-label="Move down" title="Move down">↓</button>
        {!status.is_default ? (
          <button className="btn small ghost" onClick={() => defaultMut.run(status.id).then(onChanged)} disabled={busy} title="Make default">
            ★
          </button>
        ) : null}
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={onDelete}
          disabled={busy || !!status.is_default}
          title={status.is_default ? "Can't delete the default status" : "Delete"}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ===== Priorities ==========================================================

function PrioritiesTab() {
  const { data, loading, error, reload } = useApi((signal) => listPriorities(signal), []);
  const priorities = data?.priorities ?? [];
  const [editing, setEditing] = useState<TicketPriorityRow | "new" | null>(null);

  return (
    <>
      <SettingsListCard
        title="Ticket priorities"
        count={priorities.length}
        addLabel="Add priority"
        onAdd={() => setEditing("new")}
      >
        {loading ? (
          <Loading label="Loading priorities…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : priorities.length === 0 ? (
          <EmptyState>No priorities defined.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Color</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {priorities.map((p) => (
                <PriorityRow key={p.id} priority={p} onEdit={() => setEditing(p)} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <PriorityModal
          priority={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Action-item / project-task priorities (0108) — a SEPARATE set from ticket
 *  priorities. Same editor + color, backed by /api/config/task-priorities. */
function TaskPrioritiesSection() {
  const { data, loading, error, reload } = useApi((signal) => listTaskPriorities(signal), []);
  const priorities = data?.priorities ?? [];
  const [editing, setEditing] = useState<TaskPriorityRow | "new" | null>(null);

  return (
    <>
      <SettingsListCard
        title="Action item priorities"
        count={priorities.length}
        addLabel="Add priority"
        onAdd={() => setEditing("new")}
      >
        {loading ? (
          <Loading label="Loading priorities…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : priorities.length === 0 ? (
          <EmptyState>No priorities defined.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Color</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {priorities.map((p) => (
                <TaskPriorityRow key={p.id} priority={p} onEdit={() => setEditing(p)} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <TaskPriorityModal
          priority={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function TaskPriorityModal({
  priority,
  onClose,
  onSaved,
}: {
  priority: TaskPriorityRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(priority?.name ?? "");
  const [code, setCode] = useState(priority?.code ?? "");
  const [color, setColor] = useState<string | null>(priority?.color ?? null);
  const createMut = useMutation(createTaskPriority);
  const updateMut = useMutation(updateTaskPriority);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    const fields = { code: code.trim(), name: name.trim(), color };
    const res = priority ? await updateMut.run(priority.id, fields) : await createMut.run(fields);
    if (res) onSaved();
  }

  return (
    <Modal
      title={priority ? "Edit priority" : "Add priority"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="task-priority-form" disabled={pending || !code.trim() || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : priority ? "Save changes" : "Add priority"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="task-priority-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Critical" required />
        </label>
        <label className="field">
          <span className="label">Code</span>
          <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="critical" required />
        </label>
        <StatusColorField value={color} onChange={setColor} />
      </form>
    </Modal>
  );
}

function TaskPriorityRow({ priority, onEdit, onChanged }: { priority: TaskPriorityRow; onEdit: () => void; onChanged: () => void }) {
  const deleteMut = useMutation(deleteTaskPriority);
  const defaultMut = useMutation(setDefaultTaskPriority);
  const busy = deleteMut.pending || defaultMut.pending;

  return (
    <tr>
      <td>
        <strong>{priority.name}</strong>
        {priority.is_default ? <span className="pill" style={{ marginLeft: 6 }}>default</span> : null}
      </td>
      <td className="mono">{priority.code}</td>
      <td><ColorSwatch color={priority.color} /></td>
      <td className="row-actions">
        {!priority.is_default ? (
          <button className="btn small ghost" onClick={() => defaultMut.run(priority.id).then(onChanged)} disabled={busy} title="Make default">
            ★
          </button>
        ) : null}
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() => confirmThen(`Delete priority "${priority.name}"?`, () => deleteMut.run(priority.id).then(onChanged))}
          disabled={busy || !!priority.is_default}
          title={priority.is_default ? "Can't delete the default priority" : "Delete"}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

/** Add/Edit priority modal — null priority = add. */
function PriorityModal({
  priority,
  onClose,
  onSaved,
}: {
  priority: TicketPriorityRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(priority?.name ?? "");
  const [code, setCode] = useState(priority?.code ?? "");
  const [color, setColor] = useState<string | null>(priority?.color ?? null);
  const createMut = useMutation(createPriority);
  const updateMut = useMutation(updatePriority);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    const fields = { code: code.trim(), name: name.trim(), color };
    const res = priority ? await updateMut.run(priority.id, fields) : await createMut.run(fields);
    if (res) onSaved();
  }

  return (
    <Modal
      title={priority ? "Edit priority" : "Add priority"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="priority-form" disabled={pending || !code.trim() || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : priority ? "Save changes" : "Add priority"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="priority-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Critical" required />
        </label>
        <label className="field">
          <span className="label">Code</span>
          <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="critical" required />
        </label>
        <StatusColorField value={color} onChange={setColor} />
      </form>
    </Modal>
  );
}

/** One priority row: read-only with Default(★)/Edit/Delete. */
function PriorityRow({ priority, onEdit, onChanged }: { priority: TicketPriorityRow; onEdit: () => void; onChanged: () => void }) {
  const deleteMut = useMutation(deletePriority);
  const defaultMut = useMutation(setDefaultPriority);
  const busy = deleteMut.pending || defaultMut.pending;

  return (
    <tr>
      <td>
        <strong>{priority.name}</strong>
        {priority.is_default ? <span className="pill" style={{ marginLeft: 6 }}>default</span> : null}
      </td>
      <td className="mono">{priority.code}</td>
      <td><ColorSwatch color={priority.color} /></td>
      <td className="row-actions">
        {!priority.is_default ? (
          <button className="btn small ghost" onClick={() => defaultMut.run(priority.id).then(onChanged)} disabled={busy} title="Make default">
            ★
          </button>
        ) : null}
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() => confirmThen(`Delete priority "${priority.name}"?`, () => deleteMut.run(priority.id).then(onChanged))}
          disabled={busy || !!priority.is_default}
          title={priority.is_default ? "Can't delete the default priority" : "Delete"}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ===== Ticket types (Service Request / Incident / Problem / …) =============

function TicketTypesTab() {
  const { data, loading, error, reload } = useApi((signal) => listTicketTypes(signal), []);
  const types = data?.ticket_types ?? [];
  const [editing, setEditing] = useState<TicketTypeRow | "new" | null>(null);

  return (
    <>
      <SettingsListCard
        title="Ticket types"
        count={types.length}
        addLabel="Add type"
        onAdd={() => setEditing("new")}
      >
        {loading ? (
          <Loading label="Loading ticket types…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : types.length === 0 ? (
          <EmptyState>No ticket types defined.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Color</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {types.map((tt) => (
                <TicketTypeTr key={tt.id} type={tt} onEdit={() => setEditing(tt)} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <TicketTypeModal
          type={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Add/Edit ticket type — null type = add. */
function TicketTypeModal({
  type,
  onClose,
  onSaved,
}: {
  type: TicketTypeRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(type?.name ?? "");
  const [code, setCode] = useState(type?.code ?? "");
  const [color, setColor] = useState<string | null>(type?.color ?? null);
  const createMut = useMutation(createTicketType);
  const updateMut = useMutation(updateTicketType);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  // New types: auto-derive the code from the name until the code is hand-edited.
  const [codeEdited, setCodeEdited] = useState(!!type);
  function onName(v: string) {
    setName(v);
    if (!codeEdited && !type) setCode(v.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, ""));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    const fields = { code: code.trim(), name: name.trim(), color };
    const res = type ? await updateMut.run(type.id, fields) : await createMut.run(fields);
    if (res) onSaved();
  }

  return (
    <Modal
      title={type ? "Edit ticket type" : "Add ticket type"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="ticket-type-form" disabled={pending || !code.trim() || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : type ? "Save changes" : "Add type"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="ticket-type-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => onName(e.target.value)} placeholder="Service Request" autoFocus required />
        </label>
        <label className="field">
          <span className="label">Code</span>
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setCodeEdited(true);
            }}
            placeholder="service_request"
            required
          />
          <span className="hint">Stored on the ticket; lowercase letters, numbers, underscores.</span>
        </label>
        <StatusColorField value={color} onChange={setColor} />
      </form>
    </Modal>
  );
}

/** One ticket-type row: read-only with Default(★)/Edit/Delete. */
function TicketTypeTr({ type, onEdit, onChanged }: { type: TicketTypeRow; onEdit: () => void; onChanged: () => void }) {
  const deleteMut = useMutation(deleteTicketType);
  const defaultMut = useMutation(setDefaultTicketType);
  const busy = deleteMut.pending || defaultMut.pending;

  return (
    <tr>
      <td>
        <strong>{type.name}</strong>
        {type.is_default ? <span className="pill" style={{ marginLeft: 6 }}>default</span> : null}
      </td>
      <td className="mono">{type.code}</td>
      <td><ColorSwatch color={type.color} /></td>
      <td className="row-actions">
        {!type.is_default ? (
          <button className="btn small ghost" onClick={() => defaultMut.run(type.id).then(onChanged)} disabled={busy} title="Make default">
            ★
          </button>
        ) : null}
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() => confirmThen(`Delete ticket type "${type.name}"?`, () => deleteMut.run(type.id).then(onChanged))}
          disabled={busy || !!type.is_default}
          title={type.is_default ? "Can't delete the default type" : "Delete"}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ===== Task statuses (0061: To do / In progress / Done / …) ================
// Configurable lifecycle states for project tasks AND standalone action items.
// tasks.status stores the code; this mirrors the Ticket types tab exactly.

function TaskStatusesTab() {
  const { data, loading, error, reload } = useApi((signal) => listTaskStatuses(signal), []);
  const statuses = data?.task_statuses ?? [];
  const [editing, setEditing] = useState<TaskStatusRow | "new" | null>(null);
  const reorderMut = useMutation(reorderTaskStatuses);

  async function move(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= statuses.length) return;
    const ids = statuses.map((s) => s.id);
    [ids[index], ids[next]] = [ids[next]!, ids[index]!];
    const res = await reorderMut.run(ids);
    if (res) reload();
  }

  return (
    <>
      <SettingsListCard
        title="Action Items statuses"
        count={statuses.length}
        addLabel="Add status"
        onAdd={() => setEditing("new")}
      >
        {loading ? (
          <Loading label="Loading task statuses…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : statuses.length === 0 ? (
          <EmptyState>No task statuses defined.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Color</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {statuses.map((ts, i) => (
                <TaskStatusTr
                  key={ts.id}
                  status={ts}
                  onEdit={() => setEditing(ts)}
                  onChanged={reload}
                  onMoveUp={() => move(i, -1)}
                  onMoveDown={() => move(i, 1)}
                  isFirst={i === 0}
                  isLast={i === statuses.length - 1}
                  reordering={reorderMut.pending}
                />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <TaskStatusModal
          status={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Add/Edit task status — null status = add. */
function TaskStatusModal({
  status,
  onClose,
  onSaved,
}: {
  status: TaskStatusRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(status?.name ?? "");
  const [code, setCode] = useState(status?.code ?? "");
  const [color, setColor] = useState<string | null>(status?.color ?? null);
  const createMut = useMutation(createTaskStatus);
  const updateMut = useMutation(updateTaskStatus);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  // New statuses: auto-derive the code from the name until the code is hand-edited.
  const [codeEdited, setCodeEdited] = useState(!!status);
  function onName(v: string) {
    setName(v);
    if (!codeEdited && !status) setCode(v.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, ""));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    const fields = { code: code.trim(), name: name.trim(), color };
    const res = status ? await updateMut.run(status.id, fields) : await createMut.run(fields);
    if (res) onSaved();
  }

  return (
    <Modal
      title={status ? "Edit task status" : "Add task status"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="task-status-form" disabled={pending || !code.trim() || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : status ? "Save changes" : "Add status"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="task-status-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => onName(e.target.value)} placeholder="In progress" autoFocus required />
        </label>
        <label className="field">
          <span className="label">Code</span>
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setCodeEdited(true);
            }}
            placeholder="in_progress"
            required
          />
          <span className="hint">Stored on the task; lowercase letters, numbers, underscores.</span>
        </label>
        <StatusColorField value={color} onChange={setColor} />
      </form>
    </Modal>
  );
}

/** One task-status row: read-only with Default(★)/Edit/Delete. */
function TaskStatusTr({
  status,
  onEdit,
  onChanged,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  reordering,
}: {
  status: TaskStatusRow;
  onEdit: () => void;
  onChanged: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  reordering: boolean;
}) {
  const deleteMut = useMutation(deleteTaskStatus);
  const defaultMut = useMutation(setDefaultTaskStatus);
  const busy = deleteMut.pending || defaultMut.pending || reordering;

  return (
    <tr>
      <td>
        <strong>{status.name}</strong>
        {status.is_default ? <span className="pill" style={{ marginLeft: 6 }}>default</span> : null}
      </td>
      <td className="mono">{status.code}</td>
      <td><ColorSwatch color={status.color} /></td>
      <td className="row-actions">
        <button className="btn small" onClick={onMoveUp} disabled={busy || isFirst} aria-label="Move up" title="Move up">↑</button>
        <button className="btn small" onClick={onMoveDown} disabled={busy || isLast} aria-label="Move down" title="Move down">↓</button>
        {!status.is_default ? (
          <button className="btn small ghost" onClick={() => defaultMut.run(status.id).then(onChanged)} disabled={busy} title="Make default">
            ★
          </button>
        ) : null}
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() => confirmThen(`Delete task status "${status.name}"?`, () => deleteMut.run(status.id).then(onChanged))}
          disabled={busy || !!status.is_default}
          title={status.is_default ? "Can't delete the default status" : "Delete"}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ===== Categories (three-level Category → Sub Type → Item) ===============

/** Manage the hierarchical "Category → Sub Type → Item" tree shown on tickets.
 *  Top-level rows (parent_id = null) are categories; their children are
 *  sub types, whose children are items (the choices on a ticket cascade). */
type CatEdit =
  | { mode: "new-type" }
  | { mode: "new-sub"; parentId: string; parentName: string }
  | { mode: "edit"; cat: TicketCategoryRow };

function RequestTypesTab() {
  const { data, loading, error, reload } = useApi((signal) => listTicketCategories(signal), []);
  const cats = data?.categories ?? [];
  const roots = cats.filter((c) => !c.parent_id).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  const childrenOf = (id: string) =>
    cats.filter((c) => c.parent_id === id).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  const [editing, setEditing] = useState<CatEdit | null>(null);
  const [filter, setFilter] = useState("");
  const delMut = useMutation(deleteTicketCategory);
  // Resolve a node's default_assignee_id → name so it can show in parentheses.
  const assigneesApi = useApi((signal) => listAssignees(signal), []);
  const assigneeName = (id: string | null | undefined): string | null =>
    id ? assigneesApi.data?.assignees.find((a) => a.id === id)?.name ?? null : null;

  // Tree filter: a node stays visible if it, an ancestor, or a descendant
  // matches — so the matched node keeps its path (ancestors) and its children.
  const q = filter.trim().toLowerCase();
  const hit = (s: string) => s.toLowerCase().includes(q);
  const subHasHit = (subId: string) => childrenOf(subId).some((i) => hit(i.name));
  const rootShown = (rt: TicketCategoryRow) =>
    !q || hit(rt.name) || childrenOf(rt.id).some((s) => hit(s.name) || subHasHit(s.id));
  const subShown = (rt: TicketCategoryRow, sub: TicketCategoryRow) =>
    !q || hit(rt.name) || hit(sub.name) || subHasHit(sub.id);
  const itemShown = (rt: TicketCategoryRow, sub: TicketCategoryRow, item: TicketCategoryRow) =>
    !q || hit(rt.name) || hit(sub.name) || hit(item.name);
  const visibleRoots = roots.filter(rootShown);

  function del(id: string, label: string, isType: boolean) {
    confirmThen(
      isType
        ? `Delete category "${label}"? Its children are kept but un-parented.`
        : `Delete "${label}"?`,
      () => delMut.run(id).then((ok) => ok && reload()),
    );
  }

  return (
    <>
      <SettingsListCard
        title="Categories"
        count={roots.length}
        addLabel="Add category"
        onAdd={() => setEditing({ mode: "new-type" })}
      >
        <p className="sub" style={{ margin: "0 0 12px" }}>
          Three-level ticket categories: Category → Sub Type → Item. Each level can have
          children; the choices on a ticket cascade from the level selected above.
        </p>
        {roots.length > 0 ? (
          <input
            type="search"
            className="input-inline"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter categories, sub types or items…"
            aria-label="Filter categories"
            style={{ width: "100%", marginBottom: 12 }}
          />
        ) : null}
        {delMut.error ? <Alert kind="error">{delMut.error}</Alert> : null}
        {loading ? (
          <Loading label="Loading categories…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : roots.length === 0 ? (
          <EmptyState>No categories defined.</EmptyState>
        ) : visibleRoots.length === 0 ? (
          <EmptyState>No categories match “{filter.trim()}”.</EmptyState>
        ) : (
          <ul className="req-types">
            {visibleRoots.map((rt) => (
              <li key={rt.id} className="req-type">
                <div className="req-type-head">
                  <strong>
                    {rt.name}
                    {assigneeName(rt.default_assignee_id) ? (
                      <span className="faint" style={{ fontWeight: 400, marginLeft: 6 }}>({assigneeName(rt.default_assignee_id)})</span>
                    ) : null}
                  </strong>
                  <button className="btn small ghost req-add" onClick={() => setEditing({ mode: "new-sub", parentId: rt.id, parentName: rt.name })}>
                    + Sub Type
                  </button>
                  <span className="row-actions">
                    <button className="btn small" onClick={() => setEditing({ mode: "edit", cat: rt })}>Edit</button>
                    <button className="btn small danger" onClick={() => del(rt.id, rt.name, true)}>Delete</button>
                  </span>
                </div>
                {childrenOf(rt.id).filter((sub) => subShown(rt, sub)).length ? (
                  <ul className="req-subs">
                    {childrenOf(rt.id).filter((sub) => subShown(rt, sub)).map((sub) => (
                      <li key={sub.id} className="req-sub">
                        <div className="req-sub-head">
                          <span>
                            {sub.name}
                            {assigneeName(sub.default_assignee_id) ? (
                              <span className="faint" style={{ marginLeft: 6 }}>({assigneeName(sub.default_assignee_id)})</span>
                            ) : null}
                          </span>
                          <button className="btn small ghost req-add" onClick={() => setEditing({ mode: "new-sub", parentId: sub.id, parentName: sub.name })}>
                            + Item
                          </button>
                          <span className="row-actions">
                            <button className="btn small" onClick={() => setEditing({ mode: "edit", cat: sub })}>Edit</button>
                            <button className="btn small danger" onClick={() => del(sub.id, sub.name, false)}>Delete</button>
                          </span>
                        </div>
                        {childrenOf(sub.id).filter((leaf) => itemShown(rt, sub, leaf)).length ? (
                          <ul className="req-subsubs">
                            {childrenOf(sub.id).filter((leaf) => itemShown(rt, sub, leaf)).map((leaf) => (
                              <li key={leaf.id} className="req-subsub">
                                <span>
                                  {leaf.name}
                                  {assigneeName(leaf.default_assignee_id) ? (
                                    <span className="faint" style={{ marginLeft: 6 }}>({assigneeName(leaf.default_assignee_id)})</span>
                                  ) : null}
                                </span>
                                <span className="row-actions">
                                  <button className="btn small" onClick={() => setEditing({ mode: "edit", cat: leaf })}>Edit</button>
                                  <button className="btn small danger" onClick={() => del(leaf.id, leaf.name, false)}>Delete</button>
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="req-empty faint">No sub types yet.</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </SettingsListCard>

      {editing ? (
        <CategoryModal
          edit={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Add category / add child (sub type or item) / rename a category node. */
function CategoryModal({ edit, onClose, onSaved }: { edit: CatEdit; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(edit.mode === "edit" ? edit.cat.name : "");
  // 0089: optional default assignee — auto-populated onto a ticket when this
  // category is chosen. Most useful on a leaf Item, but allowed on any node.
  const [defaultAssignee, setDefaultAssignee] = useState<string>(
    edit.mode === "edit" ? edit.cat.default_assignee_id ?? "" : "",
  );
  const assigneesApi = useApi((signal) => listAssignees(signal), []);
  const assignees = assigneesApi.data?.assignees ?? [];
  const createMut = useMutation(createTicketCategory);
  const updateMut = useMutation(updateTicketCategory);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;
  const title =
    edit.mode === "edit"
      ? `Edit "${edit.cat.name}"`
      : edit.mode === "new-sub"
        ? `Add under "${edit.parentName}"`
        : "Add category";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const default_assignee_id = defaultAssignee || null;
    let res: unknown;
    if (edit.mode === "edit") res = await updateMut.run(edit.cat.id, { name: name.trim(), default_assignee_id });
    else if (edit.mode === "new-sub")
      res = await createMut.run({ name: name.trim(), parent_id: edit.parentId, default_assignee_id });
    else res = await createMut.run({ name: name.trim(), default_assignee_id });
    if (res) onSaved();
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="category-form" disabled={pending || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : edit.mode === "edit" ? "Save changes" : "Add"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="category-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={edit.mode === "new-sub" ? "Name" : "Category name"} autoFocus required />
        </label>
        <label className="field">
          <span className="label">Default assignee</span>
          <select value={defaultAssignee} onChange={(e) => setDefaultAssignee(e.target.value)} disabled={assigneesApi.loading}>
            <option value="">— None —</option>
            {assignees.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <span className="hint">Auto-fills the ticket assignee when this category is selected.</span>
        </label>
      </form>
    </Modal>
  );
}

// ===== Academic terms (higher-ed) ==========================================

/** unix-seconds ⇄ "YYYY-MM-DD" helpers for the term date inputs (UTC). */
function secToDate(sec: number | null): string {
  if (sec == null) return "";
  return new Date(sec * 1000).toISOString().slice(0, 10);
}
function dateToSec(value: string): number | null {
  if (!value) return null;
  const ms = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

function TermsTab() {
  const { data, loading, error, reload } = useApi((signal) => listTerms(signal), []);
  const terms = data?.terms ?? [];
  const [editing, setEditing] = useState<AcademicTermRow | "new" | null>(null);
  const { rows: sortedTerms, sort, toggle } = useTableSort(terms, TERMS_SORT);

  return (
    <>
      <SettingsListCard
        title="Academic terms"
        count={terms.length}
        addLabel="Add term"
        onAdd={() => setEditing("new")}
      >
        {loading ? (
          <Loading label="Loading terms…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : terms.length === 0 ? (
          <EmptyState>No academic terms defined.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <SortTh field="name" sort={sort} onSort={toggle}>Name</SortTh>
                <SortTh field="code" sort={sort} onSort={toggle}>Code</SortTh>
                <SortTh field="starts" sort={sort} onSort={toggle}>Starts</SortTh>
                <SortTh field="ends" sort={sort} onSort={toggle}>Ends</SortTh>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sortedTerms.map((t) => (
                <TermRow key={t.id} term={t} onEdit={() => setEditing(t)} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <TermModal
          term={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Add/Edit academic term modal — null term = add. */
function TermModal({
  term,
  onClose,
  onSaved,
}: {
  term: AcademicTermRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(term?.name ?? "");
  const [code, setCode] = useState(term?.code ?? "");
  const [startsOn, setStartsOn] = useState(secToDate(term?.starts_on ?? null));
  const [endsOn, setEndsOn] = useState(secToDate(term?.ends_on ?? null));
  const createMut = useMutation(createTerm);
  const updateMut = useMutation(updateTerm);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    const fields = {
      code: code.trim(),
      name: name.trim(),
      starts_on: dateToSec(startsOn),
      ends_on: dateToSec(endsOn),
    };
    const res = term ? await updateMut.run(term.id, fields) : await createMut.run(fields);
    if (res) onSaved();
  }

  return (
    <Modal
      title={term ? "Edit term" : "Add term"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="term-form" disabled={pending || !code.trim() || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : term ? "Save changes" : "Add term"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="term-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Fall 2026" required />
        </label>
        <label className="field">
          <span className="label">Code</span>
          <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="fall-2026" required />
        </label>
        <div className="row">
          <label className="field">
            <span className="label">Starts</span>
            <input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Ends</span>
            <input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
          </label>
        </div>
      </form>
    </Modal>
  );
}

/** One term row: read-only with Current(★)/Edit/Delete. */
function TermRow({ term, onEdit, onChanged }: { term: AcademicTermRow; onEdit: () => void; onChanged: () => void }) {
  const deleteMut = useMutation(deleteTerm);
  const currentMut = useMutation(setCurrentTerm);
  const busy = deleteMut.pending || currentMut.pending;

  return (
    <tr>
      <td>
        <strong>{term.name}</strong>
        {term.is_current ? <span className="pill" style={{ marginLeft: 6 }}>current</span> : null}
      </td>
      <td className="mono">{term.code}</td>
      <td>{secToDate(term.starts_on) || "—"}</td>
      <td>{secToDate(term.ends_on) || "—"}</td>
      <td className="row-actions">
        {!term.is_current ? (
          <button className="btn small ghost" onClick={() => currentMut.run(term.id).then(onChanged)} disabled={busy} title="Make current">
            ★
          </button>
        ) : null}
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() => confirmThen(`Delete term "${term.name}"?`, () => deleteMut.run(term.id).then(onChanged))}
          disabled={busy}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ===== Departments (higher-ed) =============================================

function DepartmentsTab() {
  const { data, loading, error, reload } = useApi((signal) => listDepartments(signal), []);
  const teamsApi = useApi((signal) => listTeams(signal), []);
  const departments = data?.departments ?? [];
  const teams = teamsApi.data?.teams ?? [];
  const [editing, setEditing] = useState<DepartmentRow | "new" | null>(null);
  const [importing, setImporting] = useState(false);
  const { rows: sortedDepartments, sort, toggle } = useTableSort(departments, DEPARTMENTS_SORT);

  return (
    <>
      <SettingsListCard
        title="Departments"
        count={departments.length}
        addLabel="Add department"
        onAdd={() => setEditing("new")}
        headerExtra={
          <button className="btn small" onClick={() => setImporting(true)}>
            Import (CSV)
          </button>
        }
      >
        {loading ? (
          <Loading label="Loading departments…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : departments.length === 0 ? (
          <EmptyState>No departments defined.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <SortTh field="name" sort={sort} onSort={toggle}>Name</SortTh>
                <SortTh field="code" sort={sort} onSort={toggle}>Code</SortTh>
                <th>Routes to team</th>
                <th>Change Mgmt</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sortedDepartments.map((d) => (
                <DeptRow
                  key={d.id}
                  department={d}
                  teams={teams}
                  onEdit={() => setEditing(d)}
                  onChanged={reload}
                />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <DepartmentModal
          department={editing === "new" ? null : editing}
          teams={teams}
          teamsLoading={teamsApi.loading}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
      {importing ? (
        <DepartmentImportModal
          existing={departments}
          onClose={() => setImporting(false)}
          onDone={() => {
            setImporting(false);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Bulk-add departments from pasted CSV — rows are `Name,Code` (Code optional:
 *  slugified from the name when blank). A header is ignored; rows whose code
 *  already exists are skipped. Loops the single-create endpoint client-side. */
function DepartmentImportModal({
  existing,
  onClose,
  onDone,
}: {
  existing: DepartmentRow[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const newRows = useMemo(() => {
    const haveCodes = new Set(existing.map((d) => d.code.trim().toLowerCase()));
    const seen = new Set<string>();
    const out: { name: string; code: string }[] = [];
    const lines = text.split(/\r?\n/);
    for (let i = 0; i < lines.length; i++) {
      const cells = splitCsvLine(lines[i] ?? "");
      const name = (cells[0] ?? "").trim();
      let code = (cells[1] ?? "").trim();
      if (!name) continue;
      // Skip a header row (first non-empty line that looks like column names).
      if (i === 0 && /name/i.test(name) && (!code || /code/i.test(code))) continue;
      if (!code) code = slugCode(name);
      code = slugCode(code);
      if (!code) continue;
      if (haveCodes.has(code) || seen.has(code)) continue;
      seen.add(code);
      out.push({ name, code });
    }
    return out;
  }, [text, existing]);

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      for (const r of newRows) await createDepartment({ code: r.code, name: r.name });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed. Some rows may not have been added.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Import departments (CSV)"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn primary" onClick={apply} disabled={busy || newRows.length === 0}>
            {busy ? <Spinner label="Importing…" /> : `Import ${newRows.length} new`}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <p className="hint" style={{ marginTop: 0 }}>
        Paste one department per line as <strong>Name,Code</strong>. Code is optional — it's
        generated from the name when left blank. A header row is ignored, and rows whose code
        already exists are skipped. Team routing can be set afterward on each department.
      </p>
      <label className="field">
        <span className="label">CSV</span>
        <textarea
          rows={10}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"Name,Code\nAdmissions,admissions\nFinancial Aid,finaid\nRegistrar,\nIT Help Desk,"}
          style={{ width: "100%", fontFamily: "var(--mono, monospace)", fontSize: 13 }}
        />
      </label>
      {text.trim() ? (
        <p className="hint" style={{ marginBottom: 0 }}>
          Adding <strong>{newRows.length}</strong> new department(s).
          {newRows.length === 0 ? " Everything in this list already exists." : ""}
        </p>
      ) : null}
    </Modal>
  );
}

/** Edit a Change-Management department's default approver set — a people
 *  multi-select wired to GET/PUT /api/config/departments/:id/approvers. The set
 *  seeds the approvers of any change request filed under this department. */
/** People multi-select for a CM department's default approver set. Reads/writes
 *  GET/PUT /api/config/departments/:id/approvers immediately (each add/remove
 *  persists). Reused inline in the Edit-department modal and the standalone
 *  Approvers modal, so the department must already exist (have an id). */
function DepartmentApproversPicker({ departmentId }: { departmentId: string }) {
  const approversApi = useApi((s) => listDepartmentApprovers(departmentId, s), [departmentId]);
  const rosterApi = useApi((s) => listAssignees(s), []);
  const roster = rosterApi.data?.assignees ?? [];
  const current = approversApi.data?.approvers ?? [];
  const currentIds = current.map((a) => a.user_id);
  const addable = roster.filter((u) => !currentIds.includes(u.id));
  const setMut = useMutation((userIds: string[]) => setDepartmentApprovers(departmentId, userIds));

  async function add(userId: string) {
    const res = await setMut.run([...currentIds, userId]);
    if (res) approversApi.reload();
  }
  async function remove(userId: string) {
    const res = await setMut.run(currentIds.filter((id) => id !== userId));
    if (res) approversApi.reload();
  }

  if (approversApi.loading) return <Loading label="Loading approvers…" />;
  if (approversApi.error) return <ErrorState error={approversApi.error} onRetry={approversApi.reload} />;
  return (
    <>
      {setMut.error ? <Alert kind="error">{setMut.error}</Alert> : null}
      {current.length === 0 ? (
        <EmptyState>No approvers yet.</EmptyState>
      ) : (
        <ul className="person-list">
          {current.map((a) => (
            <li key={a.user_id} className="person-row">
              <span className="person-main">
                <span className="person-name">{a.name ?? a.user_id}</span>
              </span>
              <button
                type="button"
                className="person-remove"
                aria-label={`Remove approver ${a.name ?? a.user_id}`}
                onClick={() => remove(a.user_id)}
                disabled={setMut.pending}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <select
        value=""
        onChange={(e) => {
          if (e.target.value) void add(e.target.value);
        }}
        aria-label="Add approver"
        disabled={setMut.pending || addable.length === 0}
        style={{ marginTop: 8 }}
      >
        <option value="">Add an approver…</option>
        {addable.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name}
          </option>
        ))}
      </select>
    </>
  );
}


/** Parse a department's stored approval_rules JSON into a safe rule array. */
const CM_AUTO_FIELDS: DepartmentApprovalRule["field"][] = [
  "status",
  "risk",
  "change_type",
  "urgency",
  "business_impact",
];
function parseApprovalRules(raw: string | null | undefined): DepartmentApprovalRule[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((r) => r && typeof r.min_approvals === "number")
      .map((r) => ({
        min_approvals: Math.max(1, r.min_approvals),
        field: CM_AUTO_FIELDS.includes(r.field) ? (r.field as DepartmentApprovalRule["field"]) : "status",
        value: String(r.value ?? ""),
      }));
  } catch {
    return [];
  }
}

/** Add/Edit department modal — null department = add. */
function DepartmentModal({
  department,
  teams,
  teamsLoading,
  onClose,
  onSaved,
}: {
  department: DepartmentRow | null;
  teams: TeamRow[];
  teamsLoading: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(department?.name ?? "");
  const [code, setCode] = useState(department?.code ?? "");
  const [teamId, setTeamId] = useState(department?.team_id ?? "");
  const [changeManagement, setChangeManagement] = useState(!!department?.change_management);
  const [rules, setRules] = useState<DepartmentApprovalRule[]>(() => parseApprovalRules(department?.approval_rules));
  const changeCfgApi = useApi((s) => getChangeConfig(s), []);
  const cfg = changeCfgApi.data?.config;
  // The CM fields an approval rule can auto-update, each with its value set.
  const cmFields: { key: DepartmentApprovalRule["field"]; label: string; options: { code: string; label: string }[] }[] = [
    { key: "status", label: "Status", options: cfg?.statuses ?? [] },
    { key: "risk", label: "Risk", options: cfg?.risks ?? [] },
    { key: "change_type", label: "Type", options: cfg?.types ?? [] },
    { key: "urgency", label: "Urgency", options: cfg?.urgencies ?? [] },
    { key: "business_impact", label: "Business impact", options: cfg?.impacts ?? [] },
  ];
  const optionsFor = (field: DepartmentApprovalRule["field"]) => cmFields.find((f) => f.key === field)?.options ?? [];
  const createMut = useMutation(createDepartment);
  const updateMut = useMutation(updateDepartment);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    const cleanRules = rules.filter((r) => r.min_approvals > 0 && r.value);
    const fields = {
      code: code.trim(),
      name: name.trim(),
      team_id: teamId || null,
      change_management: changeManagement,
      approval_rules: changeManagement && cleanRules.length ? JSON.stringify(cleanRules) : null,
    };
    const res = department ? await updateMut.run(department.id, fields) : await createMut.run(fields);
    if (res) onSaved();
  }

  return (
    <Modal
      title={department ? "Edit department" : "Add department"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="department-form" disabled={pending || !code.trim() || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : department ? "Save changes" : "Add department"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="department-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Admissions" required />
        </label>
        <label className="field">
          <span className="label">Code</span>
          <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="admissions" required />
        </label>
        <label className="field">
          <span className="label">Routes to team</span>
          <select value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">— No routing —</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
            {teamId && !teams.some((t) => t.id === teamId) ? (
              <option value={teamId}>{teamId}</option>
            ) : null}
          </select>
          {teamsLoading ? <span className="hint">Loading teams…</span> : null}
        </label>
        <label className="toggle">
          <input
            type="checkbox"
            checked={changeManagement}
            onChange={(e) => setChangeManagement(e.target.checked)}
          />
          Change Management
        </label>
        <p className="hint" style={{ marginTop: -4 }}>
          When on, this department can carry default approvers and be selected on change requests.
        </p>
        {changeManagement ? (
          <>
            {department?.change_management ? (
              <div className="field">
                <span className="label">Approvers</span>
                <DepartmentApproversPicker departmentId={department.id} />
                <span className="hint">These approvers seed every change request filed under this department.</span>
              </div>
            ) : (
              <p className="hint">Save the department with Change Management on, then reopen it to choose approvers.</p>
            )}

            {/* 0110: automatic field-update actions on approval threshold. */}
            <div className="field" style={{ marginTop: 4 }}>
              <span className="label">Automatic actions on approval</span>
              <span className="hint" style={{ marginTop: 0, marginBottom: 6 }}>
                When enough approvers approve a change filed under this department, update a field automatically.
              </span>
              {rules.length === 0 ? (
                <p className="hint" style={{ margin: "0 0 6px" }}>No automatic actions.</p>
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                  {rules.map((r, i) => (
                    <li key={i} className="row" style={{ gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                      <span className="faint">When</span>
                      <input
                        type="number"
                        min={1}
                        value={r.min_approvals}
                        aria-label="Number of approvers"
                        style={{ width: 64, flex: "0 0 auto" }}
                        onChange={(e) =>
                          setRules((rs) =>
                            rs.map((x, j) => (j === i ? { ...x, min_approvals: Math.max(1, parseInt(e.target.value, 10) || 1) } : x)),
                          )
                        }
                      />
                      <span className="faint">approve → set</span>
                      <select
                        value={r.field}
                        aria-label="Field to update"
                        style={{ flex: "0 0 auto", minWidth: 120 }}
                        onChange={(e) =>
                          setRules((rs) =>
                            rs.map((x, j) =>
                              j === i ? { ...x, field: e.target.value as DepartmentApprovalRule["field"], value: "" } : x,
                            ),
                          )
                        }
                      >
                        {cmFields.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                      <span className="faint">to</span>
                      <select
                        value={r.value}
                        aria-label="Target value"
                        style={{ flex: 1, minWidth: 130 }}
                        onChange={(e) => setRules((rs) => rs.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                      >
                        <option value="">Select…</option>
                        {optionsFor(r.field).map((o) => (
                          <option key={o.code} value={o.code}>
                            {o.label}
                          </option>
                        ))}
                        {r.value && !optionsFor(r.field).some((o) => o.code === r.value) ? (
                          <option value={r.value}>{r.value}</option>
                        ) : null}
                      </select>
                      <button
                        type="button"
                        className="btn small danger"
                        style={{ flex: "0 0 auto" }}
                        onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}
                        aria-label="Remove action"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                className="btn small"
                style={{ marginTop: 8, alignSelf: "flex-start" }}
                onClick={() => setRules((rs) => [...rs, { min_approvals: rs.length + 1, field: "status", value: "" }])}
              >
                + Add action
              </button>
              <span className="hint" style={{ marginTop: 6 }}>
                Update any change field (Status, Risk, Type, Urgency, Business impact). Highest matching
                threshold wins per field — e.g. 1 approver → Status In review, 2 → Status Approved. Saved with the department.
              </span>
            </div>
          </>
        ) : null}
      </form>
    </Modal>
  );
}

/** One department row: read-only with Edit/Delete (approvers live in Edit). */
function DeptRow({
  department,
  teams,
  onEdit,
  onChanged,
}: {
  department: DepartmentRow;
  teams: TeamRow[];
  onEdit: () => void;
  onChanged: () => void;
}) {
  const deleteMut = useMutation(deleteDepartment);
  const busy = deleteMut.pending;

  const teamName = department.team_id
    ? teams.find((t) => t.id === department.team_id)?.name ?? department.team_id
    : null;

  return (
    <tr>
      <td><strong>{department.name}</strong></td>
      <td className="mono">{department.code}</td>
      <td>{teamName ?? "—"}</td>
      <td>{department.change_management ? "On" : "—"}</td>
      <td className="row-actions">
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() =>
            confirmThen(`Delete department "${department.name}"?`, () => deleteMut.run(department.id).then(onChanged))
          }
          disabled={busy}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ===== Canned responses ====================================================

function CannedTab() {
  const { data, loading, error, reload } = useApi((signal) => listConfigCanned(signal), []);
  const canned = data?.canned ?? [];
  const [editing, setEditing] = useState<CannedResponseRow | "new" | null>(null);

  return (
    <>
      <SettingsListCard
        title="Canned responses"
        count={canned.length}
        addLabel="Add canned response"
        onAdd={() => setEditing("new")}
      >
        {loading ? (
          <Loading label="Loading canned responses…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : canned.length === 0 ? (
          <EmptyState>No canned responses yet.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Title</th>
                <th>Preview</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {canned.map((c) => (
                <CannedRow key={c.id} canned={c} onEdit={() => setEditing(c)} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <CannedModal
          canned={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Add/Edit canned-response modal — null canned = add. */
/** Plain-text preview of a (possibly rich-HTML) canned body for the list row. */
function cannedPreview(html: string): string {
  const text = html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
  return text.length > 80 ? `${text.slice(0, 80)}…` : text;
}

function CannedModal({
  canned,
  onClose,
  onSaved,
}: {
  canned: CannedResponseRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(canned?.title ?? "");
  const [body, setBody] = useState(canned?.body ?? "");
  const createMut = useMutation(createConfigCanned);
  const updateMut = useMutation(updateConfigCanned);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  const bodyEmpty = richTextIsEmpty(body);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim() || bodyEmpty) return;
    const fields = { title: title.trim(), body };
    const res = canned ? await updateMut.run(canned.id, fields) : await createMut.run(fields);
    if (res) onSaved();
  }

  return (
    <Modal
      title={canned ? "Edit canned response" : "Add canned response"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="canned-form" disabled={pending || !title.trim() || bodyEmpty}>
            {pending ? <Spinner label="Saving…" /> : canned ? "Save changes" : "Add response"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="canned-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Title</span>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Password reset steps" required />
        </label>
        <div className="field">
          <span className="label">Body</span>
          <RichTextEditor value={body} onChange={setBody} placeholder="Hi {{name}}, to reset your password…" />
        </div>
      </form>
    </Modal>
  );
}

/** One canned-response row: read-only with Edit/Delete. */
function CannedRow({ canned, onEdit, onChanged }: { canned: CannedResponseRow; onEdit: () => void; onChanged: () => void }) {
  const deleteMut = useMutation(deleteConfigCanned);
  const busy = deleteMut.pending;

  return (
    <tr>
      <td><strong>{canned.title}</strong></td>
      <td className="muted">{cannedPreview(canned.body)}</td>
      <td className="row-actions">
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() => confirmThen(`Delete canned response "${canned.title}"?`, () => deleteMut.run(canned.id).then(onChanged))}
          disabled={busy}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ===== Business hours ======================================================

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** minutes-from-midnight ⇄ "HH:MM" helpers for the time inputs. */
function minToTime(min: number | null): string {
  if (min == null) return "";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function timeToMin(value: string): number | null {
  if (!value) return null;
  const [h, m] = value.split(":").map((n) => Number(n));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function BusinessHoursTab() {
  const { data, loading, error, reload } = useApi((signal) => getBusinessHours(signal), []);
  const saveMut = useMutation(setBusinessHours);
  const [savedDay, setSavedDay] = useState<number | null>(null);
  // Which weekday is mid-save — so only that row's button shows the spinner.
  const [savingDay, setSavingDay] = useState<number | null>(null);

  // Local editable copy keyed by weekday (0..6), seeded from the load.
  const [rows, setRows] = useState<Record<number, BusinessHoursRow>>({});

  useEffect(() => {
    const byDay: Record<number, BusinessHoursRow> = {};
    for (let d = 0; d < 7; d++) byDay[d] = { weekday: d, open_min: null, close_min: null };
    // Bounds-check weekday (0–6) so a malformed row can't write an orphan key.
    for (const r of data?.hours ?? []) if (r.weekday >= 0 && r.weekday < 7) byDay[r.weekday] = r;
    setRows(byDay);
  }, [data]);

  function update(weekday: number, patch: Partial<BusinessHoursRow>) {
    // Editing a row invalidates a stale "Saved" badge on that row.
    setSavedDay((d) => (d === weekday ? null : d));
    setRows((prev) => ({ ...prev, [weekday]: { ...prev[weekday]!, weekday, ...patch } }));
  }

  async function save(weekday: number) {
    const row = rows[weekday]!;
    setSavingDay(weekday);
    const res = await saveMut.run({ weekday, open_min: row.open_min, close_min: row.close_min });
    setSavingDay(null);
    if (res) {
      setSavedDay(weekday);
      reload();
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Business hours</h3>
        <span className="faint">SLA timers count only open minutes</span>
      </div>
      <div className="card-body">
        {loading ? (
          <Loading label="Loading hours…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : (
          <>
            {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
            <table className="data">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Closed</th>
                  <th>Opens</th>
                  <th>Closes</th>
                  <th aria-label="Save" />
                </tr>
              </thead>
              <tbody>
                {WEEKDAYS.map((label, d) => {
                  const row = rows[d] ?? { weekday: d, open_min: null, close_min: null };
                  const closed = row.open_min == null && row.close_min == null;
                  return (
                    <tr key={d}>
                      <td>
                        <strong>{label}</strong>
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`${label} closed`}
                          checked={closed}
                          onChange={(e) =>
                            update(d, e.target.checked ? { open_min: null, close_min: null } : { open_min: 540, close_min: 1020 })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          aria-label={`${label} opens`}
                          value={minToTime(row.open_min)}
                          disabled={closed}
                          onChange={(e) => update(d, { open_min: timeToMin(e.target.value) })}
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          aria-label={`${label} closes`}
                          value={minToTime(row.close_min)}
                          disabled={closed}
                          onChange={(e) => update(d, { close_min: timeToMin(e.target.value) })}
                        />
                      </td>
                      <td>
                        <button className="btn small" onClick={() => save(d)} disabled={saveMut.pending}>
                          {savingDay === d ? "…" : savedDay === d ? "Saved" : "Save"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="hint">Times are in the tenant's wall clock (treated as UTC in this slice).</p>
          </>
        )}
      </div>
    </div>
  );
}

// ===== Branding ============================================================

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

// ===== General (workspace experience preferences) ==========================

/** Workspace name (app shell + browser tab) and the default landing page every
 *  member is sent to after login. Module visibility lives in Roles, not here —
 *  the tab hint points admins there. */
function GeneralTab() {
  const { data, loading, error, reload } = useApi((signal) => getWorkspaceConfig(signal), []);
  const saveMut = useMutation(setWorkspaceConfig);
  const [name, setName] = useState("");
  const [landing, setLanding] = useState("/tickets");
  const [maintenanceOn, setMaintenanceOn] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) {
      setName(data.config.name ?? "");
      setLanding(data.config.landing ?? "/tickets");
      setMaintenanceOn(data.config.maintenance_enabled === true);
      setMaintenanceMsg(data.config.maintenance_message ?? "");
    }
  }, [data]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const res = await saveMut.run({
      name: name.trim() || null,
      landing,
      maintenance_enabled: maintenanceOn,
      maintenance_message: maintenanceMsg.trim() || null,
    });
    if (res) {
      setSaved(true);
      reload();
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Workspace</h3>
      </div>
      <div className="card-body">
        {loading ? (
          <Loading label="Loading workspace…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : (
          <form onSubmit={onSubmit}>
            {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
            {saved && !saveMut.pending ? <Alert kind="success">Workspace preferences saved.</Alert> : null}

            <label className="field">
              <span className="label">Workspace name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setSaved(false); }}
                placeholder="e.g. Polaris University"
                maxLength={80}
              />
              <span className="hint">
                Shown in the sidebar, the top bar, and the browser tab. Leave blank to use the name
                derived from your address.
              </span>
            </label>

            <label className="field">
              <span className="label">Default landing page</span>
              <select value={landing} onChange={(e) => { setLanding(e.target.value); setSaved(false); }}>
                {WORKSPACE_LANDING_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <span className="hint">
                Where members land after signing in. A member who can't access this page still
                reaches their first available module.
              </span>
            </label>

            <p className="hint">
              Which modules each person sees is controlled per role under{" "}
              <strong>People &amp; access → Roles</strong>.
            </p>

            <div className="maint-block">
              <label className="maint-toggle">
                <input
                  type="checkbox"
                  checked={maintenanceOn}
                  onChange={(e) => {
                    setSaved(false);
                    if (e.target.checked) {
                      // Turning it ON locks every non-admin out org-wide — confirm.
                      void confirmDialog({
                        title: "Turn on maintenance mode?",
                        message:
                          "All non-admin members will be locked out of every module until you turn it back off. Remember to Save changes to apply.",
                        confirmLabel: "Turn on",
                        danger: true,
                      }).then((ok) => setMaintenanceOn(ok));
                    } else {
                      setMaintenanceOn(false);
                    }
                  }}
                />
                <span className="label">Maintenance mode</span>
              </label>
              <p className="hint" style={{ marginTop: 0 }}>
                When on, every module shows the message below instead of working as usual.
                Administrators keep full access so they can turn it back off.
              </p>
              <label className="field">
                <span className="label">Maintenance message</span>
                <textarea
                  rows={3}
                  value={maintenanceMsg}
                  onChange={(e) => { setMaintenanceMsg(e.target.value); setSaved(false); }}
                  placeholder="We're performing scheduled maintenance and will be back shortly. Thanks for your patience."
                  maxLength={600}
                />
                <span className="hint">Shown to members on a full-screen notice while maintenance mode is on.</span>
              </label>
            </div>

            <div className="btn-row" style={{ justifyContent: "flex-end" }}>
              <button className="btn primary" type="submit" disabled={saveMut.pending}>
                {saveMut.pending ? <Spinner label="Saving…" /> : "Save changes"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function BrandingTab() {
  const { data, loading, error, reload } = useApi((signal) => getBranding(signal), []);
  const saveMut = useMutation(apiSetBranding);
  const uploadMut = useMutation(uploadBrandingLogo);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const [color, setColor] = useState("#2563eb");
  const [secondary, setSecondary] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [faviconUrl, setFaviconUrl] = useState("");

  useEffect(() => {
    if (data) {
      setColor(data.branding.primary_color ?? "#2563eb");
      setSecondary(data.branding.secondary_color ?? "");
      setLogoUrl(data.branding.logo_url ?? "");
      setCompanyName(data.branding.company_name ?? "");
      setSupportEmail(data.branding.support_email ?? "");
      setFaviconUrl(data.branding.favicon_url ?? "");
    }
  }, [data]);

  const touch = () => setSaved(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const res = await saveMut.run({
      primary_color: color,
      secondary_color: secondary.trim() || null,
      logo_url: logoUrl.trim() || null,
      company_name: companyName.trim() || null,
      support_email: supportEmail.trim() || null,
      favicon_url: faviconUrl.trim() || null,
    });
    if (res) {
      setSaved(true);
      applyBranding(res.branding); // reflect new colours app-wide immediately
      reload();
    }
  }

  async function onPickLogo(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    const res = await uploadMut.run(file);
    if (res) {
      setLogoUrl(res.branding.logo_url ?? "");
      setSaved(false);
      reload();
    }
  }

  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-head">
          <h3>Branding</h3>
        </div>
        <div className="card-body">
          {loading ? (
            <Loading label="Loading branding…" />
          ) : error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : (
            <form onSubmit={onSubmit}>
              {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
              {uploadMut.error ? <Alert kind="error">{uploadMut.error}</Alert> : null}
              {saved && !saveMut.pending ? <Alert kind="success">Branding saved.</Alert> : null}

              <div className="row">
                <label className="field">
                  <span className="label">Primary / accent color</span>
                  <div className="color-row">
                    <input
                      type="color"
                      className="color-swatch"
                      value={HEX_RE.test(color) ? color : "#2563eb"}
                      onChange={(e) => { setColor(e.target.value); touch(); }}
                      aria-label="Primary color picker"
                    />
                    <input type="text" value={color} onChange={(e) => { setColor(e.target.value); touch(); }} placeholder="#2563eb" />
                  </div>
                </label>
                <label className="field">
                  <span className="label">Secondary color</span>
                  <div className="color-row">
                    <input
                      type="color"
                      className="color-swatch"
                      value={HEX_RE.test(secondary) ? secondary : "#64748b"}
                      onChange={(e) => { setSecondary(e.target.value); touch(); }}
                      aria-label="Secondary color picker"
                    />
                    <input type="text" value={secondary} onChange={(e) => { setSecondary(e.target.value); touch(); }} placeholder="optional" />
                  </div>
                </label>
              </div>

              <label className="field">
                <span className="label">Logo</span>
                <div className="row" style={{ gap: 8, alignItems: "center" }}>
                  <button type="button" className="btn small" onClick={() => fileRef.current?.click()} disabled={uploadMut.pending}>
                    {uploadMut.pending ? <Spinner label="Uploading…" /> : "Upload / replace…"}
                  </button>
                  {logoUrl ? (
                    <button type="button" className="btn small danger" onClick={() => { setLogoUrl(""); touch(); }}>
                      Remove
                    </button>
                  ) : null}
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" onChange={onPickLogo} style={{ display: "none" }} />
                </div>
                <span className="hint">PNG, JPG, SVG, GIF or WebP — up to 2 MB. Or paste a URL below.</span>
              </label>
              <label className="field">
                <span className="label">Logo URL</span>
                <input type="text" value={logoUrl} onChange={(e) => { setLogoUrl(e.target.value); touch(); }} placeholder="https://cdn.example.com/logo.png" />
              </label>

              <div className="row">
                <label className="field">
                  <span className="label">Company name</span>
                  <input type="text" value={companyName} onChange={(e) => { setCompanyName(e.target.value); touch(); }} placeholder="Shown on the portal + emails" />
                </label>
                <label className="field">
                  <span className="label">Support email</span>
                  <input type="email" value={supportEmail} onChange={(e) => { setSupportEmail(e.target.value); touch(); }} placeholder="help@school.edu" />
                </label>
              </div>
              <label className="field">
                <span className="label">Favicon URL</span>
                <input type="text" value={faviconUrl} onChange={(e) => { setFaviconUrl(e.target.value); touch(); }} placeholder="https://cdn.example.com/favicon.ico" />
              </label>

              <button className="btn primary" type="submit" disabled={saveMut.pending}>
                {saveMut.pending ? <Spinner label="Saving…" /> : "Save branding"}
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Preview</h3>
        </div>
        <div className="card-body">
          <div
            className="brand-preview"
            style={{
              ["--preview-accent" as string]: HEX_RE.test(color) ? color : "#2563eb",
              ["--preview-accent-2" as string]: HEX_RE.test(secondary) ? secondary : "#64748b",
            }}
          >
            {logoUrl ? (
              <img src={logoUrl} alt="Logo preview" className="brand-preview-logo" />
            ) : (
              <div className="brand-preview-logo placeholder">Logo</div>
            )}
            <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>{companyName || "Your Company"}</div>
            <button type="button" className="brand-preview-btn">Primary button</button>
            <a href="#preview" className="brand-preview-link" onClick={(e) => e.preventDefault()}>Accent link</a>
            {supportEmail ? <div className="hint" style={{ marginTop: 4 }}>Support: {supportEmail}</div> : null}
          </div>
          <p className="hint">Live preview — applied across your customer portal.</p>
        </div>
      </div>
    </div>
  );
}

// ===== SLA policies ========================================================

function SlaTab() {
  const { data, loading, error, reload } = useApi((signal) => listSlaPolicies(signal), []);
  const prioApi = useApi((signal) => listPriorities(signal), []);
  const policies = data?.policies ?? [];
  const priorities: TicketPriorityRow[] = prioApi.data?.priorities ?? [];
  const [editing, setEditing] = useState<SlaPolicyRow | "new" | null>(null);

  const prioName = (id: string | null) => priorities.find((p) => p.id === id)?.name ?? "(default)";

  // Full-width list; add/edit happen in a dialog (not a side-by-side form).
  return (
    <div className="card">
      <div className="card-head">
        <h3>SLA policies</h3>
        <div className="btn-row">
          <span className="faint">{policies.length}</span>
          <button className="btn primary small" onClick={() => setEditing("new")}>
            + Add policy
          </button>
        </div>
      </div>
      <div className="card-body tight">
        {loading ? (
          <Loading label="Loading policies…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : policies.length === 0 ? (
          <EmptyState>No SLA policies defined.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Priority</th>
                <th>First resp.</th>
                <th>Resolution</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <SlaRow key={p.id} policy={p} prioName={prioName} onEdit={() => setEditing(p)} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing ? (
        <SlaEditModal
          policy={editing === "new" ? null : editing}
          priorities={priorities}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

/** One SLA-policy row: read-only with Edit (opens the dialog) / Delete. */
function SlaRow({
  policy,
  prioName,
  onEdit,
  onChanged,
}: {
  policy: SlaPolicyRow;
  prioName: (id: string | null) => string;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const deleteMut = useMutation(deleteSlaPolicy);
  return (
    <tr>
      <td><strong>{policy.name}</strong></td>
      <td>{prioName(policy.priority_id)}</td>
      <td className="num">{policy.first_response_mins}m</td>
      <td className="num">{policy.resolution_mins}m</td>
      <td className="row-actions">
        <button className="btn small" onClick={onEdit} disabled={deleteMut.pending}>Edit</button>
        <button
          className="btn small danger"
          onClick={() => confirmThen(`Delete SLA policy "${policy.name}"?`, () => deleteMut.run(policy.id).then(onChanged))}
          disabled={deleteMut.pending}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

/** Add/edit an SLA policy in a dialog. policy=null → create. */
function SlaEditModal({
  policy,
  priorities,
  onClose,
  onSaved,
}: {
  policy: SlaPolicyRow | null;
  priorities: TicketPriorityRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = policy === null;
  const [name, setName] = useState(policy?.name ?? "");
  const [priorityId, setPriorityId] = useState(policy?.priority_id ?? "");
  const [firstMins, setFirstMins] = useState(String(policy?.first_response_mins ?? 60));
  const [resolutionMins, setResolutionMins] = useState(String(policy?.resolution_mins ?? 480));
  const createMut = useMutation(createSlaPolicy);
  const updateMut = useMutation(updateSlaPolicy);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error ?? updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const fr = Number(firstMins);
    const rm = Number(resolutionMins);
    if (!name.trim() || !Number.isFinite(fr) || !Number.isFinite(rm)) return;
    const res = isNew
      ? await createMut.run({
          name: name.trim(),
          first_response_mins: fr,
          resolution_mins: rm,
          ...(priorityId ? { priority_id: priorityId } : {}),
        })
      : await updateMut.run(policy.id, {
          name: name.trim(),
          first_response_mins: fr,
          resolution_mins: rm,
          priority_id: priorityId || null,
        });
    if (res) onSaved();
  }

  return (
    <Modal
      title={isNew ? "Add SLA policy" : `Edit ${policy.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button className="btn primary" type="submit" form="sla-policy-form" disabled={pending || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : isNew ? "Add policy" : "Save changes"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="sla-policy-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Standard support" autoFocus required />
        </label>
        <label className="field">
          <span className="label">Priority</span>
          <select value={priorityId} onChange={(e) => setPriorityId(e.target.value)}>
            <option value="">Default (any priority)</option>
            {priorities.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <div className="row">
          <label className="field">
            <span className="label">First response (min)</span>
            <input type="number" min={0} value={firstMins} onChange={(e) => setFirstMins(e.target.value)} required />
          </label>
          <label className="field">
            <span className="label">Resolution (min)</span>
            <input type="number" min={0} value={resolutionMins} onChange={(e) => setResolutionMins(e.target.value)} required />
          </label>
        </div>
      </form>
    </Modal>
  );
}

// ===== Users (manual management, alongside Entra sync) =====================

type UserRole = string;
/** Fallback when /api/roles hasn't loaded — the seeded system roles. */
const USER_ROLES: UserRole[] = ["admin", "tech", "agent", "client", "viewer"];

type TenantUserRow = import("../lib/types").TenantUser;

function UsersTab() {
  const { data, loading, error, reload } = useApi((signal) => listTenantUsers(signal), []);
  const users = data?.users ?? [];
  // All tenant-defined roles (system + custom) drive the role pickers.
  const rolesApi = useApi((signal) => listRoles(signal), []);
  const roleCodes = rolesApi.data?.roles.map((r) => r.code) ?? USER_ROLES;
  const [editing, setEditing] = useState<TenantUserRow | "new" | null>(null);
  // Filters: free-text (name/email), role, and status.
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const needle = q.trim().toLowerCase();
  const filtered = users.filter((u) => {
    if (needle && !`${u.name ?? ""} ${u.email ?? ""}`.toLowerCase().includes(needle)) return false;
    if (roleFilter && !(u.roles ?? "").split(",").includes(roleFilter)) return false;
    if (statusFilter && u.status !== statusFilter) return false;
    return true;
  });
  const filtersActive = !!(needle || roleFilter || statusFilter);
  const { rows: sortedUsers, sort, toggle } = useTableSort(filtered, USERS_SORT);

  return (
    <>
      <SettingsListCard
        title="Users"
        count={filtersActive ? `${filtered.length} of ${users.length}` : users.length}
        addLabel="Add user"
        onAdd={() => setEditing("new")}
        intro={
          <>
            <p className="sub" style={{ margin: "10px 14px 8px" }}>
              Manual accounts work alongside Entra sync — synced users appear here too and can be
              disabled or re-roled manually.
            </p>
            <div className="btn-row" style={{ gap: 8, alignItems: "center", margin: "0 14px 12px", flexWrap: "wrap" }}>
              <input
                type="text"
                className="input-inline"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name or email…"
                aria-label="Search users"
                style={{ flex: 1, minWidth: 180 }}
              />
              <select
                className="input-inline"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                aria-label="Filter by role"
              >
                <option value="">All roles</option>
                {roleCodes.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <select
                className="input-inline"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="">Any status</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
              {filtersActive ? (
                <button className="btn small" onClick={() => { setQ(""); setRoleFilter(""); setStatusFilter(""); }}>
                  Clear
                </button>
              ) : null}
            </div>
          </>
        }
      >
        {loading ? (
          <Loading label="Loading users…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : users.length === 0 ? (
          <EmptyState>No users yet.</EmptyState>
        ) : filtered.length === 0 ? (
          <EmptyState>No users match the current filters.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <SortTh field="user" sort={sort} onSort={toggle}>User</SortTh>
                <SortTh field="role" sort={sort} onSort={toggle}>Role</SortTh>
                <SortTh field="access" sort={sort} onSort={toggle}>Access</SortTh>
                <SortTh field="status" sort={sort} onSort={toggle}>Status</SortTh>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => (
                <UserRow key={u.id} user={u} onEdit={() => setEditing(u)} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <UserModal
          user={editing === "new" ? null : editing}
          roleCodes={roleCodes}
          allUsers={users}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Add/Edit user modal — null user = add.
 * Create posts name/email/role/password in one call. Edit can change the
 * display name (PATCH), the role (separate role endpoint), and the status; the
 * email and the password (Reset password) are not changed here.
 */
function UserModal({
  user,
  roleCodes,
  allUsers,
  onClose,
  onSaved,
}: {
  user: TenantUserRow | null;
  roleCodes: string[];
  allUsers: TenantUserRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const initialRole = user ? (user.roles ?? "").split(",")[0] || "" : "agent";
  const initialManager = user?.manager_id ?? "";
  const initialDepartment = user?.department_id ?? "";
  // Access override: "inherit" = follow roles, "allow"/"deny" = per-user override.
  const initialAccess: "inherit" | "allow" | "deny" =
    user?.access_override === 1 ? "allow" : user?.access_override === 0 ? "deny" : "inherit";
  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url ?? "");
  const initialAvatar = user?.avatar_url ?? "";
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>(initialRole);
  const [manager, setManager] = useState(initialManager);
  const [department, setDepartment] = useState(initialDepartment);
  const departmentsApi = useApi((s) => listDepartments(s), []);
  const departments = departmentsApi.data?.departments ?? [];
  const [access, setAccess] = useState<"inherit" | "allow" | "deny">(initialAccess);
  const [status, setStatus] = useState<"active" | "disabled">(user?.status === "disabled" ? "disabled" : "active");
  const accessValue = (a: "inherit" | "allow" | "deny"): 0 | 1 | null => (a === "allow" ? 1 : a === "deny" ? 0 : null);

  const createMut = useMutation(createUser);
  const updateMut = useMutation(updateUser);
  const roleMut = useMutation(setUserRole);
  const pending = createMut.pending || updateMut.pending || roleMut.pending;
  const err = createMut.error || updateMut.error || roleMut.error;

  const roleOptions = roleCodes.includes(role) || !role ? roleCodes : [...roleCodes, role];
  // Anyone but the user themselves can be their manager (server also guards
  // self + direct cycles). Sorted by name for a usable picker.
  const managerOptions = allUsers
    .filter((u) => u.id !== user?.id)
    .slice()
    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    if (user) {
      // Patch name + status + manager + access, then the role (separate endpoint) if changed.
      const patched = await updateMut.run(user.id, {
        name: name.trim(),
        status,
        ...(manager !== initialManager ? { manager_id: manager || null } : {}),
        ...(department !== initialDepartment ? { department_id: department || null } : {}),
        ...(access !== initialAccess ? { access_override: accessValue(access) } : {}),
        ...(avatarUrl.trim() !== initialAvatar ? { avatar_url: avatarUrl.trim() || null } : {}),
      });
      if (!patched) return;
      if (role !== initialRole) {
        const roled = await roleMut.run(user.id, role);
        if (!roled) return;
      }
      onSaved();
    } else {
      const res = await createMut.run({
        name: name.trim(),
        email: email.trim(),
        role,
        ...(password ? { password } : {}),
        ...(department ? { department_id: department } : {}),
      });
      if (!res) return;
      // Manager + access + photo aren't part of create — set them in a follow-up patch.
      const followUp: { manager_id?: string; access_override?: 0 | 1 | null; avatar_url?: string } = {};
      if (manager) followUp.manager_id = manager;
      if (access !== "inherit") followUp.access_override = accessValue(access);
      if (avatarUrl.trim()) followUp.avatar_url = avatarUrl.trim();
      if (Object.keys(followUp).length) await updateMut.run(res.user.id, followUp);
      onSaved();
    }
  }

  return (
    <Modal
      title={user ? "Edit user" : "Add user"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="user-form" disabled={pending || !name.trim() || !email.trim()}>
            {pending ? <Spinner label="Saving…" /> : user ? "Save changes" : "Add user"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="user-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dana Rivera" required />
        </label>
        <label className="field">
          <span className="label">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="dana@school.edu"
            required
            readOnly={!!user}
            disabled={!!user}
          />
          {user ? <span className="hint">Email can't be changed here.</span> : null}
        </label>
        <label className="field">
          <span className="label">Profile photo</span>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            {avatarUrl.trim() && /^https?:\/\//i.test(avatarUrl.trim()) ? (
              <img
                className="avatar avatar-img"
                style={{ width: 40, height: 40, flex: "0 0 auto" }}
                src={avatarUrl.trim()}
                alt=""
              />
            ) : (
              <div className="avatar" style={{ width: 40, height: 40, flex: "0 0 auto" }} aria-hidden="true">
                {(name.trim()[0] ?? "?").toUpperCase()}
              </div>
            )}
            <input
              type="url"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              placeholder="https://…/photo.jpg"
              style={{ flex: 1 }}
            />
          </div>
          <span className="hint">Image URL (https). Leave blank to use initials.</span>
        </label>
        <div className="row">
          <label className="field">
            <span className="label">Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
              {roleOptions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>
          {user ? (
            <label className="field">
              <span className="label">Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value as "active" | "disabled")}>
                <option value="active">active</option>
                <option value="disabled">disabled</option>
              </select>
            </label>
          ) : (
            <label className="field">
              <span className="label">Password (optional)</span>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="min 8 chars — blank = invited"
                minLength={8}
              />
            </label>
          )}
        </div>
        <label className="field">
          <span className="label">Manager</span>
          <select value={manager} onChange={(e) => setManager(e.target.value)}>
            <option value="">— none —</option>
            {managerOptions.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
              </option>
            ))}
          </select>
          <span className="hint">The manager sees this user's tickets in their My Work “team” view.</span>
        </label>
        <label className="field">
          <span className="label">Department</span>
          <select value={department} onChange={(e) => setDepartment(e.target.value)}>
            <option value="">— None —</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
            {department && !departments.some((d) => d.id === department) ? (
              <option value={department}>{department}</option>
            ) : null}
          </select>
          <span className="hint">Scopes Change-Management visibility to matching departments.</span>
        </label>
        <label className="field">
          <span className="label">Site access</span>
          <select value={access} onChange={(e) => setAccess(e.target.value as "inherit" | "allow" | "deny")}>
            <option value="inherit">Inherit from role</option>
            <option value="allow">Always allow</option>
            <option value="deny">No access</option>
          </select>
          <span className="hint">
            Controls whether this person can sign in to the tenant app. “Inherit” follows their
            role's access setting; “No access” locks them out regardless of role.
          </span>
        </label>
        {user ? <TechGroupsEditor userId={user.id} /> : null}
        {!user ? (
          <p className="hint">
            Without a password the account is created as <em>invited</em> — set a password later
            with “Reset password”, or let the user arrive via SSO. Tech-group membership can be set
            after the account exists.
          </p>
        ) : null}
      </form>
    </Modal>
  );
}

/** Per-user tech-group (team) membership + manager role. Each change applies
 *  immediately via the team-member endpoints (independent of the modal's Save). */
function TechGroupsEditor({ userId }: { userId: string }) {
  const teamsApi = useApi((signal) => listTeams(signal), []);
  const myTeamsApi = useApi((signal) => listUserTeams(userId, signal), [userId]);
  const teams = teamsApi.data?.teams ?? [];
  const roleOf = (teamId: string): "none" | "member" | "manager" =>
    myTeamsApi.data?.teams.find((t) => t.team_id === teamId)?.role ?? "none";
  const setMut = useMutation(setTeamMember);
  const delMut = useMutation(removeTeamMember);
  const busy = setMut.pending || delMut.pending;

  async function change(teamId: string, value: "none" | "member" | "manager") {
    const res = value === "none" ? await delMut.run(teamId, userId) : await setMut.run(teamId, userId, value);
    if (res) myTeamsApi.reload();
  }

  return (
    <div className="field">
      <span className="label">Tech groups</span>
      {teamsApi.loading ? (
        <span className="hint">Loading teams…</span>
      ) : teams.length === 0 ? (
        <span className="hint">No tech groups defined yet — create teams under Departments.</span>
      ) : (
        <ul className="tech-groups">
          {teams.map((t) => (
            <li key={t.id} className="tech-group-row">
              <span className="grow">{t.name}</span>
              <select
                value={roleOf(t.id)}
                disabled={busy}
                onChange={(e) => change(t.id, e.target.value as "none" | "member" | "manager")}
                aria-label={`${t.name} membership`}
              >
                <option value="none">Not a member</option>
                <option value="member">Member</option>
                <option value="manager">Manager</option>
              </select>
            </li>
          ))}
        </ul>
      )}
      {setMut.error || delMut.error ? <Alert kind="error">{setMut.error || delMut.error}</Alert> : null}
      <span className="hint">Managers can manage every ticket assigned to that tech group.</span>
    </div>
  );
}

/** One user row: Edit (modal), reset password, activate/disable. */
function UserRow({
  user,
  onEdit,
  onChanged,
}: {
  user: TenantUserRow;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const statusMut = useMutation(updateUser);
  const pwMut = useMutation(resetUserPassword);
  const busy = statusMut.pending || pwMut.pending;
  const err = statusMut.error || pwMut.error;
  const primaryRole = (user.roles ?? "").split(",")[0] || "";
  const disabled = user.status === "disabled";

  async function toggleStatus() {
    const next = disabled ? "active" : "disabled";
    if (!disabled && !(await confirmDialog({ message: `Disable ${user.name}? They will no longer be able to sign in.`, danger: true, confirmLabel: "Disable" }))) return;
    if (await statusMut.run(user.id, { status: next })) onChanged();
  }
  async function resetPassword() {
    const pw = window.prompt(`New password for ${user.name} (min 8 characters):`);
    if (!pw) return;
    if (pw.length < 8) {
      window.alert("Password must be at least 8 characters.");
      return;
    }
    if (await pwMut.run(user.id, pw)) onChanged();
  }

  return (
    <tr style={disabled ? { opacity: 0.55 } : undefined}>
      <td>
        <strong>{user.name}</strong>
        <div className="mono faint" style={{ fontSize: "0.75rem" }}>{user.email}</div>
        {user.manager_name ? (
          <div className="faint" style={{ fontSize: "0.72rem" }}>Reports to {user.manager_name}</div>
        ) : null}
        {err ? <div style={{ color: "var(--danger)", fontSize: "0.75rem" }}>{err}</div> : null}
      </td>
      <td>
        <span className="pill">{primaryRole || "(none)"}</span>
      </td>
      <td>
        {user.can_access ? (
          <span className="pill ok" title={user.access_override === 1 ? "Allowed (per-user override)" : "Allowed via role"}>
            Has access
          </span>
        ) : (
          <span className="pill danger" title={user.access_override === 0 ? "Denied (per-user override)" : "No access-granting role"}>
            No access
          </span>
        )}
      </td>
      <td>
        <span className={`pill ${user.status === "active" ? "ok" : disabled ? "danger" : ""}`}>{user.status}</span>
      </td>
      <td className="row-actions">
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button className="btn small" onClick={resetPassword} disabled={busy} title="Set a new password">
          Reset password
        </button>
        <button className={`btn small${disabled ? "" : " danger"}`} onClick={toggleStatus} disabled={busy}>
          {disabled ? "Activate" : "Disable"}
        </button>
      </td>
    </tr>
  );
}

// ===== Identity & SSO (Azure Entra ID directory) ===========================

/** Human label for a connection's last sync status pill. */
function syncPill(status: string | null) {
  if (status === "ok") return <span className="pill ok">Healthy</span>;
  if (status === "error") return <span className="pill danger">Error</span>;
  if (!status) return <span className="faint">Never synced</span>;
  return <span className="pill">{status}</span>;
}

function ConnectionRuns({ connectionId }: { connectionId: string }) {
  const { data, loading, error } = useApi(
    (signal) => listDirectoryRuns(connectionId, signal),
    [connectionId],
  );
  const runs: DirectorySyncRun[] = data?.runs ?? [];
  if (loading) return <Loading label="Loading sync history…" />;
  if (error) return <Alert kind="error">{error}</Alert>;
  if (runs.length === 0) return <p className="hint">No sync runs yet.</p>;
  return (
    <table className="data">
      <thead>
        <tr>
          <th>Started</th>
          <th>Status</th>
          <th className="num">Seen</th>
          <th className="num">Created</th>
          <th className="num">Updated</th>
          <th className="num">Deactivated</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((r) => (
          <tr key={r.id}>
            <td>{formatDate(r.started_at)}</td>
            <td>{r.status === "ok" ? <span className="pill ok">ok</span> : <span className="pill danger" title={r.error ?? ""}>{r.status}</span>}</td>
            <td className="num">{r.users_seen}</td>
            <td className="num">{r.users_created}</td>
            <td className="num">{r.users_updated}</td>
            <td className="num">{r.users_deactivated}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ConnectionCard({
  conn,
  onSynced,
}: {
  conn: DirectoryConnection;
  onSynced: () => void;
}) {
  const syncMut = useMutation(syncDirectoryConnection);
  const [open, setOpen] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  async function sync() {
    setLastResult(null);
    const res = await syncMut.run(conn.id);
    if (res) {
      setLastResult(
        res.status === "ok"
          ? `Synced — ${res.users_seen ?? 0} seen, ${res.users_created ?? 0} created, ${res.users_updated ?? 0} updated, ${res.users_deactivated ?? 0} deactivated.`
          : `Sync ${res.status}${res.error ? `: ${res.error}` : ""}.`,
      );
      onSynced();
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>{conn.display_name || "Azure Entra ID"}</h3>
        {conn.enabled ? syncPill(conn.last_sync_status) : <span className="pill">Disabled</span>}
      </div>
      <div className="card-body">
        <dl className="kv">
          <div><dt>Provider</dt><dd>{conn.provider === "entra" ? "Azure Entra ID" : conn.provider}</dd></div>
          <div><dt>Directory (tenant) ID</dt><dd className="mono">{conn.directory_id}</dd></div>
          <div><dt>Client ID</dt><dd className="mono">{conn.client_id}</dd></div>
          {conn.group_id ? <div><dt>Group filter</dt><dd className="mono">{conn.group_id}</dd></div> : null}
          <div><dt>JIT provisioning</dt><dd>{conn.jit_provisioning ? "On" : "Off"}</dd></div>
          <div><dt>Deactivate removed</dt><dd>{conn.deactivate_removed ? "On" : "Off"}</dd></div>
          <div><dt>Last sync</dt><dd>{conn.last_sync_at ? formatDate(conn.last_sync_at) : "—"}</dd></div>
        </dl>

        {syncMut.error ? <Alert kind="error">{syncMut.error}</Alert> : null}
        {lastResult ? <Alert kind="success">{lastResult}</Alert> : null}

        <div className="btn-row" style={{ marginTop: 10 }}>
          <button className="btn primary" onClick={sync} disabled={syncMut.pending || !conn.enabled}>
            {syncMut.pending ? <Spinner label="Syncing…" /> : "Sync now"}
          </button>
          <button className="btn small" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide history" : "Sync history"}
          </button>
        </div>

        {open ? <div style={{ marginTop: 12 }}><ConnectionRuns connectionId={conn.id} /></div> : null}
      </div>
    </div>
  );
}

// ===== Facility groups (0084) — campus/team-scoped visibility ============
/** Manage facility groups (e.g. Bronx, NR) + their members. Members see only
 *  their group's work orders/equipment/parts; admins see everything. */
function FacilityGroupsTab() {
  const { data, loading, error, reload } = useApi((s) => listFacilityGroups(s), []);
  const meApi = useApi((s) => getMe(undefined, s), []);
  // Group definition + membership control who can see which campus's data, so it
  // is a tenant-admin-only control (the server enforces this too). Non-admins
  // get a read-only view of the group list.
  const isAdmin = meApi.data?.is_admin === true || meApi.data?.roles?.includes("admin") === true;
  const groups = data?.groups ?? [];
  const createMut = useMutation(createFacilityGroup);
  const deleteMut = useMutation(deleteFacilityGroup);
  const [name, setName] = useState("");
  const [managing, setManaging] = useState<FacilityGroupRow | null>(null);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const r = await createMut.run({ name: name.trim() });
    if (r) {
      setName("");
      reload();
    }
  }
  async function remove(g: FacilityGroupRow) {
    const ok = await confirmDialog({
      title: "Delete group",
      message: `Delete "${g.name}"? Its work orders, equipment, and parts become unscoped (visible to everyone with Facilities access).`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (ok && (await deleteMut.run(g.id))) reload();
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Facility groups</h3>
        <span className="faint">{groups.length}</span>
      </div>
      <div className="card-body">
        <p className="sub" style={{ marginTop: 0 }}>
          Groups scope who can see Facilities records (e.g. <strong>Bronx</strong> vs <strong>NR</strong>).
          A member sees only their group's work orders, equipment, and parts — plus unscoped records.
          Admins overseeing multiple campuses see everything.
        </p>
        {createMut.error ? <Alert kind="error">{createMut.error}</Alert> : null}
        {deleteMut.error ? <Alert kind="error">{deleteMut.error}</Alert> : null}
        {isAdmin ? (
          <form onSubmit={create} className="btn-row" style={{ margin: "0 0 12px" }}>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="New group name (e.g. Bronx)" />
            <button className="btn primary" type="submit" disabled={createMut.pending || !name.trim()}>
              {createMut.pending ? <Spinner label="Adding…" /> : "Add group"}
            </button>
          </form>
        ) : (
          <p className="sub" style={{ margin: "0 0 12px" }}>Only a workspace admin can create groups or change membership.</p>
        )}
        {loading ? (
          <Loading label="Loading groups…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : groups.length === 0 ? (
          <EmptyState>No groups yet. Add one (e.g. a campus) to scope visibility.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr><th>Name</th><th>Members</th>{isAdmin ? <th aria-label="Actions" /> : null}</tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.id}>
                  <td>
                    <strong>{g.name}</strong>
                    {g.description ? <div className="faint">{g.description}</div> : null}
                  </td>
                  <td>{g.member_count ?? 0}</td>
                  {isAdmin ? (
                    <td>
                      <div className="btn-row">
                        <button className="btn small" onClick={() => setManaging(g)}>Members</button>
                        <button className="btn small danger" onClick={() => remove(g)}>Delete</button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {managing ? (
        <FacilityGroupMembersModal
          group={managing}
          onClose={() => {
            setManaging(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function FacilityGroupMembersModal({ group, onClose }: { group: FacilityGroupRow; onClose: () => void }) {
  const { data, reload } = useApi((s) => listFacilityGroupMembers(group.id, s), [group.id]);
  const rosterApi = useApi((s) => listAssignees(s), []);
  const members = data?.members ?? [];
  const roster = rosterApi.data?.assignees ?? [];
  const addMut = useMutation((args: { user_id: string; role: "member" | "lead" }) => addFacilityGroupMember(group.id, args));
  const removeMut = useMutation((userId: string) => removeFacilityGroupMember(group.id, userId));
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<"member" | "lead">("member");
  const memberIds = new Set(members.map((m) => m.user_id));
  const addable = roster.filter((u) => !memberIds.has(u.id));

  async function add() {
    if (!userId) return;
    const r = await addMut.run({ user_id: userId, role });
    if (r) {
      setUserId("");
      reload();
    }
  }
  async function remove(uid: string) {
    if (await removeMut.run(uid)) reload();
  }

  return (
    <Modal title={`Members · ${group.name}`} onClose={onClose}>
      {addMut.error ? <Alert kind="error">{addMut.error}</Alert> : null}
      <div className="btn-row" style={{ marginBottom: 12 }}>
        <select value={userId} onChange={(e) => setUserId(e.target.value)} disabled={addMut.pending}>
          <option value="">Add a person…</option>
          {addable.map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        <select value={role} onChange={(e) => setRole(e.target.value as "member" | "lead")} disabled={addMut.pending}>
          <option value="member">Member</option>
          <option value="lead">Lead</option>
        </select>
        <button className="btn primary" onClick={add} disabled={addMut.pending || !userId}>Add</button>
      </div>
      {members.length === 0 ? (
        <EmptyState>No members yet — add people so they can see this group's work.</EmptyState>
      ) : (
        <table className="data">
          <thead><tr><th>Name</th><th>Role</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.user_id}>
                <td>{m.name ?? m.user_id}<div className="faint">{m.email}</div></td>
                <td><span className="pill">{m.role}</span></td>
                <td><button className="btn small danger" disabled={removeMut.pending} onClick={() => remove(m.user_id)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

/** Vendors & suppliers (0085): one registry for the companies that service
 *  equipment AND supply parts. Contacts + contract dates + payment terms +
 *  notes. List / create / edit / delete. Mirrors FacilityGroupsTab. */
function FacilityVendorsTab() {
  const { data, loading, error, reload } = useApi((s) => listFacilityVendors(s), []);
  const allVendors = data?.vendors ?? [];
  const deleteMut = useMutation(deleteFacilityVendor);
  // null = closed, "new" = add, row = edit.
  const [editing, setEditing] = useState<FacilityVendorRow | "new" | null>(null);

  // Group is a client-side convenience filter ("" = all, "none" = unassigned).
  const [group, setGroup] = useState("");
  const groupsApi = useApi((s) => listFacilityGroups(s), []);
  const groups = groupsApi.data?.groups ?? [];
  const vendors =
    group === "none"
      ? allVendors.filter((v) => !v.group_id)
      : group
        ? allVendors.filter((v) => v.group_id === group)
        : allVendors;

  async function remove(v: FacilityVendorRow) {
    const ok = await confirmDialog({
      title: "Delete vendor",
      message: `Delete "${v.name}"? It is unlinked from any equipment and parts (those records keep their other details).`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (ok && (await deleteMut.run(v.id))) reload();
  }

  return (
    <>
      <SettingsListCard
        title="Vendors & suppliers"
        count={vendors.length}
        addLabel="Add vendor"
        onAdd={() => setEditing("new")}
        headerExtra={
          groups.length > 0 ? (
            <select
              className="input-inline"
              value={group}
              onChange={(e) => setGroup(e.target.value)}
              aria-label="Filter by group"
            >
              <option value="">All groups</option>
              <option value="none">Unassigned</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          ) : undefined
        }
      >
        {deleteMut.error ? <Alert kind="error">{deleteMut.error}</Alert> : null}
        {loading ? (
          <Loading label="Loading vendors…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : vendors.length === 0 ? (
          <EmptyState>No vendors yet. Add the companies that service your equipment or supply your parts.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Contract</th>
                {groups.length > 0 ? <th>Group</th> : null}
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id}>
                  <td>
                    <strong>{v.name}</strong>
                    {v.website ? <div className="faint">{v.website}</div> : null}
                  </td>
                  <td>
                    {v.contact_name || "—"}
                    {v.contact_email ? <div className="faint">{v.contact_email}</div> : null}
                    {v.contact_phone ? <div className="faint">{v.contact_phone}</div> : null}
                  </td>
                  <td className="muted">
                    {v.contract_start || v.contract_end
                      ? `${v.contract_start ? formatDate(v.contract_start) : "—"} → ${v.contract_end ? formatDate(v.contract_end) : "—"}`
                      : "—"}
                  </td>
                  {groups.length > 0 ? (
                    <td className="muted">
                      {v.group_id ? groups.find((g) => g.id === v.group_id)?.name ?? v.group_id : "—"}
                    </td>
                  ) : null}
                  <td>
                    <div className="btn-row">
                      <button className="btn small" onClick={() => setEditing(v)}>Edit</button>
                      <button className="btn small danger" disabled={deleteMut.pending} onClick={() => remove(v)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>
      {editing ? (
        <FacilityVendorModal
          vendor={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Create/edit a vendor. Contract dates are held as yyyy-mm-dd strings and
 *  converted to/from unix seconds on the boundary. */
function FacilityVendorModal({
  vendor,
  onClose,
  onSaved,
}: {
  vendor: FacilityVendorRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toDateInput = (secs: number | null) =>
    secs != null ? new Date(secs * 1000).toISOString().slice(0, 10) : "";
  const fromDateInput = (s: string): number | null =>
    s.trim() ? Math.floor(new Date(`${s}T00:00:00Z`).getTime() / 1000) : null;

  const [name, setName] = useState(vendor?.name ?? "");
  const [contactName, setContactName] = useState(vendor?.contact_name ?? "");
  const [contactEmail, setContactEmail] = useState(vendor?.contact_email ?? "");
  const [contactPhone, setContactPhone] = useState(vendor?.contact_phone ?? "");
  const [address, setAddress] = useState(vendor?.address ?? "");
  const [website, setWebsite] = useState(vendor?.website ?? "");
  const [paymentTerms, setPaymentTerms] = useState(vendor?.payment_terms ?? "");
  const [contractStart, setContractStart] = useState(toDateInput(vendor?.contract_start ?? null));
  const [contractEnd, setContractEnd] = useState(toDateInput(vendor?.contract_end ?? null));
  const [notes, setNotes] = useState(vendor?.notes ?? "");
  const [groupId, setGroupId] = useState(vendor?.group_id ?? "");

  const groupsApi = useApi((s) => listFacilityGroups(s), []);
  const groups = groupsApi.data?.groups ?? [];

  const createMut = useMutation(createFacilityVendor);
  const updateMut = useMutation((patch: Parameters<typeof createFacilityVendor>[0]) =>
    updateFacilityVendor(vendor!.id, patch),
  );
  const pending = createMut.pending || updateMut.pending;
  const error = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const body = {
      name: name.trim(),
      contact_name: contactName.trim() || null,
      contact_email: contactEmail.trim() || null,
      contact_phone: contactPhone.trim() || null,
      address: address.trim() || null,
      website: website.trim() || null,
      payment_terms: paymentTerms.trim() || null,
      contract_start: fromDateInput(contractStart),
      contract_end: fromDateInput(contractEnd),
      notes: notes.trim() || null,
      group_id: groupId || null,
    };
    const res = vendor ? await updateMut.run(body) : await createMut.run(body);
    if (res) onSaved();
  }

  return (
    <Modal
      title={vendor ? "Edit vendor" : "Add vendor"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="facility-vendor-form" disabled={pending || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : vendor ? "Save changes" : "Add vendor"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="facility-vendor-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Grainger" autoFocus required />
        </label>
        <div className="row">
          <label className="field">
            <span className="label">Contact name</span>
            <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Contact email</span>
            <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
          </label>
        </div>
        <div className="row">
          <label className="field">
            <span className="label">Contact phone</span>
            <input type="text" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Website</span>
            <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" />
          </label>
        </div>
        <label className="field">
          <span className="label">Address</span>
          <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} />
        </label>
        <label className="field">
          <span className="label">Payment terms</span>
          <input type="text" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} placeholder="e.g. Net 30" />
        </label>
        {groups.length > 0 || vendor?.group_id ? (
          <label className="field">
            <span className="label">Group <span className="faint">(controls who can see this vendor)</span></span>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={groupsApi.loading}>
              <option value="">Unassigned (everyone with Facilities access)</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
              {groupId && !groups.some((g) => g.id === groupId) ? (
                <option value={groupId}>{groupId}</option>
              ) : null}
            </select>
          </label>
        ) : null}
        <div className="row">
          <label className="field">
            <span className="label">Contract start</span>
            <input type="date" value={contractStart} onChange={(e) => setContractStart(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Contract end</span>
            <input type="date" value={contractEnd} onChange={(e) => setContractEnd(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span className="label">Notes</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Notes…" />
        </label>
      </form>
    </Modal>
  );
}

/** Self-serve management of the OIDC "Sign in with…" connections (the login
 *  buttons). Admin-only on the server (user.admin). */
function SsoSignInCard() {
  const { data, loading, error, reload } = useApi((signal) => listSsoConnectionsAdmin(signal), []);
  const createMut = useMutation(createSsoConnection);
  const toggleMut = useMutation(updateSsoConnection);
  const deleteMut = useMutation(deleteSsoConnection);
  const conns = data?.connections ?? [];

  const [adding, setAdding] = useState(false);
  const [provider, setProvider] = useState<"microsoft" | "oidc">("microsoft");
  const [displayName, setDisplayName] = useState("Microsoft");
  const [directoryId, setDirectoryId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [emailDomain, setEmailDomain] = useState("");
  // Generic OIDC endpoints.
  const [authEndpoint, setAuthEndpoint] = useState("");
  const [tokenEndpoint, setTokenEndpoint] = useState("");
  const [jwksUri, setJwksUri] = useState("");
  const [issuer, setIssuer] = useState("");

  function resetForm() {
    setDirectoryId(""); setClientId(""); setClientSecret(""); setEmailDomain("");
    setAuthEndpoint(""); setTokenEndpoint(""); setJwksUri(""); setIssuer("");
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!clientId.trim()) return;
    const res = await createMut.run({
      provider,
      display_name: displayName.trim() || (provider === "microsoft" ? "Microsoft" : "SSO"),
      client_id: clientId.trim(),
      ...(clientSecret.trim() ? { client_secret: clientSecret.trim() } : {}),
      ...(emailDomain.trim() ? { email_domain: emailDomain.trim() } : {}),
      ...(provider === "microsoft"
        ? { directory_id: directoryId.trim() }
        : {
            ...(issuer.trim() ? { issuer: issuer.trim() } : {}),
            authorization_endpoint: authEndpoint.trim(),
            token_endpoint: tokenEndpoint.trim(),
            ...(jwksUri.trim() ? { jwks_uri: jwksUri.trim() } : {}),
          }),
    });
    if (res) {
      resetForm();
      setAdding(false);
      reload();
    }
  }

  async function toggle(id: string, enabled: boolean) {
    const res = await toggleMut.run(id, { enabled });
    if (res) reload();
  }
  async function remove(id: string, label: string) {
    const ok = await confirmDialog({
      title: "Delete SSO connection",
      message: `Remove the "${label}" sign-in button? Users linked to it will need another way to sign in.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (ok && (await deleteMut.run(id))) reload();
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>SSO sign-in</h3>
        <button className="btn ghost small" onClick={() => setAdding((s) => !s)}>
          {adding ? "Cancel" : "+ Add sign-in"}
        </button>
      </div>
      <div className="card-body tight">
        <p className="sub" style={{ margin: "0 0 8px" }}>
          The "Sign in with…" buttons shown on your workspace login page. Enable one and your users
          can log in with their IdP (just-in-time provisioned on first sign-in).
        </p>
        {createMut.error ? <Alert kind="error">{createMut.error}</Alert> : null}
        {toggleMut.error ? <Alert kind="error">{toggleMut.error}</Alert> : null}
        {deleteMut.error ? <Alert kind="error">{deleteMut.error}</Alert> : null}

        {adding ? (
          <form onSubmit={onSubmit} style={{ marginBottom: 12 }}>
            <label className="field">
              <span className="label">Provider</span>
              <select value={provider} onChange={(e) => setProvider(e.target.value as "microsoft" | "oidc")}>
                <option value="microsoft">Microsoft Entra ID</option>
                <option value="oidc">Generic OIDC</option>
              </select>
            </label>
            <label className="field">
              <span className="label">Button label</span>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Microsoft" />
            </label>
            {provider === "microsoft" ? (
              <label className="field">
                <span className="label">Directory (tenant) ID</span>
                <input type="text" value={directoryId} onChange={(e) => setDirectoryId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" required />
              </label>
            ) : (
              <>
                <label className="field">
                  <span className="label">Authorization endpoint</span>
                  <input type="url" value={authEndpoint} onChange={(e) => setAuthEndpoint(e.target.value)} placeholder="https://idp/authorize" required />
                </label>
                <label className="field">
                  <span className="label">Token endpoint</span>
                  <input type="url" value={tokenEndpoint} onChange={(e) => setTokenEndpoint(e.target.value)} placeholder="https://idp/token" required />
                </label>
                <label className="field">
                  <span className="label">JWKS URI <span className="faint">(recommended — verifies tokens)</span></span>
                  <input type="url" value={jwksUri} onChange={(e) => setJwksUri(e.target.value)} placeholder="https://idp/.well-known/jwks.json" />
                </label>
                <label className="field">
                  <span className="label">Issuer <span className="faint">(optional)</span></span>
                  <input type="text" value={issuer} onChange={(e) => setIssuer(e.target.value)} placeholder="https://idp/" />
                </label>
              </>
            )}
            <label className="field">
              <span className="label">Application (client) ID</span>
              <input type="text" value={clientId} onChange={(e) => setClientId(e.target.value)} required />
            </label>
            <label className="field">
              <span className="label">Client secret <span className="faint">(if your app is confidential)</span></span>
              <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} autoComplete="off" />
            </label>
            <label className="field">
              <span className="label">Email domain <span className="faint">(optional)</span></span>
              <input type="text" value={emailDomain} onChange={(e) => setEmailDomain(e.target.value)} placeholder="your-school.edu" />
            </label>
            <button className="btn primary" type="submit" disabled={createMut.pending || !clientId.trim()}>
              {createMut.pending ? <Spinner label="Adding…" /> : "Add sign-in connection"}
            </button>
          </form>
        ) : null}

        {loading ? (
          <Loading label="Loading SSO…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : conns.length === 0 ? (
          <EmptyState>No SSO sign-in configured yet. Add one above to show a login button.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr><th>Provider</th><th>Button label</th><th>Domain</th><th>Enabled</th><th aria-label="Actions" /></tr>
            </thead>
            <tbody>
              {conns.map((s) => (
                <tr key={s.id}>
                  <td><span className="pill">{s.provider}</span></td>
                  <td><strong>{s.display_name}</strong></td>
                  <td className="mono faint">{s.email_domain ?? "—"}</td>
                  <td>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={s.enabled === 1}
                        disabled={toggleMut.pending}
                        onChange={(e) => toggle(s.id, e.target.checked)}
                      />
                    </label>
                  </td>
                  <td>
                    <button className="btn small danger" disabled={deleteMut.pending} onClick={() => remove(s.id, s.display_name)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function IdentityTab() {
  const { data, loading, error, reload } = useApi((signal) => listDirectoryConnections(signal), []);
  const createMut = useMutation(createEntraConnection);
  const connections = data?.connections ?? [];

  const [displayName, setDisplayName] = useState("Azure Entra ID");
  const [directoryId, setDirectoryId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [groupId, setGroupId] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!directoryId.trim() || !clientId.trim() || !clientSecret.trim()) return;
    const res = await createMut.run({
      displayName: displayName.trim() || "Azure Entra ID",
      directoryId: directoryId.trim(),
      clientId: clientId.trim(),
      clientSecret: clientSecret.trim(),
      ...(groupId.trim() ? { groupId: groupId.trim() } : {}),
    });
    if (res) {
      setDirectoryId("");
      setClientId("");
      setClientSecret("");
      setGroupId("");
      reload();
    }
  }

  return (
    <div className="grid-2 grid-2-top">
      <div className="card">
        <div className="card-head">
          <h3>Connect Azure Entra ID</h3>
        </div>
        <div className="card-body">
          {createMut.error ? <Alert kind="error">{createMut.error}</Alert> : null}
          <form onSubmit={onSubmit}>
            <label className="field">
              <span className="label">Display name</span>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Azure Entra ID" />
            </label>
            <label className="field">
              <span className="label">Directory (tenant) ID</span>
              <input type="text" value={directoryId} onChange={(e) => setDirectoryId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" required />
            </label>
            <label className="field">
              <span className="label">Application (client) ID</span>
              <input type="text" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="11111111-1111-1111-1111-111111111111" required />
            </label>
            <label className="field">
              <span className="label">Client secret</span>
              <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} autoComplete="off" placeholder="Entra app client secret value" required />
            </label>
            <label className="field">
              <span className="label">Group ID <span className="faint">(optional — limit to one group)</span></span>
              <input type="text" value={groupId} onChange={(e) => setGroupId(e.target.value)} placeholder="(all users if blank)" />
            </label>
            <button className="btn primary" type="submit" disabled={createMut.pending || !directoryId.trim() || !clientId.trim() || !clientSecret.trim()}>
              {createMut.pending ? <Spinner label="Connecting…" /> : "Connect directory"}
            </button>
          </form>
          <p className="hint" style={{ marginTop: 12 }}>
            In Entra: register an app, grant <strong>Directory.Read.All</strong> +{" "}
            <strong>User.Read.All</strong> (application) permissions with admin consent, then create
            a client secret. Paste the Directory ID, Client ID, and the secret <em>value</em> here.
          </p>
        </div>
      </div>

      <div className="stack">
        <div className="card">
          <div className="card-head">
            <h3>Directory connections</h3>
            <span className="faint">{connections.length}</span>
          </div>
          <div className="card-body tight">
            <p className="sub" style={{ margin: "0 0 8px" }}>
              Connect your Azure Entra ID (Microsoft) directory to provision agents automatically.
              Users are pulled on an hourly schedule and just-in-time when they sign in with SSO.
              Admin-only; the client secret is encrypted at rest and never shown again.
            </p>
            {loading ? (
              <Loading label="Loading connections…" />
            ) : error ? (
              <ErrorState error={error} onRetry={reload} />
            ) : connections.length === 0 ? (
              <EmptyState>No directory connected yet — add Azure Entra ID on the left.</EmptyState>
            ) : null}
          </div>
        </div>

        {connections.map((conn) => (
          <ConnectionCard key={conn.id} conn={conn} onSynced={reload} />
        ))}

        <SsoSignInCard />
      </div>
    </div>
  );
}

// ===== Audit log ===========================================================

const AUDIT_SORT: Record<string, SortAccessor<import("../lib/types").AuditRow>> = {
  when: (r) => r.created_at,
  action: (r) => r.action,
  entity: (r) => r.entity_type,
  actor: (r) => r.actor_id ?? "system",
  // Sort by how many fields changed (the same count shown in the Change column).
  change: (r) =>
    Object.keys(
      parseJson<Record<string, unknown>>(r.after_json) ?? parseJson<Record<string, unknown>>(r.before_json) ?? {},
    ).length,
};

function AuditTab() {
  const [entityType, setEntityType] = useState("");
  const [entityId, setEntityId] = useState("");
  // Applied filters (committed on submit) drive the server load.
  const [applied, setApplied] = useState<{ entity_type?: string; entity_id?: string }>({});

  const { data, loading, error, reload } = useApi(
    (signal) => listAudit(applied, signal),
    [applied.entity_type, applied.entity_id],
  );
  const allRows = data?.audit ?? [];

  // Client-side quick filter (action/entity/actor/id); every column is sortable
  // via the shared useTableSort/SortTh primitive, defaulting to newest-first.
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const filtered = allRows.filter((r) =>
    !needle
      ? true
      : r.action.toLowerCase().includes(needle) ||
        r.entity_type.toLowerCase().includes(needle) ||
        (r.actor_id ?? "system").toLowerCase().includes(needle) ||
        r.entity_id.toLowerCase().includes(needle),
  );
  const { rows, sort, toggle } = useTableSort(filtered, AUDIT_SORT, { field: "when", dir: "desc" });

  // Client-side paging — 25 rows per page. Reset to the first page whenever the
  // filter/search changes the result set.
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
  }, [needle, applied.entity_type, applied.entity_id]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageRows = rows.slice(pageStart, pageStart + PAGE_SIZE);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setApplied({
      ...(entityType.trim() ? { entity_type: entityType.trim() } : {}),
      ...(entityId.trim() ? { entity_id: entityId.trim() } : {}),
    });
  }

  return (
    <>
      <form className="toolbar" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Entity type</span>
          <input type="text" value={entityType} onChange={(e) => setEntityType(e.target.value)} placeholder="ticket" />
        </label>
        <label className="field">
          <span className="label">Entity ID</span>
          <input type="text" value={entityId} onChange={(e) => setEntityId(e.target.value)} placeholder="(optional)" />
        </label>
        <button className="btn" type="submit">
          Filter
        </button>
        <label className="field" style={{ marginLeft: "auto", minWidth: 220 }}>
          <span className="label">Search</span>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="action, entity, actor…"
          />
        </label>
      </form>

      <div className="card">
        <div className="card-head">
          <h3>Audit log</h3>
          <span className="faint">
            {rows.length === 0
              ? "0 entries"
              : `${pageStart + 1}–${pageStart + pageRows.length} of ${rows.length}`}
            {needle && rows.length !== allRows.length ? ` (filtered from ${allRows.length})` : ""}
          </span>
        </div>
        <div className="card-body tight">
          {loading ? (
            <Loading label="Loading audit log…" />
          ) : error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : rows.length === 0 ? (
            <EmptyState>No audit entries match.</EmptyState>
          ) : (
            <table className="data audit-table">
              <thead>
                <tr>
                  <SortTh field="when" sort={sort} onSort={toggle} className="audit-when-col">When</SortTh>
                  <SortTh field="action" sort={sort} onSort={toggle}>Action</SortTh>
                  <SortTh field="entity" sort={sort} onSort={toggle}>Entity</SortTh>
                  <SortTh field="actor" sort={sort} onSort={toggle}>Actor</SortTh>
                  <SortTh field="change" sort={sort} onSort={toggle}>Change</SortTh>
                  <th aria-label="Expand" style={{ width: "1%" }} />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <AuditRowItem key={r.id} row={r} />
                ))}
              </tbody>
            </table>
          )}
        </div>
        {rows.length > PAGE_SIZE ? (
          <div className="audit-pager">
            <button
              type="button"
              className="btn small"
              disabled={safePage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ‹ Prev
            </button>
            <span className="faint">
              Page {safePage + 1} of {pageCount}
            </span>
            <button
              type="button"
              className="btn small"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              Next ›
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}

/** One audit row, expandable to reveal the full before/after diff. */
function AuditRowItem({ row }: { row: import("../lib/types").AuditRow }) {
  const [open, setOpen] = useState(false);
  const before = parseJson<Record<string, unknown>>(row.before_json);
  const after = parseJson<Record<string, unknown>>(row.after_json);
  const changedKeys = Object.keys(after ?? before ?? {});
  const summary =
    changedKeys.length === 0
      ? "—"
      : changedKeys.length <= 2
        ? changedKeys.join(", ")
        : `${changedKeys.length} fields`;
  const hasDetail = !!(before || after);

  return (
    <>
      <tr
        className={`audit-row${hasDetail ? " expandable" : ""}${open ? " open" : ""}`}
        onClick={hasDetail ? () => setOpen((o) => !o) : undefined}
      >
        <td style={{ whiteSpace: "nowrap" }}>{formatDate(row.created_at)}</td>
        <td className="mono">{row.action}</td>
        <td>
          <span className="pill">{row.entity_type}</span>{" "}
          <span className="mono faint">{row.entity_id.slice(0, 8)}</span>
        </td>
        <td className="mono faint">{row.actor_id ? row.actor_id.slice(0, 8) : "system"}</td>
        <td className="muted audit-change">{summary}</td>
        <td className="faint" style={{ textAlign: "center" }}>
          {hasDetail ? (open ? "▲" : "▼") : ""}
        </td>
      </tr>
      {open && hasDetail ? (
        <tr className="audit-detail-row">
          <td colSpan={6}>
            <div className="audit-detail">
              {before ? (
                <div>
                  <div className="audit-detail-label">Before</div>
                  <pre className="raw-json">{JSON.stringify(before, null, 2)}</pre>
                </div>
              ) : null}
              {after ? (
                <div>
                  <div className="audit-detail-label">After</div>
                  <pre className="raw-json">{JSON.stringify(after, null, 2)}</pre>
                </div>
              ) : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

// ===== Locations (campus buildings/sites; legacy helpdesk parity) ==========

/** Manage the tech groups (teams) themselves — create/rename/delete.
 *  Membership is assigned per-user on the Users tab. */
function TechGroupsTab() {
  const { data, loading, error, reload } = useApi((signal) => listTeams(signal), []);
  const teams = data?.teams ?? [];
  const [editing, setEditing] = useState<TeamRow | "new" | null>(null);
  const { rows: sortedTeams, sort, toggle } = useTableSort(teams, TEAMS_SORT);

  return (
    <>
      <SettingsListCard
        title="Tech groups"
        count={teams.length}
        addLabel="Add tech group"
        onAdd={() => setEditing("new")}
      >
        {loading ? (
          <Loading label="Loading tech groups…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : teams.length === 0 ? (
          <EmptyState>No tech groups yet. Add one, then assign members on each user.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <SortTh field="name" sort={sort} onSort={toggle}>Name</SortTh>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sortedTeams.map((t) => (
                <TechGroupTr key={t.id} team={t} onEdit={() => setEditing(t)} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <TechGroupModal
          team={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Add/Edit tech group modal — null team = add. */
function TechGroupModal({
  team,
  onClose,
  onSaved,
}: {
  team: TeamRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(team?.name ?? "");
  const createMut = useMutation((n: string) => createTeam(n));
  const updateMut = useMutation((id: string, n: string) => updateTeam(id, n));
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const res = team ? await updateMut.run(team.id, name.trim()) : await createMut.run(name.trim());
    if (res) onSaved();
  }

  return (
    <Modal
      title={team ? "Edit tech group" : "Add tech group"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="tech-group-form" disabled={pending || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : team ? "Save changes" : "Add tech group"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="tech-group-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tier 1 Support" required />
        </label>
      </form>
    </Modal>
  );
}

/** One tech-group row: read-only with Edit/Delete. */
function TechGroupTr({ team, onEdit, onChanged }: { team: TeamRow; onEdit: () => void; onChanged: () => void }) {
  const deleteMut = useMutation(deleteTeam);
  const busy = deleteMut.pending;

  return (
    <tr>
      <td><strong>{team.name}</strong></td>
      <td className="row-actions">
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() => confirmThen(`Delete tech group "${team.name}"? Members will be unassigned.`, () => deleteMut.run(team.id).then(onChanged))}
          disabled={busy}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

/** Manage the asset categories (types) used by the inventory module. */
function AssetTypesTab() {
  const { data, loading, error, reload } = useApi((signal) => listAssetTypes(signal), []);
  const types = data?.asset_types ?? [];
  const [editing, setEditing] = useState<AssetTypeRow | "new" | null>(null);
  const [importing, setImporting] = useState(false);
  const { rows: sortedTypes, sort, toggle } = useTableSort(types, ASSET_TYPES_SORT);

  return (
    <>
      <SettingsListCard
        title="Asset types"
        count={types.length}
        addLabel="Add asset type"
        onAdd={() => setEditing("new")}
        headerExtra={
          <button className="btn small" onClick={() => setImporting(true)}>
            Import (CSV)
          </button>
        }
      >
        {loading ? (
          <Loading label="Loading asset types…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : types.length === 0 ? (
          <EmptyState>No asset types yet.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <SortTh field="name" sort={sort} onSort={toggle}>Name</SortTh>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sortedTypes.map((t) => (
                <AssetTypeTr key={t.id} type={t} onEdit={() => setEditing(t)} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <AssetTypeModal
          type={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
      {importing ? (
        <AssetTypeImportModal
          existing={types}
          onClose={() => setImporting(false)}
          onDone={() => {
            setImporting(false);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Bulk-add asset types from pasted CSV — one name per line (a "Name" header and
 *  blank/duplicate lines are ignored; names already present are skipped). Loops
 *  the single-create endpoint client-side; no bulk API needed. */
function AssetTypeImportModal({
  existing,
  onClose,
  onDone,
}: {
  existing: AssetTypeRow[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const newNames = useMemo(() => {
    const have = new Set(existing.map((t) => t.name.trim().toLowerCase()));
    const seen = new Set<string>();
    const out: string[] = [];
    for (const line of text.split(/\r?\n/)) {
      const name = splitCsvLine(line)[0]?.trim() ?? "";
      if (!name) continue;
      const key = name.toLowerCase();
      if (key === "name" && out.length === 0 && !have.has(key)) continue; // header
      if (have.has(key) || seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
    return out;
  }, [text, existing]);

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      for (const name of newNames) await createAssetType(name);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed. Some rows may not have been added.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Import asset types (CSV)"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn primary" onClick={apply} disabled={busy || newNames.length === 0}>
            {busy ? <Spinner label="Importing…" /> : `Import ${newNames.length} new`}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <p className="hint" style={{ marginTop: 0 }}>
        Paste one asset-type <strong>name</strong> per line. A header row is ignored. Names that
        already exist are skipped.
      </p>
      <label className="field">
        <span className="label">Names</span>
        <textarea
          rows={10}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"Name\nLaptop\nMonitor\nDocking Station\nLicense"}
          style={{ width: "100%", fontFamily: "var(--mono, monospace)", fontSize: 13 }}
        />
      </label>
      {text.trim() ? (
        <p className="hint" style={{ marginBottom: 0 }}>
          Adding <strong>{newNames.length}</strong> new asset type(s).
          {newNames.length === 0 ? " Everything in this list already exists." : ""}
        </p>
      ) : null}
    </Modal>
  );
}

function AssetTypeModal({
  type,
  onClose,
  onSaved,
}: {
  type: AssetTypeRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(type?.name ?? "");
  const createMut = useMutation((n: string) => createAssetType(n));
  const updateMut = useMutation((id: string, n: string) => updateAssetType(id, n));
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const res = type ? await updateMut.run(type.id, name.trim()) : await createMut.run(name.trim());
    if (res) onSaved();
  }

  return (
    <Modal
      title={type ? "Edit asset type" : "Add asset type"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="asset-type-form" disabled={pending || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : type ? "Save changes" : "Add asset type"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="asset-type-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Laptop" required />
        </label>
      </form>
    </Modal>
  );
}

function AssetTypeTr({ type, onEdit, onChanged }: { type: AssetTypeRow; onEdit: () => void; onChanged: () => void }) {
  const deleteMut = useMutation(deleteAssetType);
  const busy = deleteMut.pending;

  return (
    <tr>
      <td><strong>{type.name}</strong></td>
      <td className="row-actions">
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() => confirmThen(`Delete asset type "${type.name}"? Assets keep their other details.`, () => deleteMut.run(type.id).then(onChanged))}
          disabled={busy}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ===== Asset categories ====================================================

/** A grouping dimension for inventory assets (Hardware, Software, …), distinct
 *  from the asset "Type" lookup. Flat, positional list managed in Settings. */
function AssetCategoriesTab() {
  const { data, loading, error, reload } = useApi((signal) => listAssetCategories(signal), []);
  const categories = data?.asset_categories ?? [];
  const [editing, setEditing] = useState<AssetCategoryRow | "new" | null>(null);
  const { rows: sortedCategories, sort, toggle } = useTableSort(categories, ASSET_CATEGORIES_SORT);

  return (
    <>
      <SettingsListCard
        title="Asset categories"
        count={categories.length}
        addLabel="Add asset category"
        onAdd={() => setEditing("new")}
      >
        {loading ? (
          <Loading label="Loading asset categories…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : categories.length === 0 ? (
          <EmptyState>No asset categories yet.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <SortTh field="name" sort={sort} onSort={toggle}>Name</SortTh>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sortedCategories.map((c) => (
                <AssetCategoryTr key={c.id} category={c} onEdit={() => setEditing(c)} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <AssetCategoryModal
          category={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function AssetCategoryModal({
  category,
  onClose,
  onSaved,
}: {
  category: AssetCategoryRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(category?.name ?? "");
  const createMut = useMutation((n: string) => createAssetCategory(n));
  const updateMut = useMutation((id: string, n: string) => updateAssetCategory(id, n));
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const res = category ? await updateMut.run(category.id, name.trim()) : await createMut.run(name.trim());
    if (res) onSaved();
  }

  return (
    <Modal
      title={category ? "Edit asset category" : "Add asset category"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="asset-category-form" disabled={pending || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : category ? "Save changes" : "Add asset category"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="asset-category-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Hardware" required />
        </label>
      </form>
    </Modal>
  );
}

function AssetCategoryTr({ category, onEdit, onChanged }: { category: AssetCategoryRow; onEdit: () => void; onChanged: () => void }) {
  const deleteMut = useMutation(deleteAssetCategory);
  const busy = deleteMut.pending;

  return (
    <tr>
      <td><strong>{category.name}</strong></td>
      <td className="row-actions">
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() => confirmThen(`Delete asset category "${category.name}"? Assets keep their other details.`, () => deleteMut.run(category.id).then(onChanged))}
          disabled={busy}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ===== IT Systems (Change Management multi-select lookup; 0101) =============

/** Configurable IT systems selectable on a change request. Positional, with an
 *  active flag (inactive = hidden from the picker but kept on existing changes).
 *  Reorder via up/down arrows (persisted as a position swap). */
function ItSystemsTab() {
  // include_inactive so admins manage the full set (the change picker reads only active).
  const { data, loading, error, reload } = useApi((s) => listItSystems({ includeInactive: true }, s), []);
  const systems = data?.it_systems ?? [];
  const [editing, setEditing] = useState<ItSystem | "new" | null>(null);
  const moveMut = useMutation((id: string, position: number) => updateItSystem(id, { position }));

  // Swap a row with its neighbor by exchanging their stored positions.
  async function move(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= systems.length) return;
    const a = systems[index]!;
    const b = systems[next]!;
    const ra = await moveMut.run(a.id, b.position);
    if (!ra) return;
    const rb = await moveMut.run(b.id, a.position);
    if (rb) reload();
  }

  return (
    <>
      <SettingsListCard
        title="IT systems"
        count={systems.length}
        addLabel="Add IT system"
        onAdd={() => setEditing("new")}
      >
        {loading ? (
          <Loading label="Loading IT systems…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : systems.length === 0 ? (
          <EmptyState>No IT systems yet.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Active</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {systems.map((s, i) => (
                <ItSystemTr
                  key={s.id}
                  system={s}
                  onEdit={() => setEditing(s)}
                  onChanged={reload}
                  onMoveUp={() => move(i, -1)}
                  onMoveDown={() => move(i, 1)}
                  isFirst={i === 0}
                  isLast={i === systems.length - 1}
                  reordering={moveMut.pending}
                />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <ItSystemModal
          system={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function ItSystemModal({
  system,
  onClose,
  onSaved,
}: {
  system: ItSystem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(system?.name ?? "");
  const createMut = useMutation((n: string) => createItSystem({ name: n }));
  const updateMut = useMutation((id: string, n: string) => updateItSystem(id, { name: n }));
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const res = system ? await updateMut.run(system.id, name.trim()) : await createMut.run(name.trim());
    if (res) onSaved();
  }

  return (
    <Modal
      title={system ? "Edit IT system" : "Add IT system"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="it-system-form" disabled={pending || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : system ? "Save changes" : "Add IT system"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="it-system-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Active Directory" required />
        </label>
      </form>
    </Modal>
  );
}

/** One IT-system row: name, an inline active toggle, reorder, Edit and Delete. */
function ItSystemTr({
  system,
  onEdit,
  onChanged,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  reordering,
}: {
  system: ItSystem;
  onEdit: () => void;
  onChanged: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  reordering: boolean;
}) {
  const activeMut = useMutation((active: boolean) => updateItSystem(system.id, { active }));
  const deleteMut = useMutation(deleteItSystem);
  const busy = activeMut.pending || deleteMut.pending || reordering;

  async function toggleActive(active: boolean) {
    const res = await activeMut.run(active);
    if (res) onChanged();
  }

  return (
    <tr>
      <td><strong>{system.name}</strong></td>
      <td>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={system.active === 1}
            onChange={(e) => toggleActive(e.target.checked)}
            disabled={busy}
            aria-label={`${system.name} active`}
          />
          <span className="muted">{system.active === 1 ? "Active" : "Inactive"}</span>
        </label>
      </td>
      <td className="row-actions">
        <button className="btn small" onClick={onMoveUp} disabled={busy || isFirst} aria-label="Move up" title="Move up">↑</button>
        <button className="btn small" onClick={onMoveDown} disabled={busy || isLast} aria-label="Move down" title="Move down">↓</button>
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() =>
            confirmThen(
              `Delete IT system "${system.name}"? It will be removed from every change request that lists it.`,
              () => deleteMut.run(system.id).then(onChanged),
            )
          }
          disabled={busy}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ===== Asset statuses ======================================================

/** Lifecycle statuses for inventory assets (In stock, Assigned, …). Like ticket
 *  statuses they are positional (user-defined order) and carry a color swatch.
 *  Built-in (is_system) rows can't be renamed or deleted, but their color and
 *  order are still editable. */
function AssetStatusesTab() {
  const { data, loading, error, reload } = useApi((signal) => listAssetStatuses(signal), []);
  const statuses = data?.asset_statuses ?? [];
  const [editing, setEditing] = useState<AssetStatusRow | "new" | null>(null);
  const reorderMut = useMutation(reorderAssetStatuses);

  // Swap a row with its neighbor and persist the full id order.
  async function move(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= statuses.length) return;
    const ids = statuses.map((s) => s.id);
    [ids[index], ids[next]] = [ids[next]!, ids[index]!];
    const res = await reorderMut.run(ids);
    if (res) reload();
  }

  return (
    <>
      <SettingsListCard
        title="Asset statuses"
        count={statuses.length}
        addLabel="Add status"
        onAdd={() => setEditing("new")}
      >
        {loading ? (
          <Loading label="Loading asset statuses…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : statuses.length === 0 ? (
          <EmptyState>No asset statuses yet.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Color</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {statuses.map((s, i) => (
                <AssetStatusRow
                  key={s.id}
                  status={s}
                  onEdit={() => setEditing(s)}
                  onChanged={reload}
                  onMoveUp={() => move(i, -1)}
                  onMoveDown={() => move(i, 1)}
                  isFirst={i === 0}
                  isLast={i === statuses.length - 1}
                  reordering={reorderMut.pending}
                />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <AssetStatusModal
          status={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Add/Edit asset-status modal — null status = add. Every status is renamable;
 *  the color is edited inline on the row, not here. */
function AssetStatusModal({
  status,
  onClose,
  onSaved,
}: {
  status: AssetStatusRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(status?.name ?? "");
  const createMut = useMutation((n: string) => createAssetStatus({ name: n }));
  const updateMut = useMutation((id: string, n: string) => updateAssetStatus(id, { name: n }));
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const res = status ? await updateMut.run(status.id, name.trim()) : await createMut.run(name.trim());
    if (res) onSaved();
  }

  return (
    <Modal
      title={status ? "Edit asset status" : "Add asset status"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="asset-status-form" disabled={pending || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : status ? "Save changes" : "Add status"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="asset-status-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Assigned" required />
        </label>
      </form>
    </Modal>
  );
}

/** One asset-status row: name, an inline color picker (commit on blur/change),
 *  reorder, Edit and Delete — available for every status. */
function AssetStatusRow({
  status,
  onEdit,
  onChanged,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  reordering,
}: {
  status: AssetStatusRow;
  onEdit: () => void;
  onChanged: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  reordering: boolean;
}) {
  const colorMut = useMutation((color: string) => updateAssetStatus(status.id, { color }));
  const deleteMut = useMutation(deleteAssetStatus);
  const busy = colorMut.pending || deleteMut.pending || reordering;
  // Local color so the swatch updates immediately; persist on change/blur.
  const [color, setColor] = useState(status.color ?? "#94a3b8");

  async function commitColor(value: string) {
    if (value === (status.color ?? "#94a3b8")) return;
    const res = await colorMut.run(value);
    if (res) onChanged();
  }

  return (
    <tr>
      <td>
        <strong>{status.name}</strong>
      </td>
      <td>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            onBlur={(e) => commitColor(e.target.value)}
            disabled={busy}
            aria-label={`Color for ${status.name}`}
            title="Pick a color"
            style={{ width: 28, height: 24, padding: 0, border: "none", background: "none", cursor: busy ? "default" : "pointer" }}
          />
          <span className="mono">{color}</span>
        </span>
      </td>
      <td className="row-actions">
        <button className="btn small" onClick={onMoveUp} disabled={busy || isFirst} aria-label="Move up" title="Move up">↑</button>
        <button className="btn small" onClick={onMoveDown} disabled={busy || isLast} aria-label="Move down" title="Move down">↓</button>
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() =>
            confirmThen(
              `Delete asset status "${status.name}"? Any assets currently in this status will be moved to another status.`,
              () => deleteMut.run(status.id).then(onChanged),
            )
          }
          disabled={busy}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

function LocationsTab() {
  // The Campus → Building → Room cascade is the single location system, shared by
  // tickets + work orders — managed here with the same nested add/edit/delete
  // sub-logic as Request types, plus bulk CSV import.
  const placesApi = useApi((signal) => getFacilityConfig(signal), []);
  const places = placesApi.data?.config;
  const [importing, setImporting] = useState(false);

  return (
    <>
      <div className="btn-row" style={{ justifyContent: "flex-end", marginBottom: 10 }}>
        <button className="btn small" onClick={() => setImporting(true)}>
          Import locations (CSV)
        </button>
      </div>
      {places ? (
        <FacilityLocationsTree
          campuses={places.campuses}
          buildings={places.buildings}
          rooms={places.rooms}
          onSaved={placesApi.reload}
        />
      ) : null}
      {importing ? (
        <LocationImportModal
          current={places}
          onClose={() => setImporting(false)}
          onDone={() => {
            setImporting(false);
            placesApi.reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Mirror of the server's slugifyCode (config-db.ts) so import-built codes match
 *  what setFacilityConfig stores — keeps parent references (campus_code/
 *  building_code) consistent on a round-trip. */
function slugCode(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
}

/** Deterministic 8-hex FNV-1a hash (no Date/random) used to disambiguate codes
 *  whose readable form would exceed the 40-char limit and collide on truncation. */
function hash8(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

/** Stable, globally-unique code for a place from its full path
 *  (e.g. ["Main Campus","Science Hall","Room 101"]). The readable
 *  `campus_building_room` form is used when it fits in 40 chars; otherwise a
 *  short hash of the full path is appended so different long paths never
 *  collide on truncation — and the same path always yields the same code, so
 *  re-importing is idempotent. The server slugifies this again (a no-op here). */
function placeCode(parts: string[]): string {
  const full = parts.map(slugCode).join("_");
  if (full.length <= 40) return full;
  return `${full.slice(0, 31)}_${hash8(full)}`;
}

/** Split one CSV line into cells, honoring double-quoted fields ("" → literal "). */
function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      cells.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  cells.push(cur);
  return cells;
}

interface ParsedLocations {
  campuses: FacilityOption[];
  buildings: FacilityBuildingOption[];
  rooms: FacilityRoomOption[];
  added: { campuses: number; buildings: number; rooms: number };
  rows: number;
  error: string | null;
}

/** Parse a Campus,Building,Room CSV into a full cascade, merged with the current
 *  config (union by code — existing entries are never dropped or relabeled).
 *  Building/Room are optional per row (campus-only and campus+building rows are
 *  allowed). Codes are derived to be globally unique within their level so two
 *  buildings can each own a "Room 101". */
function parseLocations(text: string, current: FacilityConfig | undefined): ParsedLocations {
  const campuses = new Map<string, FacilityOption>();
  const buildings = new Map<string, FacilityBuildingOption>();
  const rooms = new Map<string, FacilityRoomOption>();
  for (const c of current?.campuses ?? []) campuses.set(c.code, { code: c.code, label: c.label });
  for (const b of current?.buildings ?? [])
    buildings.set(b.code, { code: b.code, label: b.label, campus_code: b.campus_code });
  for (const r of current?.rooms ?? [])
    rooms.set(r.code, { code: r.code, label: r.label, building_code: r.building_code });

  const added = { campuses: 0, buildings: 0, rooms: 0 };
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) {
    return { campuses: [], buildings: [], rooms: [], added, rows: 0, error: null };
  }
  // Skip a header row if the first line looks like column names.
  let start = 0;
  if (/campus/i.test(lines[0] ?? "") && /building|room/i.test(lines[0] ?? "")) start = 1;

  let rows = 0;
  for (let i = start; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i] ?? "");
    const campusName = (cells[0] ?? "").trim();
    const buildingName = (cells[1] ?? "").trim();
    const roomName = (cells[2] ?? "").trim();
    if (!campusName) continue;
    rows++;
    const cCode = placeCode([campusName]);
    if (!cCode) continue;
    if (!campuses.has(cCode)) {
      campuses.set(cCode, { code: cCode, label: campusName });
      added.campuses++;
    }
    if (!buildingName) continue;
    const bCode = placeCode([campusName, buildingName]);
    if (!bCode) continue;
    if (!buildings.has(bCode)) {
      buildings.set(bCode, { code: bCode, label: buildingName, campus_code: cCode });
      added.buildings++;
    }
    if (!roomName) continue;
    const rCode = placeCode([campusName, buildingName, roomName]);
    if (!rCode) continue;
    if (!rooms.has(rCode)) {
      rooms.set(rCode, { code: rCode, label: roomName, building_code: bCode });
      added.rooms++;
    }
  }
  return {
    campuses: [...campuses.values()],
    buildings: [...buildings.values()],
    rooms: [...rooms.values()],
    added,
    rows,
    error: null,
  };
}

/** Bulk-import the Campus → Building → Room cascade from pasted CSV. Rows are
 *  `Campus,Building,Room` (Building/Room optional); an optional header is
 *  ignored. Imported entries are merged with the existing cascade — nothing is
 *  removed — then persisted via setFacilityConfig. */
function LocationImportModal({
  current,
  onClose,
  onDone,
}: {
  current: FacilityConfig | undefined;
  onClose: () => void;
  onDone: () => void;
}) {
  const [text, setText] = useState("");
  const saveMut = useMutation((patch: Partial<FacilityConfig>) => setFacilityConfig(patch));
  const parsed = useMemo(() => parseLocations(text, current), [text, current]);
  const hasInput = text.trim().length > 0;
  const nothingNew =
    parsed.added.campuses === 0 && parsed.added.buildings === 0 && parsed.added.rooms === 0;

  async function apply() {
    const res = await saveMut.run({
      campuses: parsed.campuses,
      buildings: parsed.buildings,
      rooms: parsed.rooms,
    });
    if (res) onDone();
  }

  return (
    <Modal
      title="Import locations (CSV)"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saveMut.pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={apply}
            disabled={saveMut.pending || !hasInput || nothingNew}
          >
            {saveMut.pending ? (
              <Spinner label="Importing…" />
            ) : (
              `Import ${parsed.added.campuses + parsed.added.buildings + parsed.added.rooms} new`
            )}
          </button>
        </>
      }
    >
      {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
      <p className="hint" style={{ marginTop: 0 }}>
        Paste one row per location as <strong>Campus,Building,Room</strong>. Building and Room are
        optional (a row may stop at the campus or building). A header row is ignored. Existing
        locations are kept — only new ones are added.
      </p>
      <label className="field">
        <span className="label">CSV</span>
        <textarea
          rows={10}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"Campus,Building,Room\nMain Campus,Science Hall,Room 101\nMain Campus,Science Hall,Room 102\nMain Campus,Library,\nNorth Campus,,"}
          style={{ width: "100%", fontFamily: "var(--mono, monospace)", fontSize: 13 }}
        />
      </label>
      {hasInput ? (
        <p className="hint" style={{ marginBottom: 0 }}>
          {parsed.rows} row(s) parsed — adding{" "}
          <strong>{parsed.added.campuses}</strong> campus(es),{" "}
          <strong>{parsed.added.buildings}</strong> building(s),{" "}
          <strong>{parsed.added.rooms}</strong> room(s).
          {nothingNew ? " Everything in this list already exists." : ""}
        </p>
      ) : null}
    </Modal>
  );
}

// ===== Ticket numbering ====================================================
// Per-tenant DISPLAY format (prefix + zero-padding) plus the sequence START
// for the next NEW ticket. The stored ticket.number stays a raw int; this only
// changes how it's shown and where a brand-new tenant's sequence begins. Start
// never renumbers existing tickets (see TenantDb.nextTicketNumber).

function NumberingTab() {
  const { data, loading, error, reload } = useApi((signal) => getTicketNumbering(signal), []);
  const saveMut = useMutation(setTicketNumbering);
  const [prefix, setPrefix] = useState("");
  const [start, setStart] = useState("1");
  const [padding, setPadding] = useState("0");
  const [saved, setSaved] = useState(false);

  const cfg: TicketNumberingConfig | undefined = data?.numbering;

  useEffect(() => {
    if (!cfg) return;
    setPrefix(cfg.prefix ?? "");
    setStart(String(cfg.start ?? 1));
    setPadding(String(cfg.padding ?? 0));
  }, [cfg]);

  const startNum = Number.parseInt(start, 10);
  const padNum = Number.parseInt(padding, 10);
  const prefixValid = prefix.length <= 16;
  const startValid = Number.isInteger(startNum) && startNum >= 1;
  const padValid = Number.isInteger(padNum) && padNum >= 0 && padNum <= 10;
  const allValid = prefixValid && startValid && padValid;

  // Live preview: the NEXT ticket number under the current (unsaved) settings.
  // If the tenant already has tickets, the real next number won't be `start`,
  // but `start` is the right value to PREVIEW the format against pre-launch.
  const previewN = startValid ? startNum : 1;
  const preview = formatTicketNumber(previewN, {
    prefix: prefixValid ? prefix : "",
    padding: padValid ? padNum : 0,
  });

  async function save() {
    if (!allValid) return;
    const res = await saveMut.run({ prefix, start: startNum, padding: padNum });
    if (res) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      reload();
    }
  }

  return (
    <div>
      <div className="card">
        <div className="card-head">
          <h3>Ticket numbering</h3>
        </div>
        <div className="card-body">
          {loading ? (
            <Loading label="Loading numbering settings…" />
          ) : error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : cfg ? (
            <>
              <p className="sub" style={{ marginTop: 0 }}>
                Choose how ticket numbers are displayed. The starting number affects only NEW
                tickets — existing tickets keep their numbers.
              </p>
              {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
              <label className="field">
                <span className="label">Prefix</span>
                <input
                  type="text"
                  value={prefix}
                  maxLength={16}
                  onChange={(e) => setPrefix(e.target.value)}
                  placeholder="TKT-"
                />
                {!prefixValid ? <span className="hint" style={{ color: "var(--danger)" }}>Prefix must be 16 characters or fewer.</span> : null}
              </label>
              <div className="row">
                <label className="field" style={{ flex: 1 }}>
                  <span className="label">Start number</span>
                  <input
                    type="number"
                    min={1}
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    placeholder="1"
                  />
                  {!startValid ? <span className="hint" style={{ color: "var(--danger)" }}>Must be a whole number ≥ 1.</span> : null}
                </label>
                <label className="field" style={{ flex: 1 }}>
                  <span className="label">Minimum digits (zero-padding)</span>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    value={padding}
                    onChange={(e) => setPadding(e.target.value)}
                    placeholder="0"
                  />
                  {!padValid ? <span className="hint" style={{ color: "var(--danger)" }}>Must be a whole number 0–10.</span> : null}
                </label>
              </div>
              <p className="hint">
                Next ticket will look like: <strong className="mono">{preview}</strong>
              </p>
              <div className="btn-row">
                <button className="btn primary" onClick={save} disabled={saveMut.pending || !allValid}>
                  {saveMut.pending ? <Spinner label="Saving…" /> : "Save"}
                </button>
                {saved ? <span className="pill ok">Saved</span> : null}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ===== Facility (work order) numbering =====================================
// ===========================================================================
// Facility value sets — tenant-configurable work-order statuses, categories,
// and work-order types (persisted as JSON lists under facility.* settings).
// Each list is edited locally and saved as a whole; codes are derived from the
// label server-side. Statuses and work-order types must keep at least one entry.
// ===========================================================================

/** Loads the facility value sets once and hands the config + a reload to its
 *  children. Each Facilities value-set tab wraps its editor in this gate. */
function FacilityCfgGate({ children }: { children: (cfg: FacilityConfig, reload: () => void) => ReactNode }) {
  const { data, loading, error, reload } = useApi((signal) => getFacilityConfig(signal), []);
  const cfg: FacilityConfig | undefined = data?.config;
  if (loading) return <Loading label="Loading facility settings…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!cfg) return <EmptyState>No facility settings.</EmptyState>;
  return <>{children(cfg, reload)}</>;
}

function FacilityStatusesTab() {
  return (
    <FacilityCfgGate>
      {(cfg, reload) => (
        <FacilityListEditor
          title="Work order statuses"
          addLabel="Add status"
          listKey="statuses"
          items={cfg.statuses}
          requireOne
          intro={
            <p className="hint" style={{ marginTop: 0 }}>
              Lifecycle lanes for the work-order queue and board (first lane is the default for new
              work orders; a “Done” status completes preventive-maintenance recurrence).
            </p>
          }
          onSaved={reload}
        />
      )}
    </FacilityCfgGate>
  );
}

function FacilityTypesTab() {
  return (
    <FacilityCfgGate>
      {(cfg, reload) => (
        <FacilityListEditor
          title="Work order types"
          addLabel="Add type"
          listKey="wo_types"
          items={cfg.wo_types}
          requireOne
          intro={
            <p className="hint" style={{ marginTop: 0 }}>
              Nature of the work (Reactive, Preventive, Inspection…). A “preventive” type with a
              recurrence auto-schedules its next occurrence on completion.
            </p>
          }
          onSaved={reload}
        />
      )}
    </FacilityCfgGate>
  );
}

function FacilityCategoriesTab() {
  return (
    <FacilityCfgGate>
      {(cfg, reload) => (
        <FacilityListEditor
          title="Categories"
          addLabel="Add category"
          listKey="categories"
          items={cfg.categories}
          intro={
            <p className="hint" style={{ marginTop: 0 }}>
              Trade / area used to classify and filter work orders (HVAC, Plumbing, Grounds…).
            </p>
          }
          onSaved={reload}
        />
      )}
    </FacilityCfgGate>
  );
}

function FacilityPrioritiesTab() {
  return (
    <FacilityCfgGate>
      {(cfg, reload) => (
        <FacilityListEditor
          title="Priorities"
          addLabel="Add priority"
          listKey="priorities"
          items={cfg.priorities}
          requireOne
          intro={
            <p className="hint" style={{ marginTop: 0 }}>
              Urgency levels shown on the work-order Priority dropdown (Low, Normal, High, Urgent…).
            </p>
          }
          onSaved={reload}
        />
      )}
    </FacilityCfgGate>
  );
}

/** Locations: a 3-level Campus → Building → Room tree (same UX as Request types).
 *  Top-level rows are campuses; their children are buildings; each building's
 *  children are rooms. Add/Edit/Delete persist immediately via setFacilityConfig
 *  (codes derived server-side). The same cascade powers tickets + work orders. */
type PlaceEdit =
  | { mode: "new-campus" }
  | { mode: "edit-campus"; option: FacilityOption }
  | { mode: "new-building"; campusCode: string; campusName: string }
  | { mode: "edit-building"; option: FacilityBuildingOption }
  | { mode: "new-room"; buildingCode: string; buildingName: string }
  | { mode: "edit-room"; option: FacilityRoomOption };

function FacilityLocationsTree({
  campuses,
  buildings,
  rooms,
  onSaved,
}: {
  campuses: FacilityOption[];
  buildings: FacilityBuildingOption[];
  rooms: FacilityRoomOption[];
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState<PlaceEdit | null>(null);
  const [filter, setFilter] = useState("");
  const saveMut = useMutation((patch: Partial<FacilityConfig>) => setFacilityConfig(patch));
  const buildingsOf = (campusCode: string) => buildings.filter((b) => b.campus_code === campusCode);
  const roomsOf = (buildingCode: string) => rooms.filter((r) => r.building_code === buildingCode);

  // Tree filter: keep a node visible if it, an ancestor, or a descendant matches
  // (so a matched room keeps its building + campus, and a matched campus keeps all
  // its children). Mirrors the Categories tree filter.
  const q = filter.trim().toLowerCase();
  const hit = (s: string) => s.toLowerCase().includes(q);
  const bldgHasHit = (bCode: string) => roomsOf(bCode).some((r) => hit(r.label));
  const campusShown = (c: FacilityOption) =>
    !q || hit(c.label) || buildingsOf(c.code).some((b) => hit(b.label) || bldgHasHit(b.code));
  const buildingShown = (c: FacilityOption, b: FacilityBuildingOption) =>
    !q || hit(c.label) || hit(b.label) || bldgHasHit(b.code);
  const roomShown = (c: FacilityOption, b: FacilityBuildingOption, r: FacilityRoomOption) =>
    !q || hit(c.label) || hit(b.label) || hit(r.label);
  const visibleCampuses = campuses.filter(campusShown);

  async function persist(patch: Partial<FacilityConfig>) {
    const res = await saveMut.run(patch);
    if (res) onSaved();
  }

  function delCampus(c: FacilityOption) {
    const childBuildings = buildingsOf(c.code);
    const childBuildingCodes = new Set(childBuildings.map((b) => b.code));
    const n = childBuildings.length;
    confirmThen(
      n > 0
        ? `Delete campus "${c.label}" and its ${n} building(s) + their rooms?`
        : `Delete campus "${c.label}"?`,
      () =>
        persist({
          campuses: campuses.filter((x) => x.code !== c.code),
          buildings: buildings.filter((b) => b.campus_code !== c.code),
          rooms: rooms.filter((r) => !childBuildingCodes.has(r.building_code)),
        }),
    );
  }
  function delBuilding(b: FacilityBuildingOption) {
    const n = roomsOf(b.code).length;
    confirmThen(
      n > 0 ? `Delete building "${b.label}" and its ${n} room(s)?` : `Delete building "${b.label}"?`,
      () =>
        persist({
          buildings: buildings.filter((x) => x.code !== b.code),
          rooms: rooms.filter((r) => r.building_code !== b.code),
        }),
    );
  }
  function delRoom(r: FacilityRoomOption) {
    confirmThen(`Delete room "${r.label}"?`, () =>
      persist({ rooms: rooms.filter((x) => x.code !== r.code) }),
    );
  }

  // Turn the modal's submitted name into the right whole-list patch.
  async function onModalSave(name: string) {
    const label = name.trim();
    if (!editing || !label) return;
    let patch: Partial<FacilityConfig>;
    switch (editing.mode) {
      case "new-campus":
        patch = { campuses: [...campuses, { code: "", label }] };
        break;
      case "edit-campus":
        patch = { campuses: campuses.map((c) => (c.code === editing.option.code ? { ...c, label } : c)) };
        break;
      case "new-building":
        patch = { buildings: [...buildings, { code: "", label, campus_code: editing.campusCode }] };
        break;
      case "edit-building":
        patch = { buildings: buildings.map((b) => (b.code === editing.option.code ? { ...b, label } : b)) };
        break;
      case "new-room":
        patch = { rooms: [...rooms, { code: "", label, building_code: editing.buildingCode }] };
        break;
      case "edit-room":
        patch = { rooms: rooms.map((r) => (r.code === editing.option.code ? { ...r, label } : r)) };
        break;
    }
    const res = await saveMut.run(patch);
    if (res) {
      setEditing(null);
      onSaved();
    }
  }

  return (
    <>
      <SettingsListCard
        title="Locations"
        count={campuses.length}
        addLabel="Add campus"
        onAdd={() => setEditing({ mode: "new-campus" })}
        intro={
          <p className="hint" style={{ marginTop: 0 }}>
            A Campus → Building → Room hierarchy. Forms show a Campus, then its Buildings, then that
            building's Rooms. Shared across work orders and tickets.
          </p>
        }
      >
        {campuses.length > 0 ? (
          <input
            type="search"
            className="input-inline"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter campuses, buildings or rooms…"
            aria-label="Filter locations"
            style={{ width: "100%", marginBottom: 12 }}
          />
        ) : null}
        {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
        {campuses.length === 0 ? (
          <EmptyState>No campuses yet — add the first one.</EmptyState>
        ) : visibleCampuses.length === 0 ? (
          <EmptyState>No locations match “{filter.trim()}”.</EmptyState>
        ) : (
          <ul className="req-types">
            {visibleCampuses.map((c) => (
              <li key={c.code} className="req-type">
                <div className="req-type-head">
                  <strong>{c.label}</strong>
                  <button className="btn small ghost req-add" onClick={() => setEditing({ mode: "new-building", campusCode: c.code, campusName: c.label })}>
                    + Building
                  </button>
                  <span className="row-actions">
                    <button className="btn small" onClick={() => setEditing({ mode: "edit-campus", option: c })}>Edit</button>
                    <button className="btn small danger" onClick={() => delCampus(c)}>Delete</button>
                  </span>
                </div>
                {buildingsOf(c.code).filter((b) => buildingShown(c, b)).length ? (
                  <ul className="req-subs">
                    {buildingsOf(c.code).filter((b) => buildingShown(c, b)).map((b) => (
                      <li key={b.code} className="req-sub">
                        <div className="req-sub-head">
                          <span>{b.label}</span>
                          <button className="btn small ghost req-add" onClick={() => setEditing({ mode: "new-room", buildingCode: b.code, buildingName: b.label })}>
                            + Room
                          </button>
                          <span className="row-actions">
                            <button className="btn small" onClick={() => setEditing({ mode: "edit-building", option: b })}>Edit</button>
                            <button className="btn small danger" onClick={() => delBuilding(b)}>Delete</button>
                          </span>
                        </div>
                        {roomsOf(b.code).filter((r) => roomShown(c, b, r)).length ? (
                          <ul className="req-subsubs">
                            {roomsOf(b.code).filter((r) => roomShown(c, b, r)).map((r) => (
                              <li key={r.code} className="req-subsub">
                                <span>{r.label}</span>
                                <span className="row-actions">
                                  <button className="btn small" onClick={() => setEditing({ mode: "edit-room", option: r })}>Edit</button>
                                  <button className="btn small danger" onClick={() => delRoom(r)}>Delete</button>
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="req-empty faint" style={{ marginLeft: 16 }}>No rooms yet.</p>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="req-empty faint">No buildings yet.</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </SettingsListCard>

      {editing ? (
        <FacilityPlaceModal edit={editing} pending={saveMut.pending} onClose={() => setEditing(null)} onSave={onModalSave} />
      ) : null}
    </>
  );
}

/** Add/Edit one campus, building, or room — a single label field. The code is
 *  derived from the label server-side; the parent comes from the edit context. */
function FacilityPlaceModal({
  edit,
  pending,
  onClose,
  onSave,
}: {
  edit: PlaceEdit;
  pending: boolean;
  onClose: () => void;
  onSave: (label: string) => void;
}) {
  const initial =
    edit.mode === "edit-campus" || edit.mode === "edit-building" || edit.mode === "edit-room"
      ? edit.option.label
      : "";
  const [label, setLabel] = useState(initial);
  const title =
    edit.mode === "new-campus"
      ? "Add campus"
      : edit.mode === "edit-campus"
        ? `Edit campus "${edit.option.label}"`
        : edit.mode === "new-building"
          ? `Add building to "${edit.campusName}"`
          : edit.mode === "edit-building"
            ? `Edit building "${edit.option.label}"`
            : edit.mode === "new-room"
              ? `Add room to "${edit.buildingName}"`
              : `Edit room "${edit.option.label}"`;
  const placeholder =
    edit.mode === "new-campus" || edit.mode === "edit-campus"
      ? "e.g. Bronx"
      : edit.mode === "new-building" || edit.mode === "edit-building"
        ? "e.g. King Hall"
        : "e.g. 2nd floor";

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    onSave(label);
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="facility-place-form" disabled={pending || !label.trim()}>
            {pending ? <Spinner label="Saving…" /> : "Save"}
          </button>
        </>
      }
    >
      <form id="facility-place-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={placeholder} autoFocus required />
          <span className="hint">The internal code is generated from the name and is stable across renames.</span>
        </label>
      </form>
    </Modal>
  );
}

/** One editable facility value list. Holds a local working copy; Add/Edit/Delete
 *  mutate it, and Save persists the whole list (codes derived server-side). */
/** Settings → Submission tracking: manage the options behind the ticket
 *  "Submission Tracking" (source channel) dropdown — add / rename / reorder /
 *  remove, exactly like Statuses & Types. Stored as a tenant config list;
 *  reuses FacilityOptionModal for the add/edit form. */
// ---- Import tickets from a previous system (CSV) ------------------------------

/** Minimal RFC-4180-ish CSV parser: handles quoted fields, escaped quotes (""),
 *  embedded commas/newlines, CRLF, and a leading BOM. Returns header + data rows. */
function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const s = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else if (ch === "\r") {
      /* CRLF — handled on the \n */
    } else field += ch;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  const headers = (rows.shift() ?? []).map((h) => h.trim());
  const data = rows.filter((r) => r.some((c) => c.trim() !== ""));
  return { headers, rows: data };
}

function parseDateSec(v: string): number | undefined {
  const t = v.trim();
  if (!t) return undefined;
  const ms = Date.parse(t);
  if (Number.isNaN(ms)) return undefined;
  return Math.floor(ms / 1000);
}

/** Target ticket fields the CSV columns can map to. `status`/`priority` resolve
 *  their cell value against this workspace's configured codes/names. */
const IMPORT_FIELDS: { key: string; label: string; required?: boolean; guess: string[]; note?: string }[] = [
  { key: "subject", label: "Subject", required: true, guess: ["subject", "title", "summary"] },
  { key: "description", label: "Description", guess: ["description", "body", "details", "message", "comment"] },
  { key: "requester_name", label: "Requester name", guess: ["requester", "contact", "customer", "reporter", "name", "from"] },
  { key: "requester_email", label: "Requester email", guess: ["email", "e-mail", "mail"] },
  { key: "status_code", label: "Status", guess: ["status", "state"], note: "matched to your Statuses by name/code" },
  { key: "priority_code", label: "Priority", guess: ["priority", "severity"], note: "matched to your Priorities by name/code" },
  { key: "department", label: "Department", guess: ["department", "dept", "queue", "group"] },
  { key: "created_at", label: "Created date", guess: ["created", "opened", "submitted", "date"], note: "original date is preserved" },
  { key: "due_date", label: "Due date", guess: ["due", "deadline"] },
];

function ImportTicketsTab() {
  const statusesApi = useApi((signal) => listStatuses(signal), []);
  const prioApi = useApi((signal) => listPriorities(signal), []);
  const statuses = statusesApi.data?.statuses ?? [];
  const priorities = prioApi.data?.priorities ?? [];

  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  // field key -> CSV column index (-1 = skip / not mapped)
  const [mapping, setMapping] = useState<Record<string, number>>({});
  const [parseError, setParseError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<ImportTicketsResponse | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    setResult(null);
    setImportError(null);
    setParseError(null);
    const looksCsv = /\.csv$/i.test(file.name) || file.type === "text/csv" || file.type === "";
    if (!looksCsv) {
      setParseError("That doesn't look like a CSV file. Export your tickets as .csv and try again.");
      return;
    }
    try {
      const text = await file.text();
      const p = parseCsv(text);
      if (p.headers.length === 0 || p.rows.length === 0) {
        setParseError("That file has no header row or no data rows.");
        setParsed(null);
        return;
      }
      // Auto-guess each field to the first header that contains one of its keywords.
      const guessed: Record<string, number> = {};
      const lowHeaders = p.headers.map((h) => h.toLowerCase());
      for (const f of IMPORT_FIELDS) {
        guessed[f.key] = lowHeaders.findIndex((h) => f.guess.some((g) => h.includes(g)));
      }
      setFileName(file.name);
      setParsed(p);
      setMapping(guessed);
    } catch {
      setParseError("Could not read that file. Make sure it's a UTF-8 CSV.");
    }
  }

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (running) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  const statusByVal = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of statuses) {
      m.set(s.code.toLowerCase(), s.code);
      m.set(s.name.toLowerCase(), s.code);
    }
    return m;
  }, [statuses]);
  const prioByVal = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of priorities) {
      m.set(p.code.toLowerCase(), p.code);
      m.set(p.name.toLowerCase(), p.code);
    }
    return m;
  }, [priorities]);

  function buildRows(): ImportTicketRow[] {
    if (!parsed) return [];
    const cell = (r: string[], key: string) => {
      const idx = mapping[key];
      return idx != null && idx >= 0 ? (r[idx] ?? "").trim() : "";
    };
    const out: ImportTicketRow[] = [];
    for (const r of parsed.rows) {
      const subject = cell(r, "subject");
      if (!subject) continue; // no subject → nothing to import for this row
      const row: ImportTicketRow = { subject };
      const desc = cell(r, "description");
      if (desc) row.description = desc;
      const rn = cell(r, "requester_name");
      if (rn) row.requester_name = rn;
      const re = cell(r, "requester_email");
      if (re) row.requester_email = re;
      const dep = cell(r, "department");
      if (dep) row.department = dep;
      const st = cell(r, "status_code");
      const stc = st ? statusByVal.get(st.toLowerCase()) : undefined;
      if (stc) row.status_code = stc;
      const pr = cell(r, "priority_code");
      const prc = pr ? prioByVal.get(pr.toLowerCase()) : undefined;
      if (prc) row.priority_code = prc;
      const ca = parseDateSec(cell(r, "created_at"));
      if (ca) row.created_at = ca;
      const dd = parseDateSec(cell(r, "due_date"));
      if (dd) row.due_date = dd;
      out.push(row);
    }
    return out;
  }

  const builtRows = parsed ? buildRows() : [];
  const skipped = parsed ? parsed.rows.length - builtRows.length : 0;
  const subjectMapped = (mapping["subject"] ?? -1) >= 0;

  async function runImport() {
    const rows = buildRows();
    if (rows.length === 0) {
      setImportError("No importable rows — map a Subject column with non-empty values.");
      return;
    }
    setRunning(true);
    setResult(null);
    setImportError(null);
    const agg: ImportTicketsResponse = { created: 0, failed: 0, results: [] };
    const CHUNK = 200; // under the server's 500-row cap; keeps each request light
    setProgress({ done: 0, total: rows.length });
    try {
      for (let i = 0; i < rows.length; i += CHUNK) {
        const res = await importTickets(rows.slice(i, i + CHUNK));
        agg.created += res.created;
        agg.failed += res.failed;
        for (const rr of res.results) agg.results.push({ ...rr, index: rr.index + i });
        setProgress({ done: Math.min(i + CHUNK, rows.length), total: rows.length });
      }
      setResult(agg);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Import failed.");
    } finally {
      setRunning(false);
      setProgress(null);
    }
  }

  function reset() {
    setParsed(null);
    setMapping({});
    setFileName("");
    setResult(null);
    setImportError(null);
    setParseError(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  const failures = result?.results.filter((r) => !r.ok) ?? [];

  return (
    <div className="import-tickets">
      {/* Step 1 — upload */}
      <section className="settings-card">
        <h3>1 · Upload a CSV</h3>
        <p className="muted">
          Export your tickets from the previous system as CSV (the first row must be column headers). Up to a few thousand
          rows; large files import in batches.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={onFile}
          disabled={running}
          style={{ display: "none" }}
        />
        {parsed ? (
          // Loaded state: filename + row summary + a way to swap the file.
          <div className="import-file-loaded">
            <span className="import-file-icon" aria-hidden="true">
              📄
            </span>
            <div className="import-file-meta">
              <div className="import-file-name">{fileName}</div>
              <div className="muted">
                {parsed.headers.length} columns · {parsed.rows.length} rows
              </div>
            </div>
            <button type="button" className="btn ghost small" onClick={() => fileRef.current?.click()} disabled={running}>
              Replace
            </button>
            <button type="button" className="btn ghost small" onClick={reset} disabled={running}>
              Clear
            </button>
          </div>
        ) : (
          // Empty state: a clickable + drag-and-drop dropzone.
          <button
            type="button"
            className={`import-dropzone${dragOver ? " over" : ""}`}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              if (!running) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            disabled={running}
          >
            <span className="import-dropzone-icon" aria-hidden="true">
              ⬆️
            </span>
            <span className="import-dropzone-main">
              <strong>Drag &amp; drop your CSV here</strong>
              <span className="muted"> or click to browse</span>
            </span>
            <span className="muted import-dropzone-hint">.csv files only · UTF-8</span>
          </button>
        )}
        {parseError ? <Alert kind="error">{parseError}</Alert> : null}
      </section>

      {/* Step 2 — map columns */}
      {parsed ? (
        <section className="settings-card">
          <h3>2 · Map columns to ticket fields</h3>
          <p className="muted">
            We guessed these from your headers — adjust any that look wrong. Unmapped fields are left blank. Status and
            Priority values are matched to this workspace's configured options; unmatched values fall back to the
            defaults.
          </p>
          <div className="import-map">
            {IMPORT_FIELDS.map((f) => (
              <label key={f.key} className="import-map-row">
                <span className="import-map-label">
                  {f.label}
                  {f.required ? <span className="req"> *</span> : null}
                  {f.note ? <span className="muted import-map-note"> — {f.note}</span> : null}
                </span>
                <select
                  value={mapping[f.key] ?? -1}
                  onChange={(e) => setMapping((m) => ({ ...m, [f.key]: Number(e.target.value) }))}
                  disabled={running}
                >
                  <option value={-1}>— skip —</option>
                  {parsed.headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `Column ${i + 1}`}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          {!subjectMapped ? <Alert kind="error">Map a <strong>Subject</strong> column to continue.</Alert> : null}
        </section>
      ) : null}

      {/* Step 3 — preview + import */}
      {parsed && subjectMapped ? (
        <section className="settings-card">
          <h3>3 · Preview &amp; import</h3>
          <p className="muted">
            <strong>{builtRows.length}</strong> rows ready to import
            {skipped > 0 ? ` · ${skipped} skipped (no subject)` : ""}. First few shown below.
          </p>
          <div style={{ overflowX: "auto" }}>
            <table className="import-preview">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Requester</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {builtRows.slice(0, 5).map((r, i) => (
                  <tr key={i}>
                    <td>{r.subject}</td>
                    <td>{r.requester_name ?? ""}</td>
                    <td>{r.requester_email ?? ""}</td>
                    <td>{r.status_code ?? <span className="muted">default</span>}</td>
                    <td>{r.priority_code ?? <span className="muted">default</span>}</td>
                    <td>{r.created_at ? formatDate(r.created_at) : <span className="muted">now</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row" style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
            <button type="button" className="btn primary" onClick={runImport} disabled={running || builtRows.length === 0}>
              {running ? <Spinner /> : null} Import {builtRows.length} ticket{builtRows.length === 1 ? "" : "s"}
            </button>
            {progress ? (
              <span className="muted">
                {progress.done} / {progress.total}…
              </span>
            ) : null}
          </div>
          {importError ? <Alert kind="error">{importError}</Alert> : null}
          {result ? (
            <Alert kind={result.failed > 0 ? "info" : "success"}>
              Imported <strong>{result.created}</strong> ticket{result.created === 1 ? "" : "s"}
              {result.failed > 0 ? ` · ${result.failed} failed` : ""}.
              {failures.length > 0 ? (
                <ul className="import-failures">
                  {failures.slice(0, 10).map((fr) => (
                    <li key={fr.index}>
                      Row {fr.index + 2}: {fr.error}
                    </li>
                  ))}
                  {failures.length > 10 ? <li>…and {failures.length - 10} more</li> : null}
                </ul>
              ) : null}
            </Alert>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function SubmissionTrackingTab() {
  const { data, loading, error, reload } = useApi((signal) => getTicketSources(signal), []);
  const items = data?.sources ?? [];
  const [draft, setDraft] = useState<FacilityOption[]>([]);
  const [editing, setEditing] = useState<{ index: number; option: FacilityOption } | "new" | null>(null);
  const saveMut = useMutation((next: FacilityOption[]) => setTicketSources(next));
  const [saved, setSaved] = useState(false);

  useEffect(() => setDraft(items), [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = JSON.stringify(draft) !== JSON.stringify(items);

  function upsert(label: string, target: { index: number } | "new") {
    const trimmed = label.trim();
    if (!trimmed) return;
    setDraft((prev) =>
      target === "new"
        ? [...prev, { code: "", label: trimmed }]
        : prev.map((o, i) => (i === target.index ? { ...o, label: trimmed } : o)),
    );
    setEditing(null);
  }
  function remove(index: number) {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  }
  function move(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= draft.length) return;
    setDraft((prev) => {
      const copy = [...prev];
      [copy[index], copy[next]] = [copy[next]!, copy[index]!];
      return copy;
    });
  }
  async function save() {
    const res = await saveMut.run(draft);
    if (res) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      reload();
    }
  }

  if (loading) return <Loading label="Loading options…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <>
      <SettingsListCard
        title="Submission tracking options"
        count={draft.length}
        addLabel="Add option"
        onAdd={() => setEditing("new")}
        intro={
          <p className="hint" style={{ marginTop: 0 }}>
            The choices in the ticket “Submission Tracking” (source channel) dropdown. The order
            here is the order shown on the form.
          </p>
        }
      >
        {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
        {draft.length === 0 ? (
          <EmptyState>No options yet — add the first one.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Label</th>
                <th>Code</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {draft.map((o, i) => (
                <tr key={`${o.code}-${i}`}>
                  <td><strong>{o.label}</strong></td>
                  <td className="mono faint">{o.code || <span className="faint">— on save —</span>}</td>
                  <td className="row-actions">
                    <button className="btn small" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" title="Move up">↑</button>
                    <button className="btn small" onClick={() => move(i, 1)} disabled={i === draft.length - 1} aria-label="Move down" title="Move down">↓</button>
                    <button className="btn small" onClick={() => setEditing({ index: i, option: o })}>Edit</button>
                    <button
                      className="btn small danger"
                      onClick={() => confirmThen(`Remove "${o.label}"? Existing tickets keep this value.`, () => remove(i))}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="btn-row" style={{ marginTop: 12, alignItems: "center" }}>
          <button className="btn primary" onClick={save} disabled={!dirty || saveMut.pending}>
            {saveMut.pending ? <Spinner label="Saving…" /> : "Save changes"}
          </button>
          {dirty ? <span className="faint">Unsaved changes</span> : null}
          {saved ? <span className="pill ok">Saved</span> : null}
        </div>
      </SettingsListCard>

      {editing ? (
        <FacilityOptionModal
          title={editing === "new" ? "Add option" : "Edit option"}
          initial={editing === "new" ? "" : editing.option.label}
          onClose={() => setEditing(null)}
          onSave={(label) => upsert(label, editing === "new" ? "new" : { index: editing.index })}
        />
      ) : null}
    </>
  );
}

/** Action item categories — a single configurable {code,label} list (settings
 *  key action_item.categories). Mirrors SubmissionTrackingTab exactly. */
function ActionItemCategoriesTab() {
  const { data, loading, error, reload } = useApi((signal) => getActionItemCategories(signal), []);
  const items = data?.categories ?? [];
  const [draft, setDraft] = useState<FacilityOption[]>([]);
  const [editing, setEditing] = useState<{ index: number; option: FacilityOption } | "new" | null>(null);
  const saveMut = useMutation((next: FacilityOption[]) => setActionItemCategories(next));
  const [saved, setSaved] = useState(false);

  useEffect(() => setDraft(items), [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = JSON.stringify(draft) !== JSON.stringify(items);

  function upsert(label: string, target: { index: number } | "new") {
    const trimmed = label.trim();
    if (!trimmed) return;
    setDraft((prev) =>
      target === "new"
        ? [...prev, { code: "", label: trimmed }]
        : prev.map((o, i) => (i === target.index ? { ...o, label: trimmed } : o)),
    );
    setEditing(null);
  }
  function remove(index: number) {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  }
  function move(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= draft.length) return;
    setDraft((prev) => {
      const copy = [...prev];
      [copy[index], copy[next]] = [copy[next]!, copy[index]!];
      return copy;
    });
  }
  async function save() {
    const res = await saveMut.run(draft);
    if (res) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      reload();
    }
  }

  if (loading) return <Loading label="Loading categories…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <>
      <SettingsListCard
        title="Action item categories"
        count={draft.length}
        addLabel="Add category"
        onAdd={() => setEditing("new")}
        intro={
          <p className="hint" style={{ marginTop: 0 }}>
            The choices in the action-item Category dropdown. The order here is the order shown on the form.
          </p>
        }
      >
        {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
        {draft.length === 0 ? (
          <EmptyState>No categories yet — add the first one.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Label</th>
                <th>Code</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {draft.map((o, i) => (
                <tr key={`${o.code}-${i}`}>
                  <td><strong>{o.label}</strong></td>
                  <td className="mono faint">{o.code || <span className="faint">— on save —</span>}</td>
                  <td className="row-actions">
                    <button className="btn small" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" title="Move up">↑</button>
                    <button className="btn small" onClick={() => move(i, 1)} disabled={i === draft.length - 1} aria-label="Move down" title="Move down">↓</button>
                    <button className="btn small" onClick={() => setEditing({ index: i, option: o })}>Edit</button>
                    <button
                      className="btn small danger"
                      onClick={() => confirmThen(`Remove "${o.label}"? Existing action items keep this value.`, () => remove(i))}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="btn-row" style={{ marginTop: 12, alignItems: "center" }}>
          <button className="btn primary" onClick={save} disabled={!dirty || saveMut.pending}>
            {saveMut.pending ? <Spinner label="Saving…" /> : "Save changes"}
          </button>
          {dirty ? <span className="faint">Unsaved changes</span> : null}
          {saved ? <span className="pill ok">Saved</span> : null}
        </div>
      </SettingsListCard>

      {editing ? (
        <FacilityOptionModal
          title={editing === "new" ? "Add category" : "Edit category"}
          initial={editing === "new" ? "" : editing.option.label}
          onClose={() => setEditing(null)}
          onSave={(label) => upsert(label, editing === "new" ? "new" : { index: editing.index })}
        />
      ) : null}
    </>
  );
}

function FacilityListEditor({
  title,
  addLabel,
  listKey,
  items,
  intro,
  requireOne = false,
  onSaved,
}: {
  title: string;
  addLabel: string;
  listKey: keyof FacilityConfig;
  items: FacilityOption[];
  intro?: ReactNode;
  requireOne?: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<FacilityOption[]>(items);
  const [editing, setEditing] = useState<{ index: number; option: FacilityOption } | "new" | null>(null);
  const saveMut = useMutation((next: FacilityOption[]) => setFacilityConfig({ [listKey]: next }));

  // Re-sync from the server whenever the loaded list changes (e.g. after save).
  useEffect(() => setDraft(items), [items]);

  // Each change auto-saves immediately (the facility config is one KV blob, so a
  // change persists the whole updated list). Optimistic local update, then save.
  function persist(next: FacilityOption[]) {
    setDraft(next);
    void saveMut.run(next).then((res) => res && onSaved());
  }

  function upsert(label: string, color: string | null, target: { index: number } | "new") {
    const trimmed = label.trim();
    if (!trimmed) return;
    const next =
      target === "new"
        ? [...draft, { code: "", label: trimmed, color }]
        : draft.map((o, i) => (i === target.index ? { ...o, label: trimmed, color } : o));
    setEditing(null);
    persist(next);
  }

  function remove(index: number) {
    persist(draft.filter((_, i) => i !== index));
  }

  function moveOption(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= draft.length) return;
    const copy = [...draft];
    [copy[index], copy[next]] = [copy[next]!, copy[index]!];
    persist(copy);
  }

  return (
    <>
      <SettingsListCard
        title={title}
        count={draft.length}
        addLabel={addLabel}
        onAdd={() => setEditing("new")}
        intro={intro}
      >
        {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
        {draft.length === 0 ? (
          <EmptyState>No options yet — add the first one.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Label</th>
                <th>Code</th>
                {listKey === "statuses" ? <th>Color</th> : null}
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {draft.map((o, i) => (
                <tr key={`${o.code}-${i}`}>
                  <td><strong>{o.label}</strong></td>
                  <td className="mono faint">{o.code || <span className="faint">— on save —</span>}</td>
                  {listKey === "statuses" ? <td><ColorSwatch color={o.color} /></td> : null}
                  <td className="row-actions">
                    <button className="btn small" onClick={() => moveOption(i, -1)} disabled={i === 0} aria-label="Move up" title="Move up">↑</button>
                    <button className="btn small" onClick={() => moveOption(i, 1)} disabled={i === draft.length - 1} aria-label="Move down" title="Move down">↓</button>
                    <button className="btn small" onClick={() => setEditing({ index: i, option: o })}>
                      Edit
                    </button>
                    <button
                      className="btn small danger"
                      disabled={requireOne && draft.length <= 1}
                      title={requireOne && draft.length <= 1 ? "At least one is required" : undefined}
                      onClick={() =>
                        confirmThen(
                          `Remove "${o.label}"? Existing work orders keep this value until changed.`,
                          () => remove(i),
                        )
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {saveMut.pending ? (
          <div className="btn-row" style={{ marginTop: 10 }}>
            <Spinner label="Saving…" />
          </div>
        ) : null}
      </SettingsListCard>

      {editing ? (
        <FacilityOptionModal
          title={editing === "new" ? `Add — ${title}` : `Edit — ${title}`}
          initial={editing === "new" ? "" : editing.option.label}
          initialColor={editing === "new" ? null : editing.option.color ?? null}
          withColor={listKey === "statuses"}
          onClose={() => setEditing(null)}
          onSave={(label, color) => upsert(label, color, editing === "new" ? "new" : { index: editing.index })}
        />
      ) : null}
    </>
  );
}

function FacilityOptionModal({
  title,
  initial,
  initialColor,
  withColor,
  onClose,
  onSave,
}: {
  title: string;
  initial: string;
  initialColor?: string | null;
  withColor?: boolean;
  onClose: () => void;
  onSave: (label: string, color: string | null) => void;
}) {
  const [label, setLabel] = useState(initial);
  const [color, setColor] = useState<string | null>(initialColor ?? null);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    onSave(label, withColor ? color : null);
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="submit" form="facility-option-form" disabled={!label.trim()}>
            Save
          </button>
        </>
      }
    >
      <form id="facility-option-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Label</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Awaiting parts"
            autoFocus
            required
          />
          <span className="hint">The internal code is generated from the label and is stable across renames.</span>
        </label>
        {withColor ? <StatusColorField value={color} onChange={setColor} /> : null}
      </form>
    </Modal>
  );
}

// ===========================================================================
// Change Management value sets (Settings → Change Management). Mirrors the
// Facility value-set editor: tenant-configurable statuses / types / risks with
// color + reorder, persisted as JSON under change.statuses|types|risks.
// ===========================================================================

function ChangeCfgGate({ children }: { children: (cfg: ChangeConfig, reload: () => void) => ReactNode }) {
  const { data, loading, error, reload } = useApi((signal) => getChangeConfig(signal), []);
  const cfg: ChangeConfig | undefined = data?.config;
  if (loading) return <Loading label="Loading change settings…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!cfg) return <EmptyState>No change settings.</EmptyState>;
  return <>{children(cfg, reload)}</>;
}

function ChangeListEditor({
  title,
  addLabel,
  listKey,
  items,
  intro,
  onSaved,
}: {
  title: string;
  addLabel: string;
  listKey: keyof ChangeConfig;
  items: ChangeOption[];
  intro?: ReactNode;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<ChangeOption[]>(items);
  const [editing, setEditing] = useState<{ index: number; option: ChangeOption } | "new" | null>(null);
  const saveMut = useMutation((next: ChangeOption[]) => setChangeConfig({ [listKey]: next }));

  useEffect(() => setDraft(items), [items]);

  function persist(next: ChangeOption[]) {
    setDraft(next);
    void saveMut.run(next).then((res) => res && onSaved());
  }
  function upsert(label: string, color: string | null, target: { index: number } | "new") {
    const trimmed = label.trim();
    if (!trimmed) return;
    const next =
      target === "new"
        ? [...draft, { code: "", label: trimmed, color }]
        : draft.map((o, i) => (i === target.index ? { ...o, label: trimmed, color } : o));
    setEditing(null);
    persist(next);
  }
  function remove(index: number) {
    persist(draft.filter((_, i) => i !== index));
  }
  function moveOption(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= draft.length) return;
    const copy = [...draft];
    [copy[index], copy[next]] = [copy[next]!, copy[index]!];
    persist(copy);
  }

  return (
    <>
      <SettingsListCard title={title} count={draft.length} addLabel={addLabel} onAdd={() => setEditing("new")} intro={intro}>
        {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
        {draft.length === 0 ? (
          <EmptyState>No options yet — add the first one.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Label</th>
                <th>Code</th>
                <th>Color</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {draft.map((o, i) => (
                <tr key={`${o.code}-${i}`}>
                  <td><strong>{o.label}</strong></td>
                  <td className="mono faint">{o.code || <span className="faint">— on save —</span>}</td>
                  <td><ColorSwatch color={o.color} /></td>
                  <td className="row-actions">
                    <button className="btn small" onClick={() => moveOption(i, -1)} disabled={i === 0} aria-label="Move up" title="Move up">↑</button>
                    <button className="btn small" onClick={() => moveOption(i, 1)} disabled={i === draft.length - 1} aria-label="Move down" title="Move down">↓</button>
                    <button className="btn small" onClick={() => setEditing({ index: i, option: o })}>Edit</button>
                    <button
                      className="btn small danger"
                      disabled={draft.length <= 1}
                      title={draft.length <= 1 ? "At least one is required" : undefined}
                      onClick={() =>
                        confirmThen(`Remove "${o.label}"? Existing changes keep this value until changed.`, () => remove(i))
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {saveMut.pending ? (
          <div className="btn-row" style={{ marginTop: 10 }}>
            <Spinner label="Saving…" />
          </div>
        ) : null}
      </SettingsListCard>

      {editing ? (
        <FacilityOptionModal
          title={editing === "new" ? `Add — ${title}` : `Edit — ${title}`}
          initial={editing === "new" ? "" : editing.option.label}
          initialColor={editing === "new" ? null : editing.option.color ?? null}
          withColor
          onClose={() => setEditing(null)}
          onSave={(label, color) => upsert(label, color, editing === "new" ? "new" : { index: editing.index })}
        />
      ) : null}
    </>
  );
}

function ChangeStatusesTab() {
  return (
    <ChangeCfgGate>
      {(cfg, reload) => (
        <ChangeListEditor
          title="Change statuses"
          addLabel="Add status"
          listKey="statuses"
          items={cfg.statuses}
          intro={
            <p className="hint" style={{ marginTop: 0 }}>
              Lifecycle lanes for the Change Management queue and board (first lane is the default for new changes).
            </p>
          }
          onSaved={reload}
        />
      )}
    </ChangeCfgGate>
  );
}

function ChangeTypesTab() {
  return (
    <ChangeCfgGate>
      {(cfg, reload) => (
        <ChangeListEditor
          title="Change types"
          addLabel="Add type"
          listKey="types"
          items={cfg.types}
          intro={
            <p className="hint" style={{ marginTop: 0 }}>
              The nature of the change (Standard, Normal, Emergency…).
            </p>
          }
          onSaved={reload}
        />
      )}
    </ChangeCfgGate>
  );
}

function ChangeRisksTab() {
  return (
    <ChangeCfgGate>
      {(cfg, reload) => (
        <ChangeListEditor
          title="Risk levels"
          addLabel="Add risk level"
          listKey="risks"
          items={cfg.risks}
          intro={
            <p className="hint" style={{ marginTop: 0 }}>
              Risk levels shown on the change Risk dropdown and risk badges.
            </p>
          }
          onSaved={reload}
        />
      )}
    </ChangeCfgGate>
  );
}

function ChangeImpactsTab() {
  return (
    <ChangeCfgGate>
      {(cfg, reload) => (
        <ChangeListEditor
          title="Business impact"
          addLabel="Add impact level"
          listKey="impacts"
          items={cfg.impacts}
          intro={
            <p className="hint" style={{ marginTop: 0 }}>
              Business-impact levels shown on the change request form (High, Moderate, Low…).
            </p>
          }
          onSaved={reload}
        />
      )}
    </ChangeCfgGate>
  );
}

function ChangeUrgenciesTab() {
  return (
    <ChangeCfgGate>
      {(cfg, reload) => (
        <ChangeListEditor
          title="Urgency / Priority"
          addLabel="Add level"
          listKey="urgencies"
          items={cfg.urgencies}
          intro={
            <p className="hint" style={{ marginTop: 0 }}>
              Urgency / priority levels shown on the change request form (High, Moderate, Low…).
            </p>
          }
          onSaved={reload}
        />
      )}
    </ChangeCfgGate>
  );
}

function ChangeSchedulesTab() {
  return (
    <ChangeCfgGate>
      {(cfg, reload) => (
        <ChangeListEditor
          title="Schedules"
          addLabel="Add schedule"
          listKey="schedules"
          items={cfg.schedules}
          intro={
            <p className="hint" style={{ marginTop: 0 }}>
              Recurrence cadences for automated/recurring changes (Daily, Weekly, Every Semester…).
            </p>
          }
          onSaved={reload}
        />
      )}
    </ChangeCfgGate>
  );
}

// Clone of NumberingTab for the Facilities module; same prefix / start /
// min-digits controls + live preview, persisted under facility.numbering.*.

function FacilityNumberingTab() {
  const { data, loading, error, reload } = useApi((signal) => getFacilityNumbering(signal), []);
  const saveMut = useMutation(setFacilityNumbering);
  const [prefix, setPrefix] = useState("");
  const [start, setStart] = useState("1");
  const [padding, setPadding] = useState("0");
  const [saved, setSaved] = useState(false);

  const cfg: FacilityNumberingConfig | undefined = data?.numbering;

  useEffect(() => {
    if (!cfg) return;
    setPrefix(cfg.prefix ?? "");
    setStart(String(cfg.start ?? 1));
    setPadding(String(cfg.padding ?? 0));
  }, [cfg]);

  const startNum = Number.parseInt(start, 10);
  const padNum = Number.parseInt(padding, 10);
  const prefixValid = prefix.length <= 16;
  const startValid = Number.isInteger(startNum) && startNum >= 1;
  const padValid = Number.isInteger(padNum) && padNum >= 0 && padNum <= 10;
  const allValid = prefixValid && startValid && padValid;

  // Live preview: the NEXT work order number under the current (unsaved) settings.
  const previewN = startValid ? startNum : 1;
  const preview = formatTicketNumber(previewN, {
    prefix: prefixValid ? prefix : "",
    padding: padValid ? padNum : 0,
  });

  async function save() {
    if (!allValid) return;
    const res = await saveMut.run({ prefix, start: startNum, padding: padNum });
    if (res) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      reload();
    }
  }

  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-head">
          <h3>Work order numbering</h3>
        </div>
        <div className="card-body">
          {loading ? (
            <Loading label="Loading numbering settings…" />
          ) : error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : cfg ? (
            <>
              <p className="sub" style={{ marginTop: 0 }}>
                Choose how work order numbers are displayed. The starting number affects only
                NEW work orders — existing work orders keep their numbers.
              </p>
              {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
              <label className="field">
                <span className="label">Prefix</span>
                <input
                  type="text"
                  value={prefix}
                  maxLength={16}
                  onChange={(e) => setPrefix(e.target.value)}
                  placeholder="FAC-"
                />
                {!prefixValid ? <span className="hint" style={{ color: "var(--danger)" }}>Prefix must be 16 characters or fewer.</span> : null}
              </label>
              <div className="row">
                <label className="field" style={{ flex: 1 }}>
                  <span className="label">Start number</span>
                  <input
                    type="number"
                    min={1}
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    placeholder="1"
                  />
                  {!startValid ? <span className="hint" style={{ color: "var(--danger)" }}>Must be a whole number ≥ 1.</span> : null}
                </label>
                <label className="field" style={{ flex: 1 }}>
                  <span className="label">Minimum digits (zero-padding)</span>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    value={padding}
                    onChange={(e) => setPadding(e.target.value)}
                    placeholder="0"
                  />
                  {!padValid ? <span className="hint" style={{ color: "var(--danger)" }}>Must be a whole number 0–10.</span> : null}
                </label>
              </div>
              <p className="hint">
                Next work order will look like: <strong className="mono">{preview}</strong>
              </p>
              <div className="btn-row">
                <button className="btn primary" onClick={save} disabled={saveMut.pending || !allValid}>
                  {saveMut.pending ? <Spinner label="Saving…" /> : "Save"}
                </button>
                {saved ? <span className="pill ok">Saved</span> : null}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ===== Change-request numbering — mirrors Work Order Numbering exactly =======

function ChangeNumberingTab() {
  const { data, loading, error, reload } = useApi((signal) => getChangeNumbering(signal), []);
  const saveMut = useMutation(setChangeNumbering);
  const [prefix, setPrefix] = useState("");
  const [start, setStart] = useState("1");
  const [padding, setPadding] = useState("0");
  const [saved, setSaved] = useState(false);

  const cfg: ChangeNumberingConfig | undefined = data?.numbering;

  useEffect(() => {
    if (!cfg) return;
    setPrefix(cfg.prefix ?? "");
    setStart(String(cfg.start ?? 1));
    setPadding(String(cfg.padding ?? 0));
  }, [cfg]);

  const startNum = Number.parseInt(start, 10);
  const padNum = Number.parseInt(padding, 10);
  const prefixValid = prefix.length <= 16;
  const startValid = Number.isInteger(startNum) && startNum >= 1;
  const padValid = Number.isInteger(padNum) && padNum >= 0 && padNum <= 10;
  const allValid = prefixValid && startValid && padValid;

  // Live preview: the NEXT change request number under the current (unsaved) settings.
  const previewN = startValid ? startNum : 1;
  const preview = formatChangeNumber(previewN, {
    prefix: prefixValid ? prefix : "",
    padding: padValid ? padNum : 0,
  });

  async function save() {
    if (!allValid) return;
    const res = await saveMut.run({ prefix, start: startNum, padding: padNum });
    if (res) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      reload();
    }
  }

  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-head">
          <h3>Change request numbering</h3>
        </div>
        <div className="card-body">
          {loading ? (
            <Loading label="Loading numbering settings…" />
          ) : error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : cfg ? (
            <>
              <p className="sub" style={{ marginTop: 0 }}>
                Choose how change request numbers are displayed. The starting number affects only
                NEW change requests — existing change requests keep their numbers.
              </p>
              {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
              <label className="field">
                <span className="label">Prefix</span>
                <input
                  type="text"
                  value={prefix}
                  maxLength={16}
                  onChange={(e) => setPrefix(e.target.value)}
                  placeholder="CHG-"
                />
                {!prefixValid ? <span className="hint" style={{ color: "var(--danger)" }}>Prefix must be 16 characters or fewer.</span> : null}
              </label>
              <div className="row">
                <label className="field" style={{ flex: 1 }}>
                  <span className="label">Start number</span>
                  <input
                    type="number"
                    min={1}
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    placeholder="1"
                  />
                  {!startValid ? <span className="hint" style={{ color: "var(--danger)" }}>Must be a whole number ≥ 1.</span> : null}
                </label>
                <label className="field" style={{ flex: 1 }}>
                  <span className="label">Minimum digits (zero-padding)</span>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    value={padding}
                    onChange={(e) => setPadding(e.target.value)}
                    placeholder="0"
                  />
                  {!padValid ? <span className="hint" style={{ color: "var(--danger)" }}>Must be a whole number 0–10.</span> : null}
                </label>
              </div>
              <p className="hint">
                Next change request will look like: <strong className="mono">{preview}</strong>
              </p>
              <div className="btn-row">
                <button className="btn primary" onClick={save} disabled={saveMut.pending || !allValid}>
                  {saveMut.pending ? <Spinner label="Saving…" /> : "Save"}
                </button>
                {saved ? <span className="pill ok">Saved</span> : null}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ===== Preventive-maintenance (PM) schedule templates (0071) ===============
// Recurring work-order blueprints. List/create/edit/delete templates plus a
// "Generate due PMs now" button that materializes every active, due template
// into a facility_request (wo_type='preventive'). Mirrors the other list tabs.

const PM_FREQUENCY_OPTIONS: { value: string; label: string }[] = [
  { value: "none", label: "None (manual only)" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

const PM_PRIORITY_OPTIONS = ["low", "normal", "high", "urgent"];

function pmFrequencyLabel(freq: string, everyN: number): string {
  const base = PM_FREQUENCY_OPTIONS.find((o) => o.value === freq)?.label ?? freq;
  if (freq === "none" || everyN <= 1) return base;
  const unit = { daily: "days", weekly: "weeks", monthly: "months", quarterly: "quarters", yearly: "years" }[freq];
  return `Every ${everyN} ${unit ?? freq}`;
}

function FacilityPmTab() {
  const { data, loading, error, reload } = useApi((signal) => listPmTemplates(signal), []);
  const { data: cfgData } = useApi((signal) => getFacilityConfig(signal), []);
  const categories: FacilityOption[] = cfgData?.config?.categories ?? [];
  const generateMut = useMutation(generateDuePms);
  const deleteMut = useMutation(deletePmTemplate);
  const [editing, setEditing] = useState<FacilityPmTemplateRow | "new" | null>(null);
  const [genMsg, setGenMsg] = useState<string | null>(null);

  // Group is a client-side convenience filter ("" = all, "none" = unassigned).
  const [group, setGroup] = useState("");
  const groupsApi = useApi((s) => listFacilityGroups(s), []);
  const groups = groupsApi.data?.groups ?? [];

  const allTemplates = data?.templates ?? [];
  const templates =
    group === "none"
      ? allTemplates.filter((t) => !t.group_id)
      : group
        ? allTemplates.filter((t) => t.group_id === group)
        : allTemplates;

  async function generate() {
    setGenMsg(null);
    const res = await generateMut.run();
    if (res) {
      setGenMsg(
        res.created === 0
          ? "No templates are due right now."
          : `Generated ${res.created} work order${res.created === 1 ? "" : "s"}.`,
      );
      reload();
    }
  }

  async function onDelete(t: FacilityPmTemplateRow) {
    confirmThen(`Delete PM template "${t.name}"? Generated work orders are kept.`, async () => {
      const res = await deleteMut.run(t.id);
      if (res) reload();
    });
  }

  return (
    <>
      <SettingsListCard
        title="Preventive-maintenance templates"
        count={`${templates.length} template${templates.length === 1 ? "" : "s"}`}
        addLabel="Add template"
        onAdd={() => setEditing("new")}
        headerExtra={
          groups.length > 0 ? (
            <select
              className="input-inline"
              value={group}
              onChange={(e) => setGroup(e.target.value)}
              aria-label="Filter by group"
            >
              <option value="">All groups</option>
              <option value="none">Unassigned</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          ) : undefined
        }
        intro={
          <div className="btn-row" style={{ alignItems: "center", marginBottom: 4 }}>
            <button className="btn" onClick={generate} disabled={generateMut.pending}>
              {generateMut.pending ? <Spinner label="Generating…" /> : "Generate due PMs now"}
            </button>
            {genMsg ? <span className="faint">{genMsg}</span> : null}
          </div>
        }
      >
        {generateMut.error ? <Alert kind="error">{generateMut.error}</Alert> : null}
        {deleteMut.error ? <Alert kind="error">{deleteMut.error}</Alert> : null}
        {loading ? (
          <Loading label="Loading PM templates…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : templates.length === 0 ? (
          <EmptyState>No PM templates yet — add the first one.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Schedule</th>
                <th>Priority</th>
                <th>Steps</th>
                {groups.length > 0 ? <th>Group</th> : null}
                <th>Active</th>
                <th>Last run</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id}>
                  <td><strong>{t.name}</strong></td>
                  <td>{pmFrequencyLabel(t.frequency, t.every_n)}</td>
                  <td className="capitalize">{t.priority}</td>
                  <td className="faint">{t.checklist.length}</td>
                  {groups.length > 0 ? (
                    <td className="muted">
                      {t.group_id ? groups.find((g) => g.id === t.group_id)?.name ?? t.group_id : "—"}
                    </td>
                  ) : null}
                  <td>{t.active ? <span className="pill ok">Active</span> : <span className="pill">Off</span>}</td>
                  <td className="faint">{t.last_generated_at ? formatDate(t.last_generated_at) : "—"}</td>
                  <td className="row-actions">
                    <button className="btn small" onClick={() => setEditing(t)}>Edit</button>
                    <button className="btn small danger" onClick={() => onDelete(t)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <PmTemplateModal
          initial={editing === "new" ? null : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function PmTemplateModal({
  initial,
  categories,
  onClose,
  onSaved,
}: {
  initial: FacilityPmTemplateRow | null;
  categories: FacilityOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [category, setCategory] = useState(initial?.category ?? "");
  const [priority, setPriority] = useState(initial?.priority ?? "normal");
  const [frequency, setFrequency] = useState(initial?.frequency ?? "monthly");
  const [everyN, setEveryN] = useState(String(initial?.every_n ?? 1));
  const [active, setActive] = useState(initial ? initial.active === 1 : true);
  const [checklistText, setChecklistText] = useState((initial?.checklist ?? []).join("\n"));
  const [groupId, setGroupId] = useState(initial?.group_id ?? "");
  const groupsApi = useApi((s) => listFacilityGroups(s), []);
  const groups = groupsApi.data?.groups ?? [];
  const saveMut = useMutation((body: CreateFacilityPmTemplateInput) =>
    initial ? updatePmTemplate(initial.id, body) : createPmTemplate(body),
  );

  const everyNum = Number.parseInt(everyN, 10);
  const everyValid = Number.isInteger(everyNum) && everyNum >= 1;
  const valid = !!name.trim() && everyValid;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    const checklist = checklistText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const res = await saveMut.run({
      name: name.trim(),
      category: category || null,
      priority,
      frequency,
      every_n: everyNum,
      checklist,
      active,
      group_id: groupId || null,
    });
    if (res) onSaved();
  }

  return (
    <Modal
      title={initial ? "Edit PM template" : "Add PM template"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="submit" form="pm-template-form" disabled={!valid || saveMut.pending}>
            {saveMut.pending ? <Spinner label="Saving…" /> : "Save"}
          </button>
        </>
      }
    >
      <form id="pm-template-form" onSubmit={onSubmit}>
        {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
        <label className="field">
          <span className="label">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Monthly HVAC filter change"
            autoFocus
            required
          />
          <span className="hint">Becomes the title of each generated work order.</span>
        </label>
        <div className="row">
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">— none —</option>
              {categories.map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {PM_PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="row">
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Frequency</span>
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              {PM_FREQUENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Every N</span>
            <input
              type="number"
              min={1}
              value={everyN}
              onChange={(e) => setEveryN(e.target.value)}
              disabled={frequency === "none"}
            />
            {!everyValid ? <span className="hint" style={{ color: "var(--danger)" }}>Must be ≥ 1.</span> : null}
          </label>
        </div>
        <label className="field">
          <span className="label">Checklist (one step per line)</span>
          <textarea
            rows={5}
            value={checklistText}
            onChange={(e) => setChecklistText(e.target.value)}
            placeholder={"Inspect belts\nReplace filter\nLog readings"}
          />
          <span className="hint">Copied onto each generated work order as a checklist.</span>
        </label>
        {groups.length > 0 || initial?.group_id ? (
          <label className="field">
            <span className="label">Group <span className="faint">(controls who can see this template and its generated work orders)</span></span>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={groupsApi.loading}>
              <option value="">Unassigned (everyone with Facilities access)</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
              {groupId && !groups.some((g) => g.id === groupId) ? (
                <option value={groupId}>{groupId}</option>
              ) : null}
            </select>
          </label>
        ) : null}
        <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span className="label" style={{ margin: 0 }}>Active (only active templates generate work orders)</span>
        </label>
      </form>
    </Modal>
  );
}

// ===== Outbound email provider =============================================

const EMAIL_PROVIDER_OPTIONS: { value: EmailProviderKind; label: string }[] = [
  { value: "platform", label: "Platform default" },
  { value: "mailgun", label: "Mailgun" },
  { value: "sendgrid", label: "SendGrid" },
  { value: "postmark", label: "Postmark" },
  { value: "resend", label: "Resend" },
  { value: "smtp", label: "SMTP server" },
];
// Providers that authenticate with a single HTTP API key (vs SMTP user/pass).
const API_KEY_PROVIDERS = new Set<EmailProviderKind>(["mailgun", "sendgrid", "postmark", "resend"]);

function EmailTab() {
  const { data, loading, error, reload } = useApi((signal) => getEmailConfig(signal), []);
  const saveMut = useMutation(setEmailConfig);
  const testMut = useMutation(sendTestEmail);
  const [provider, setProvider] = useState<EmailProviderKind>("platform");
  const [from, setFrom] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [domain, setDomain] = useState("");
  // SMTP relay fields.
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpSecure, setSmtpSecure] = useState(false);
  const [smtpUsername, setSmtpUsername] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [saved, setSaved] = useState(false);
  const [testTo, setTestTo] = useState(getCurrentUser()?.email ?? "");
  const [testSentId, setTestSentId] = useState<string | null>(null);

  const email: EmailConfig | undefined = data?.email;

  useEffect(() => {
    if (!email) return;
    setProvider(email.provider);
    setFrom(email.from ?? "");
    setDomain(email.domain ?? "");
    setApiKey(""); // write-only: never echo a stored key
    setSmtpHost(email.smtp_host ?? "");
    setSmtpPort(email.smtp_port != null ? String(email.smtp_port) : "587");
    setSmtpSecure(email.smtp_secure);
    setSmtpUsername(email.smtp_username ?? "");
    setSmtpPassword(""); // write-only
  }, [email]);

  // Implicit-TLS toggle defaults the conventional port (465 vs 587) until edited.
  function toggleSecure(next: boolean) {
    setSmtpSecure(next);
    setSmtpPort((cur) => (cur === "" || cur === "465" || cur === "587" ? (next ? "465" : "587") : cur));
  }

  async function save() {
    const res = await saveMut.run({
      provider,
      from: from.trim() || null,
      // Secrets are write-only — send them only when the admin typed a new one.
      ...(API_KEY_PROVIDERS.has(provider) && apiKey.trim() ? { api_key: apiKey.trim() } : {}),
      ...(provider === "mailgun" ? { domain: domain.trim() || null } : {}),
      ...(provider === "smtp"
        ? {
            smtp_host: smtpHost.trim() || null,
            smtp_port: smtpPort.trim() ? Number.parseInt(smtpPort.trim(), 10) : null,
            smtp_secure: smtpSecure,
            smtp_username: smtpUsername.trim() || null,
            ...(smtpPassword ? { smtp_password: smtpPassword } : {}),
          }
        : {}),
    });
    if (res) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      setSmtpPassword("");
      setApiKey("");
      reload();
    }
  }

  async function sendTest() {
    setTestSentId(null);
    const res = await testMut.run(testTo.trim());
    if (res) setTestSentId(res.emailId);
  }

  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-head">
          <h3>Outbound email provider</h3>
          {email ? (
            <span className="pill">
              {EMAIL_PROVIDER_OPTIONS.find((o) => o.value === email.provider)?.label ?? email.provider}
            </span>
          ) : null}
        </div>
        <div className="card-body">
          {loading ? (
            <Loading label="Loading email settings…" />
          ) : error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : email ? (
            <>
              <p className="sub" style={{ marginTop: 0 }}>
                Notifications and ticket emails are sent through the ActionConnect platform sender by
                default. Bring your own vendor to send from your institution's domain instead.
              </p>
              {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
              <label className="field">
                <span className="label">Provider</span>
                <select value={provider} onChange={(e) => setProvider(e.target.value as EmailProviderKind)}>
                  {EMAIL_PROVIDER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="label">From address (optional)</span>
                <input
                  type="email"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  placeholder="support@your-school.edu"
                />
              </label>
              {API_KEY_PROVIDERS.has(provider) ? (
                <label className="field">
                  <span className="label">API key</span>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={email.has_key ? "•••• saved" : "Paste the provider API key"}
                    autoComplete="new-password"
                  />
                </label>
              ) : null}
              {provider === "mailgun" ? (
                <label className="field">
                  <span className="label">Mailgun sending domain</span>
                  <input
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="mg.your-school.edu"
                  />
                </label>
              ) : null}
              {provider === "smtp" ? (
                <>
                  <div className="row">
                    <label className="field" style={{ flex: 2 }}>
                      <span className="label">SMTP host</span>
                      <input
                        type="text"
                        value={smtpHost}
                        onChange={(e) => setSmtpHost(e.target.value)}
                        placeholder="smtp.your-school.edu"
                      />
                    </label>
                    <label className="field" style={{ flex: 1 }}>
                      <span className="label">Port</span>
                      <input
                        type="number"
                        min={1}
                        max={65535}
                        value={smtpPort}
                        onChange={(e) => setSmtpPort(e.target.value)}
                        placeholder="587"
                      />
                    </label>
                  </div>
                  <label className="toggle" title="Use implicit TLS (port 465). Off = STARTTLS upgrade (587/25).">
                    <input
                      type="checkbox"
                      checked={smtpSecure}
                      onChange={(e) => toggleSecure(e.target.checked)}
                    />
                    Implicit TLS (SSL on connect, port 465)
                  </label>
                  <label className="field">
                    <span className="label">Username</span>
                    <input
                      type="text"
                      value={smtpUsername}
                      onChange={(e) => setSmtpUsername(e.target.value)}
                      placeholder="apikey / mailbox login"
                      autoComplete="off"
                    />
                  </label>
                  <label className="field">
                    <span className="label">Password</span>
                    <input
                      type="password"
                      value={smtpPassword}
                      onChange={(e) => setSmtpPassword(e.target.value)}
                      placeholder={email.has_smtp_password ? "•••• saved" : "SMTP password"}
                      autoComplete="new-password"
                    />
                  </label>
                </>
              ) : null}
              <div className="btn-row">
                <button className="btn primary" onClick={save} disabled={saveMut.pending}>
                  {saveMut.pending ? <Spinner label="Saving…" /> : "Save"}
                </button>
                {saved ? <span className="pill ok">Saved</span> : null}
              </div>
              <p className="hint">
                The API key is stored encrypted and never shown again; switching back to the platform
                default clears it.
              </p>
            </>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Send test email</h3>
        </div>
        <div className="card-body">
          <p className="sub" style={{ marginTop: 0 }}>
            Queues a test message through the configured provider so you can confirm delivery
            end-to-end. Save your changes first.
          </p>
          {testMut.error ? <Alert kind="error">{testMut.error}</Alert> : null}
          <label className="field">
            <span className="label">Send to</span>
            <input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="you@your-school.edu"
            />
          </label>
          <div className="btn-row">
            <button
              className="btn primary"
              onClick={sendTest}
              disabled={testMut.pending || loading || !testTo.trim()}
            >
              {testMut.pending ? <Spinner label="Sending…" /> : "Send test email"}
            </button>
            {testSentId ? <span className="pill ok">Queued — check your inbox</span> : null}
          </div>
        </div>
      </div>

      <DigestCard />
    </div>
  );
}

// Opt-in daily agent digest (#9): when enabled, ActionConnect emails each agent
// a once-daily summary of their open tickets and any at risk of breaching SLA.
function DigestCard() {
  const { data, loading, reload } = useApi((signal) => getDigestSetting(signal), []);
  const saveMut = useMutation(setDigestSetting);
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    if (data) setEnabled(data.enabled);
  }, [data]);

  async function toggle(next: boolean) {
    setEnabled(next);
    const res = await saveMut.run(next);
    if (res) reload();
    else setEnabled(!next); // revert on failure
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Daily agent digest</h3>
      </div>
      <div className="card-body">
        <p className="sub" style={{ marginTop: 0 }}>
          When enabled, each agent receives one email a day summarizing the open tickets assigned
          to them and how many are at risk of breaching SLA. Agents with no open tickets are not
          emailed.
        </p>
        {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
        <label className="toggle">
          <input
            type="checkbox"
            checked={enabled}
            disabled={loading || saveMut.pending}
            onChange={(e) => toggle(e.target.checked)}
          />
          Email a daily digest to agents
        </label>
      </div>
    </div>
  );
}

// AI email intake — when enabled, inbound emails are read by Claude and opened
// as pre-filled "Pre Tickets" (title/summary/requester/category/priority). The
// toggle + optional default category code are stored as `email.intake.*` JSON
// via the new /api/config/email-intake endpoints. Mirrors the EmailTab form
// pattern (useApi + useMutation + success Alert).
/** Inbox-check cadence options (minutes). */
const INTAKE_CHECK_INTERVALS: { value: number; label: string }[] = [
  { value: 5, label: "Every 5 minutes" },
  { value: 10, label: "Every 10 minutes" },
  { value: 15, label: "Every 15 minutes" },
  { value: 30, label: "Every 30 minutes" },
  { value: 60, label: "Every hour" },
  { value: 120, label: "Every 2 hours" },
];
/** Compact relative-time label (e.g. "3 min ago") from a unix-second timestamp. */
function intakeRelTime(ts: number): string {
  if (!ts || ts <= 0) return "never";
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return "just now";
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31536000],
    ["month", 2592000],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (const [unit, secs] of units) {
    if (Math.abs(diff) >= secs) return rtf.format(-Math.round(diff / secs), unit);
  }
  return rtf.format(-Math.round(diff / 60), "minute");
}

// ===== System email templates (per-tenant overrides over built-in defaults) ===
function EmailTemplatesTab() {
  const { data, loading, error, reload } = useApi((s) => listEmailTemplates(s), []);
  const [editing, setEditing] = useState<SystemEmailTemplate | null>(null);
  const templates = data?.templates ?? [];

  // Group templates by their `group` while preserving first-seen order.
  const groups: [string, SystemEmailTemplate[]][] = [];
  for (const tpl of templates) {
    let bucket = groups.find(([g]) => g === tpl.group);
    if (!bucket) {
      bucket = [tpl.group, []];
      groups.push(bucket);
    }
    bucket[1].push(tpl);
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Email templates</h3>
      </div>
      <div className="card-body">
        <p className="sub" style={{ marginTop: 0 }}>
          These are the system emails ActionConnect sends on this tenant. Edit the subject and body, insert{" "}
          <code>{"{{placeholders}}"}</code>, send a test, or revert to the built-in default.
        </p>
        {loading ? (
          <Loading label="Loading templates…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : (
          <div style={{ display: "grid", gap: 18 }}>
            {groups.map(([group, list]) => (
              <div key={group}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{group}</div>
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {list.map((tpl) => (
                    <li
                      key={tpl.code}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: "10px 0",
                        borderTop: "1px solid var(--border)",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                          {tpl.label}
                          {tpl.customized ? <span className="pill">Customized</span> : null}
                        </div>
                        <div className="hint">{tpl.description}</div>
                      </div>
                      <button className="btn small" type="button" onClick={() => setEditing(tpl)}>
                        Edit
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
      {editing ? (
        <EmailTemplateEditor
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function EmailTemplateEditor({
  template,
  onClose,
  onSaved,
}: {
  template: SystemEmailTemplate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body_html);
  const [insert, setInsert] = useState<{ html: string; nonce: number } | undefined>(undefined);
  const [testTo, setTestTo] = useState(getCurrentUser()?.email ?? "");
  const [testSent, setTestSent] = useState(false);
  const saveMut = useMutation(() => saveEmailTemplate(template.code, { subject: subject.trim(), body_html: body }));
  const revertMut = useMutation(() => revertEmailTemplate(template.code));
  const testMut = useMutation((to: string) => testEmailTemplate(template.code, to));

  const invalid = !subject.trim() || richTextIsEmpty(body);

  function insertToken(key: string) {
    setInsert({ html: `{{${key}}}`, nonce: Date.now() });
  }
  async function save() {
    if (invalid) return;
    const res = await saveMut.run();
    if (res) onSaved();
  }
  async function revert() {
    const ok = await confirmDialog({
      title: "Revert to default",
      message: `Revert "${template.label}" to the built-in default? Your customizations will be removed.`,
      danger: true,
      confirmLabel: "Revert",
    });
    if (!ok) return;
    const res = await revertMut.run();
    if (res) onSaved();
  }
  async function sendTest() {
    setTestSent(false);
    const res = await testMut.run(testTo.trim());
    if (res) setTestSent(true);
  }

  return (
    <Modal
      title={`Edit: ${template.label}`}
      onClose={onClose}
      wide
      footer={
        <>
          {template.customized ? (
            <button className="btn danger" type="button" onClick={revert} disabled={revertMut.pending}>
              {revertMut.pending ? "Reverting…" : "Revert to default"}
            </button>
          ) : null}
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" onClick={save} disabled={saveMut.pending || invalid}>
            {saveMut.pending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
      {revertMut.error ? <Alert kind="error">{revertMut.error}</Alert> : null}
      <label className="field">
        <span className="label">Subject</span>
        <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </label>
      <div className="field">
        <span className="label">Body</span>
        <RichTextEditor value={body} onChange={setBody} placeholder="Email body…" {...(insert ? { insert } : {})} />
      </div>
      <div className="field">
        <span className="label">Placeholders</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {template.placeholders.map((p) => (
            <button
              key={p.key}
              type="button"
              className="btn small"
              title={p.description}
              onClick={() => insertToken(p.key)}
            >
              {`{{${p.key}}}`}
            </button>
          ))}
        </div>
        <span className="hint">Click to insert into the body. Placeholders also work in the subject.</span>
      </div>
      <div className="field">
        <span className="label">Send a test</span>
        <div className="btn-row" style={{ gap: 8 }}>
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.edu"
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn small"
            onClick={sendTest}
            disabled={testMut.pending || !testTo.trim()}
          >
            {testMut.pending ? "Sending…" : "Send test"}
          </button>
        </div>
        {testMut.error ? (
          <Alert kind="error">{testMut.error}</Alert>
        ) : testSent ? (
          <Alert kind="success">Test queued to {testTo}.</Alert>
        ) : null}
        <span className="hint">Sends this template rendered with sample data (subject prefixed with “[Test]”).</span>
      </div>
    </Modal>
  );
}

/** Settings → Helpdesk → Satisfaction (CSAT): the survey config panel (0112). */
function CsatTab() {
  const { data, loading, error, reload } = useApi((signal) => getCsatConfig(signal), []);
  const statusesApi = useApi((signal) => listStatuses(signal), []);
  const statuses = statusesApi.data?.statuses ?? [];
  const closedStatuses = statuses.filter((s) => s.category === "closed");
  const saveMut = useMutation(setCsatConfig);
  const cfg = data?.config;
  const [enabled, setEnabled] = useState(false);
  const [triggers, setTriggers] = useState<string[]>([]);
  const [question, setQuestion] = useState("");
  const [labels, setLabels] = useState<string[]>(["Bad", "OK", "Great"]);
  const [thankyou, setThankyou] = useState("");
  const [commentLabel, setCommentLabel] = useState("");
  const [commentPlaceholder, setCommentPlaceholder] = useState("");
  const [submitLabel, setSubmitLabel] = useState("");
  const [delayHours, setDelayHours] = useState("0");
  const [saved, setSaved] = useState(false);
  const [view, setView] = useState<"settings" | "history">("settings");

  useEffect(() => {
    if (!cfg) return;
    setEnabled(cfg.enabled);
    setTriggers(cfg.trigger_statuses ?? []);
    setQuestion(cfg.question);
    setLabels(cfg.labels?.length === 3 ? cfg.labels : ["Bad", "OK", "Great"]);
    setThankyou(cfg.thankyou);
    setCommentLabel(cfg.comment_label);
    setCommentPlaceholder(cfg.comment_placeholder);
    setSubmitLabel(cfg.submit_label);
    setDelayHours(String(cfg.delay_hours ?? 0));
  }, [cfg]);

  const toggleTrigger = (code: string) =>
    setTriggers((t) => (t.includes(code) ? t.filter((c) => c !== code) : [...t, code]));

  async function save() {
    const res = await saveMut.run({
      enabled,
      trigger_statuses: triggers,
      question: question.trim() || "How was our support?",
      labels: labels.map((l) => l.trim() || " "),
      thankyou: thankyou.trim() || "Thanks for your feedback!",
      comment_label: commentLabel.trim() || "Any comments? (optional)",
      comment_placeholder: commentPlaceholder.trim() || "Tell us more…",
      submit_label: submitLabel.trim() || "Submit feedback",
      delay_hours: Math.max(0, parseInt(delayHours, 10) || 0),
    });
    if (res) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      reload();
    }
  }

  const emojis = ["😞", "😐", "🙂"];
  const ratingValues = [1, 3, 5]; // 3-emoji scale mapped onto the 1–5 csat score

  return (
    <div className="card">
      <div className="card-head">
        <h3 style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <span aria-hidden="true">💬</span> Satisfaction Survey
        </h3>
        {cfg ? <span className="pill">{cfg.enabled ? "On" : "Off"}</span> : null}
      </div>
      <div className="card-body">
        <div className="seg" role="group" aria-label="Satisfaction view" style={{ marginBottom: 14 }}>
          <button
            type="button"
            className={`seg-btn${view === "settings" ? " active" : ""}`}
            aria-pressed={view === "settings"}
            onClick={() => setView("settings")}
          >
            Survey Settings
          </button>
          <button
            type="button"
            className={`seg-btn${view === "history" ? " active" : ""}`}
            aria-pressed={view === "history"}
            onClick={() => setView("history")}
          >
            History
          </button>
        </div>
        {view === "history" ? (
          <CsatHistoryView labels={cfg?.labels ?? ["Bad", "OK", "Great"]} />
        ) : loading ? (
          <Loading label="Loading survey settings…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : (
          <>
            <p className="sub" style={{ marginTop: 0 }}>
              When a ticket is resolved, email the requester a one-click survey. Ratings show on the
              ticket and feed the CSAT report. The email's subject &amp; body are editable under Email Templates.
            </p>
            {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
            {saved ? <Alert kind="success">Saved</Alert> : null}

            <label className="toggle">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Send a satisfaction survey when a ticket is resolved
            </label>

            <label className="field" style={{ marginTop: 10 }}>
              <span className="label">Survey question</span>
              <input type="text" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="How was our support?" />
            </label>

            <div className="field">
              <span className="label">Rating labels</span>
              <span className="hint" style={{ marginTop: -4, marginBottom: 6 }}>
                Each choice scores a value out of 5 (used for the satisfaction % and averages).
              </span>
              <div className="row" style={{ gap: 10 }}>
                {labels.map((l, i) => (
                  <label className="field" key={i} style={{ flex: 1 }}>
                    <span className="hint" style={{ marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 16 }}>{emojis[i]}</span>
                      <span className="pill">Value {ratingValues[i]}</span>
                    </span>
                    <input
                      type="text"
                      value={l}
                      onChange={(e) => setLabels((ls) => ls.map((x, j) => (j === i ? e.target.value : x)))}
                    />
                  </label>
                ))}
              </div>
            </div>

            <label className="field">
              <span className="label">Thank-you message (shown after they rate)</span>
              <input type="text" value={thankyou} onChange={(e) => setThankyou(e.target.value)} placeholder="Thanks for your feedback!" />
            </label>

            <div className="row" style={{ gap: 10 }}>
              <label className="field" style={{ flex: 1 }}>
                <span className="label">Comment box label</span>
                <input type="text" value={commentLabel} onChange={(e) => setCommentLabel(e.target.value)} placeholder="Any comments? (optional)" />
              </label>
              <label className="field" style={{ flex: 1 }}>
                <span className="label">Comment box placeholder</span>
                <input type="text" value={commentPlaceholder} onChange={(e) => setCommentPlaceholder(e.target.value)} placeholder="Tell us more…" />
              </label>
            </div>

            <label className="field">
              <span className="label">Submit button text</span>
              <input type="text" value={submitLabel} onChange={(e) => setSubmitLabel(e.target.value)} placeholder="Submit feedback" style={{ maxWidth: 260 }} />
            </label>

            <label className="field">
              <span className="label">Send delay</span>
              <select value={delayHours} onChange={(e) => setDelayHours(e.target.value)} style={{ maxWidth: 260 }}>
                <option value="0">Immediately on resolution</option>
                <option value="1">1 hour after resolution</option>
                <option value="4">4 hours after resolution</option>
                <option value="24">1 day after resolution</option>
                <option value="72">3 days after resolution</option>
              </select>
              <span className="hint">Deferred surveys go out on the hourly sweep (± up to an hour).</span>
            </label>

            <div className="field">
              <span className="label">Trigger on these resolution statuses</span>
              <span className="hint" style={{ marginBottom: 6 }}>
                Leave all unchecked to survey on <strong>any</strong> closing status.
              </span>
              {closedStatuses.length === 0 ? (
                <span className="muted">No closed-category statuses configured.</span>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                  {closedStatuses.map((s) => (
                    <label className="toggle" key={s.code} style={{ margin: 0 }}>
                      <input type="checkbox" checked={triggers.includes(s.code)} onChange={() => toggleTrigger(s.code)} />
                      {s.name}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="btn-row" style={{ marginTop: 16 }}>
              <button className="btn primary" onClick={save} disabled={saveMut.pending}>
                {saveMut.pending ? <Spinner label="Saving…" /> : "Save"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** The three rating buckets, in tile order, matched against RANGES rather than
 *  1/3/5 equality: the public page only offers those three, but any 1-5 value is
 *  storable, and a tile's count has to match the rows its filter returns. Names
 *  come from the tenant's own rating labels so the filter, the table and the
 *  tiles never show two vocabularies for the same three values. */
const CSAT_BUCKETS = [
  { value: "bad" as const, emoji: "😞", fallback: "Poor" },
  { value: "ok" as const, emoji: "😐", fallback: "Okay" },
  { value: "great" as const, emoji: "🙂", fallback: "Great" },
];
const csatBucketOf = (rating: number): CsatBucket => (rating <= 2 ? "bad" : rating === 3 ? "ok" : "great");
/** Ceiling on one export. Anything beyond it is reported, never silently cut. */
const CSAT_EXPORT_MAX = 20000;

/** Settings → Satisfaction → Response history: paged, filterable list of every
 *  answered survey. Read-only. */
function CsatHistoryView({ labels }: { labels: string[] }) {
  const [bucket, setBucket] = useState<"" | CsatBucket>("");
  const [days, setDays] = useState("7"); // default to the last 7 days
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const from = days === "all" ? null : Math.floor(Date.now() / 1000) - Number(days) * 86400;
  const { data, loading, error, reload } = useApi(
    (signal) => listCsatResponses({ bucket: bucket || null, from, limit, offset }, signal),
    [bucket, days, offset],
  );
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const summary = data?.summary;
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState<string | null>(null);

  const bucketName = (b: CsatBucket) => {
    const i = CSAT_BUCKETS.findIndex((x) => x.value === b);
    return labels[i] ?? CSAT_BUCKETS[i].fallback;
  };
  const bucketEmoji = (b: CsatBucket) => CSAT_BUCKETS[CSAT_BUCKETS.findIndex((x) => x.value === b)].emoji;
  const ratingLabel = (r: number) => bucketName(csatBucketOf(r));
  const periodLabel = days === "all" ? "all time" : `the last ${days} days`;
  const fmt = (ts: number) =>
    new Date(ts * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

  // Export every row matching the current filters (not just the page) as CSV,
  // fetched in chunks to respect the API's page cap.
  async function exportCsv() {
    setExporting(true);
    setExportNote(null);
    try {
      const all: CsatResponseRow[] = [];
      let matching = 0;
      for (;;) {
        // The summary covers the period, not the page — chunks skip that scan.
        const page = await listCsatResponses({
          bucket: bucket || null,
          from,
          limit: 100,
          offset: all.length,
          summary: false,
        });
        matching = page.total;
        all.push(...page.rows);
        if (page.rows.length === 0 || all.length >= matching || all.length >= CSAT_EXPORT_MAX) break;
      }
      const header = ["Ticket", "Subject", "Rating", "Comment", "Requester", "Responded"];
      const body = all.map((r) => [
        r.number_display || `#${r.number}`,
        r.subject ?? "",
        ratingLabel(r.rating),
        r.comment ?? "",
        r.requester_name || r.requester_email || "",
        // ISO, not the localized display format: a spreadsheet has to be able to
        // sort it, and same-day responses need their time to stay distinguishable.
        new Date(r.responded_at * 1000).toISOString(),
      ]);
      saveBlob(csvBlob([header, ...body]), "csat-responses.csv");
      if (all.length < matching) {
        setExportNote(`Exported the first ${all.length} of ${matching} responses — narrow the period to export the rest.`);
      }
    } catch (e) {
      // A chunk can fail mid-run (expired session, worker error); say so rather
      // than letting the button quietly re-enable with no file.
      setExportNote(e instanceof Error ? e.message : "Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <p className="sub" style={{ marginTop: 0 }}>
        Every answered survey, newest first. Filter by rating and period.
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", width: "100%", alignItems: "flex-end", gap: 10, marginBottom: 12 }}>
        <label className="field" style={{ maxWidth: 200, flex: "0 0 auto" }}>
          <span className="label">Rating</span>
          <select
            value={bucket}
            onChange={(e) => {
              setOffset(0);
              setBucket(e.target.value as "" | CsatBucket);
            }}
          >
            <option value="">All ratings</option>
            {CSAT_BUCKETS.map((b) => (
              <option key={b.value} value={b.value}>
                {b.emoji} {bucketName(b.value)}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ maxWidth: 200, flex: "0 0 auto" }}>
          <span className="label">Period</span>
          <select
            value={days}
            onChange={(e) => {
              setOffset(0);
              setDays(e.target.value);
            }}
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </label>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "flex-end", gap: 10 }}>
          <span className="pill" title="Responses matching the current filters">
            {total} {total === 1 ? "response" : "responses"}
          </span>
          <button className="btn" onClick={exportCsv} disabled={exporting || total === 0}>
            {exporting ? <Spinner label="Exporting…" /> : "Export CSV"}
          </button>
        </div>
      </div>
      {exportNote ? (
        <p className="hint" role="status" style={{ marginTop: -6, marginBottom: 12 }}>
          {exportNote}
        </p>
      ) : null}
      {summary && summary.rated > 0 ? (
        <>
          <div style={{ display: "flex", width: "100%", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            {[
              {
                label: "Satisfaction",
                value: `${summary.csat_pct}%`,
                hint: `Positive ratings as a share of the survey responses received in ${periodLabel}. Reports → Satisfaction counts every rating, agent-set ones included, over when surveys were sent — so the two figures can differ.`,
              },
              {
                label: "Avg rating",
                value: summary.avg_rating != null ? `${summary.avg_rating} / 5` : "—",
                hint: `Mean of the ratings received in ${periodLabel}.`,
              },
              { label: `${bucketEmoji("great")} ${bucketName("great")}`, value: String(summary.great), hint: "Ratings of 4-5." },
              { label: `${bucketEmoji("ok")} ${bucketName("ok")}`, value: String(summary.ok), hint: "Ratings of 3." },
              { label: `${bucketEmoji("bad")} ${bucketName("bad")}`, value: String(summary.bad), hint: "Ratings of 1-2." },
            ].map((s) => (
              <div
                key={s.label}
                title={s.hint}
                style={{ flex: "1 1 0", minWidth: 110, border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px" }}
              >
                <div style={{ fontSize: 22, fontWeight: 700 }}>{s.value}</div>
                <div className="hint" style={{ marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
          {bucket ? (
            <p className="hint" style={{ marginTop: -8, marginBottom: 14 }}>
              These cover every rating in {periodLabel} — the rating filter narrows the list below only.
            </p>
          ) : null}
        </>
      ) : null}
      {loading ? (
        <Loading label="Loading responses…" />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : rows.length === 0 ? (
        <EmptyState>
          {days === "all" && !bucket
            ? "No survey responses yet."
            : `No survey responses match the current filters (${bucket ? `${bucketName(bucket)}, ` : ""}${periodLabel}).`}
        </EmptyState>
      ) : (
        <>
          <table className="data">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Rating</th>
                <th>Comment</th>
                <th>Requester</th>
                <th>Responded</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.number_display || `#${r.number}`}
                    {r.subject ? <span className="muted"> — {r.subject}</span> : null}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <span aria-hidden="true">{bucketEmoji(csatBucketOf(r.rating))}</span> {ratingLabel(r.rating)}
                  </td>
                  <td>{r.comment || <span className="muted">—</span>}</td>
                  <td>{r.requester_name || r.requester_email || <span className="muted">—</span>}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{fmt(r.responded_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="btn-row" style={{ marginTop: 12, alignItems: "center", gap: 10 }}>
            <button className="btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
              Previous
            </button>
            <span className="muted">
              {offset + 1}–{Math.min(offset + limit, total)} of {total}
            </span>
            <button className="btn" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>
              Next
            </button>
          </div>
        </>
      )}
    </>
  );
}

function EmailIntakeCard() {
  const { data, loading, error, reload } = useApi((signal) => getEmailIntakeConfig(signal), []);
  const typesApi = useApi((signal) => listTicketTypes(signal), []);
  const ticketTypes = typesApi.data?.ticket_types ?? [];
  const statusesApi = useApi((signal) => listStatuses(signal), []);
  const statuses = statusesApi.data?.statuses ?? [];
  const saveMut = useMutation(setEmailIntakeConfig);
  const [enabled, setEnabled] = useState(false);
  const [dedupeEnabled, setDedupeEnabled] = useState(false);
  const [threadReplies, setThreadReplies] = useState(true);
  const [testRedirectTo, setTestRedirectTo] = useState("");
  const [defaultTicketType, setDefaultTicketType] = useState("");
  const [defaultStatus, setDefaultStatus] = useState("");
  const [checkInterval, setCheckInterval] = useState(15);
  const [inboxHost, setInboxHost] = useState("");
  const [inboxPort, setInboxPort] = useState("993");
  const [inboxSsl, setInboxSsl] = useState(true);
  const [inboxUsername, setInboxUsername] = useState("");
  const [inboxPassword, setInboxPassword] = useState(""); // write-only
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState<"intake" | "mailbox" | "options">("intake");

  const cfg = data?.config;
  useEffect(() => {
    if (!cfg) return;
    setEnabled(cfg.enabled);
    setDedupeEnabled(cfg.dedupe_enabled);
    setThreadReplies(cfg.thread_replies);
    setTestRedirectTo(cfg.test_redirect_to ?? "");
    setDefaultTicketType(cfg.default_ticket_type ?? "");
    setDefaultStatus(cfg.default_status ?? "");
    setCheckInterval(cfg.check_interval_minutes);
    setInboxHost(cfg.inbox_host ?? "");
    setInboxPort(cfg.inbox_port != null ? String(cfg.inbox_port) : "993");
    setInboxSsl(cfg.inbox_ssl);
    setInboxUsername(cfg.inbox_username ?? "");
    setInboxPassword(""); // never prefill the stored secret
  }, [cfg]);

  async function save() {
    const res = await saveMut.run({
      enabled,
      dedupe_enabled: dedupeEnabled,
      thread_replies: threadReplies,
      test_redirect_to: testRedirectTo.trim() || null,
      default_ticket_type: defaultTicketType.trim() || null,
      default_status: defaultStatus.trim() || null,
      check_enabled: true,
      check_interval_minutes: checkInterval,
      inbox_host: inboxHost.trim() || null,
      inbox_port: inboxPort.trim() ? Number(inboxPort) : null,
      inbox_ssl: inboxSsl,
      inbox_username: inboxUsername.trim() || null,
      // Only send the password when the admin typed one (write-only; blank keeps the stored one).
      ...(inboxPassword ? { inbox_password: inboxPassword } : {}),
    });
    if (res) {
      setSaved(true);
      setInboxPassword("");
      setTimeout(() => setSaved(false), 2500);
      reload();
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3 style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <span aria-hidden="true">✨</span> AI Email Intake
        </h3>
        {cfg ? <span className="pill">{cfg.enabled ? "On" : "Off"}</span> : null}
      </div>
      <div className="card-body">
        {loading ? (
          <Loading label="Loading intake settings…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : (
          <>
            {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
            {saved ? <Alert kind="success">Saved</Alert> : null}
            <div className="seg" role="group" aria-label="Email intake settings" style={{ marginBottom: 14 }}>
              <button type="button" className={`seg-btn${tab === "intake" ? " active" : ""}`} aria-pressed={tab === "intake"} onClick={() => setTab("intake")}>
                Intake
              </button>
              <button type="button" className={`seg-btn${tab === "mailbox" ? " active" : ""}`} aria-pressed={tab === "mailbox"} onClick={() => setTab("mailbox")}>
                Mailbox
              </button>
              <button type="button" className={`seg-btn${tab === "options" ? " active" : ""}`} aria-pressed={tab === "options"} onClick={() => setTab("options")}>
                Options
              </button>
            </div>

            {tab === "intake" ? (
              <>
            <p className="sub" style={{ marginTop: 0 }}>
              When enabled, inbound emails are read by AI and opened as pre-filled tickets — a
              suggested title, summary, requester, and priority. If the AI is unavailable
              the email still becomes a ticket the usual way.
            </p>
            <label className="toggle">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Turn inbound emails into pre-filled tickets
            </label>
            {enabled ? (
              <>
            <label className="field">
              <span className="label">Default ticket type</span>
              <select
                value={defaultTicketType}
                onChange={(e) => setDefaultTicketType(e.target.value)}
                disabled={!enabled || typesApi.loading}
              >
                <option value="">— tenant default —</option>
                {ticketTypes.map((t) => (
                  <option key={t.id} value={t.code}>
                    {t.name}
                  </option>
                ))}
                {defaultTicketType && !ticketTypes.some((t) => t.code === defaultTicketType) ? (
                  <option value={defaultTicketType}>{defaultTicketType}</option>
                ) : null}
              </select>
            </label>

            <label className="field">
              <span className="label">Default status</span>
              <select
                value={defaultStatus}
                onChange={(e) => setDefaultStatus(e.target.value)}
                disabled={!enabled || statusesApi.loading}
              >
                <option value="">— tenant default —</option>
                {statuses.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
                {defaultStatus && !statuses.some((s) => s.code === defaultStatus) ? (
                  <option value={defaultStatus}>{defaultStatus}</option>
                ) : null}
              </select>
            </label>

            <label className="field" style={{ marginTop: 4 }}>
              <span className="label">Check the inbox for new email</span>
              <select
                value={checkInterval}
                onChange={(e) => setCheckInterval(Number(e.target.value))}
                disabled={!enabled}
              >
                {INTAKE_CHECK_INTERVALS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

              {/* Last-run status (read-only observability from the scheduled poller). */}
              {cfg?.inbox_host ? (
                <div style={{ borderTop: "1px solid var(--border)", marginTop: 10, paddingTop: 12 }}>
                  <p className="label" style={{ marginBottom: 8 }}>Scheduled check status</p>
                  {cfg.last_run ? (
                    <>
                      <p className="sub" style={{ margin: 0 }}>
                        Last checked {intakeRelTime(cfg.last_run.ran_at)} —{" "}
                        {cfg.last_run.created} ticket{cfg.last_run.created === 1 ? "" : "s"} created from{" "}
                        {cfg.last_run.processed}/{cfg.last_run.total} message
                        {cfg.last_run.total === 1 ? "" : "s"}
                        {cfg.last_run.errors > 0
                          ? `, ${cfg.last_run.errors} error${cfg.last_run.errors === 1 ? "" : "s"}`
                          : ""}
                        {cfg.last_run.ai_used > 0 ? ` · AI triaged ${cfg.last_run.ai_used}` : ""}
                      </p>
                      {cfg.last_run.last_error ? (
                        <Alert kind="error">Last error: {cfg.last_run.last_error}</Alert>
                      ) : null}
                    </>
                  ) : cfg.last_check && cfg.last_check > 0 ? (
                    <p className="sub" style={{ margin: 0 }}>
                      Last checked {intakeRelTime(cfg.last_check)}.
                    </p>
                  ) : (
                    <p className="sub" style={{ margin: 0 }}>
                      No scheduled checks have run yet.
                    </p>
                  )}
                </div>
              ) : null}
              </>
            ) : null}
              </>
            ) : null}

            {tab === "mailbox" ? (
            <div style={{ marginTop: 4 }}>
              <p className="label" style={{ marginBottom: 8 }}>Inbox to check (IMAP)</p>
              <p className="sub" style={{ marginTop: -4 }}>
                The mailbox the system connects to. Used to poll for new mail and to thread email
                replies back into tickets — configure it here regardless of the toggle above.
              </p>
              <div className="row">
                <label className="field">
                  <span className="label">IMAP server</span>
                  <input type="text" value={inboxHost} onChange={(e) => setInboxHost(e.target.value)} placeholder="imap.gmail.com" autoComplete="off" />
                </label>
                <label className="field" style={{ maxWidth: 140 }}>
                  <span className="label">Port</span>
                  <input type="number" value={inboxPort} onChange={(e) => setInboxPort(e.target.value)} placeholder="993" />
                </label>
              </div>
              <label className="toggle">
                <input type="checkbox" checked={inboxSsl} onChange={(e) => setInboxSsl(e.target.checked)} />
                Use SSL/TLS
              </label>
              <label className="field">
                <span className="label">Email / username</span>
                <input type="text" value={inboxUsername} onChange={(e) => setInboxUsername(e.target.value)} placeholder="helpdesk@yourschool.edu" autoComplete="off" />
              </label>
              <label className="field">
                <span className="label">Password</span>
                <input
                  type="password"
                  value={inboxPassword}
                  onChange={(e) => setInboxPassword(e.target.value)}
                  placeholder={cfg?.has_inbox_password ? "•••••••• (stored — leave blank to keep)" : "App password"}
                  autoComplete="new-password"
                />
                <span className="hint">Stored encrypted, never shown again. For Gmail / Microsoft 365 use an app password.</span>
              </label>
            </div>
            ) : null}

            {tab === "options" ? (
            <>
            <div style={{ marginTop: 4 }}>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={threadReplies}
                  onChange={(e) => setThreadReplies(e.target.checked)}
                />
                Add email replies to the ticket conversation
              </label>
              <p className="sub" style={{ marginTop: -6 }}>
                When someone replies to a ticket email, the reply is added to that ticket's
                conversation as a comment — matched by the ticket number in the subject (e.g.
                <span className="mono"> [#1234]</span>). With this off, a reply opens a new ticket instead.
              </p>
            </div>

            <div style={{ borderTop: "1px solid var(--border)", marginTop: 10, paddingTop: 12 }}>
              <label className="field">
                <span className="label">Redirect all outbound email to (testing)</span>
                <input
                  type="email"
                  value={testRedirectTo}
                  onChange={(e) => setTestRedirectTo(e.target.value)}
                  placeholder="you@example.com — leave blank for normal delivery"
                  autoComplete="off"
                />
                <span className="hint">
                  When set, <strong>every</strong> outbound email (notifications, replies, sign-in links)
                  is sent to this one address instead of the real recipient — the intended recipient is
                  shown in the subject as <span className="mono">[test → …]</span>. Clear it to resume
                  normal delivery.
                </span>
              </label>
            </div>

            <div style={{ borderTop: "1px solid var(--border)", marginTop: 10, paddingTop: 12 }}>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={dedupeEnabled}
                  onChange={(e) => setDedupeEnabled(e.target.checked)}
                />
                Detect possible duplicate tickets
              </label>
              <p className="sub" style={{ marginTop: -6 }}>
                When on, new tickets are checked against recent tickets for likely duplicates (at
                intake and via a "Find possible duplicates" button on the ticket).
              </p>
            </div>
            </>
            ) : null}

            <div className="btn-row" style={{ marginTop: 16 }}>
              <button className="btn primary" onClick={save} disabled={saveMut.pending}>
                {saveMut.pending ? <Spinner label="Saving…" /> : "Save"}
              </button>
            </div>
            <p className="hint">
              Email contents are treated as untrusted and never followed as instructions. Student
              data is minimized before it reaches the model.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ===== Knowledge base — categories =========================================
// Moved here from the Knowledge Base page: this is the admin-gated category
// taxonomy used to organize KB articles (article authoring still lives at /kb
// and keeps the category picker in its dialog). The create/update/delete
// endpoints are admin-only server-side; matching the other Settings lookups,
// we surface the controls and let the server enforce authorization.

/** URL-safe slug suggestion from a category name (matches the KB page helper). */
function kbSlugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function KnowledgeBaseTab() {
  const { data, loading, error, reload } = useApi((signal) => listKbCategories(signal), []);
  const categories = data?.categories ?? [];
  const [editing, setEditing] = useState<KbCategoryRow | "new" | null>(null);

  return (
    <>
      <SettingsListCard
        title="Article categories"
        count={categories.length}
        addLabel="Add category"
        onAdd={() => setEditing("new")}
        intro={
          <p className="sub" style={{ margin: "10px 14px 8px" }}>
            Categories organize knowledge-base articles. Authors pick one when writing an article on
            the Knowledge Base page; deleting a category leaves its articles uncategorized.
          </p>
        }
      >
        {loading ? (
          <Loading label="Loading categories…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : categories.length === 0 ? (
          <EmptyState>No categories yet. Articles can also live uncategorized.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <KbCategoryTr key={cat.id} category={cat} onEdit={() => setEditing(cat)} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </SettingsListCard>

      {editing ? (
        <KbCategoryModal
          category={editing === "new" ? null : editing}
          existingCount={categories.length}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Add/Edit KB category modal — null category = add (slug auto-derives from name). */
function KbCategoryModal({
  category,
  existingCount,
  onClose,
  onSaved,
}: {
  category: KbCategoryRow | null;
  existingCount: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(category?.name ?? "");
  const createMut = useMutation(createKbCategory);
  const updateMut = useMutation(updateKbCategory);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const res = category
      ? await updateMut.run(category.id, { name: name.trim() })
      : await createMut.run({ name: name.trim(), slug: kbSlugify(name), position: existingCount });
    if (res) onSaved();
  }

  return (
    <Modal
      title={category ? "Edit category" : "Add category"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="kb-category-form" disabled={pending || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : category ? "Save changes" : "Add category"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="kb-category-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Getting started" autoFocus required />
        </label>
        {category ? (
          <label className="field">
            <span className="label">Slug</span>
            <input type="text" value={category.slug} readOnly disabled />
            <span className="hint">The slug is fixed once a category is created.</span>
          </label>
        ) : null}
      </form>
    </Modal>
  );
}

/** One KB-category row: read-only with Edit (modal) / Delete. */
function KbCategoryTr({ category, onEdit, onChanged }: { category: KbCategoryRow; onEdit: () => void; onChanged: () => void }) {
  const deleteMut = useMutation(deleteKbCategory);
  const busy = deleteMut.pending;

  return (
    <tr>
      <td><strong>{category.name}</strong></td>
      <td className="mono faint">{category.slug}</td>
      <td className="row-actions">
        {deleteMut.error ? <div style={{ color: "var(--danger)", fontSize: "0.75rem" }}>{deleteMut.error}</div> : null}
        <button className="btn small" onClick={onEdit} disabled={busy}>Edit</button>
        <button
          className="btn small danger"
          onClick={() =>
            confirmThen(`Delete category "${category.name}"? Its articles stay, uncategorized.`, () =>
              deleteMut.run(category.id).then(onChanged),
            )
          }
          disabled={busy}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}
